import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { focusChatComposer } from "../../lib/composer-focus.js";
import {
  ComposerLinkModal,
  buildMarkdownLink,
  findMarkdownLinkAt
} from "./composer-link.js";
import { useVirtualizer } from "@tanstack/react-virtual";
import type {
  ChatSearchScope,
  MediaUploadKind,
  MessageContent,
  MessageTextEntity
} from "@tg/shared";
import { hasMarkdownSyntax, isServerMessageId, parseMarkdown } from "@tg/shared";
import {
  internalApi,
  type InternalDeletedMessageRecord,
  type InternalTemplateSectionRecord,
  type InternalTrustAssetRecord,
  type InternalTrustAssetUsageRecord
} from "../../lib/internal-api.js";
import { mediaApi } from "../../lib/media-api.js";
import {
  canCopyImageToClipboard,
  copyImageToClipboard,
  copyTextToClipboard
} from "../../lib/clipboard.js";
import { pushToast } from "../../lib/toast.js";
import { httpBaseFromWsUrl } from "../../lib/auth-api.js";
import type { MessageViewModel, ScheduledMessageViewModel } from "../../lib/store.js";
import { FALLBACK_TRANSLATE_LANGUAGES } from "../../lib/translate-languages.js";
import {
  resolveAccountDefaultTranslationLanguage,
  resolveOutgoingLanguage,
  translationKey,
  translationSourceText,
  useRealtimeStore
} from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import {
  dayKey,
  formatDaySeparator,
  formatDialogTime,
  formatExactDateTime,
  formatFullDateTime,
  formatShortDateTime,
  formatTime,
  timeZoneShortLabel,
  type TimeZonePref
} from "../../lib/datetime.js";
import { visualRect, visualRectOf } from "../../lib/zoom.js";
import { ForwardTargetPicker } from "./forward-target-picker.js";
import { ExportChatModal } from "./export-chat-modal.js";
import { ComposerPicker } from "./composer-picker.js";
import { ChatAvatar } from "../../components/chat-avatar.js";
import { ContextMenuPositioner } from "../../components/context-menu.js";
import { LottieSticker } from "../../components/lottie-sticker.js";
import { VoiceMessage } from "./voice-message.js";
import { VideoNoteMessage } from "./video-note-message.js";
import { SupportScriptsPanel } from "./support-scripts-panel.js";
import { PhotoAnnotator } from "./photo-annotator.js";
import { MediaViewer, openMediaViewer, openPhotoViewer, type MediaViewerItem } from "./media-viewer.js";
import { SchedulePickerModal, type SchedulePickerResult } from "./schedule-picker-modal.js";
import { ScheduledMessagesModal } from "./scheduled-messages-modal.js";
import { messageStatusLabel } from "../../lib/message-status.js";
import { pickByHash } from "../../lib/hash-pick.js";
import { formatMessageLines, selfDisplayName } from "../../lib/message-export.js";
import type { FrequentChatEntry } from "../../lib/internal-api.js";
import { isMessageIdAfter, isMessageIdAtMost } from "../../lib/message-id.js";
import { avatarGradientClass } from "../dialogs/dialog-list.js";

// URL картинки, которую можно скопировать в буфер как изображение:
// фото (полный файл, иначе превью) или документ-картинка. Стикеры/видео —
// нет: у них нет растрового оригинала в буфере обмена.
const copyableImageUrl = (content: MessageContent): string | null => {
  if (content.kind === "photo") {
    return content.url ?? content.thumbnailUrl ?? null;
  }
  if (
    content.kind === "document" &&
    content.url &&
    (content.mimeType?.startsWith("image/") ||
      /\.(png|jpe?g|webp|gif|bmp)$/i.test(content.fileName ?? ""))
  ) {
    return content.url;
  }
  return null;
};

// Цвет имени отправителя в группах — палитра peer-* из мокапа, детерминированно
// по senderId (тот же приём, что и градиент аватара).
const PEER_NAME_COLORS = [
  "var(--peer-red)",
  "var(--peer-orange)",
  "var(--peer-green)",
  "var(--peer-blue)",
  "var(--peer-cyan)",
  "var(--peer-violet)"
] as const;

const peerNameColor = (seed: string): string => pickByHash(seed, PEER_NAME_COLORS);

// Одна повторная попытка для картинки медиа: файл мог быть временно
// недоступен (gateway отдал 404 в момент переноса/чистки, сеть мигнула), а
// <img> без onError так и оставался битым до F5. Query-параметр обходит
// возможную кэш-запись браузера; gateway смотрит только pathname.
const retryMediaImageOnce = (event: React.SyntheticEvent<HTMLImageElement>): void => {
  const img = event.currentTarget;
  if (img.dataset.mediaRetried === "1") {
    return;
  }
  img.dataset.mediaRetried = "1";
  const src = img.getAttribute("src");
  if (!src || src.startsWith("blob:") || src.startsWith("data:")) {
    return;
  }
  window.setTimeout(() => {
    if (img.isConnected && img.getAttribute("src") === src) {
      img.src = `${src}${src.includes("?") ? "&" : "?"}retry=${Date.now()}`;
    }
  }, 1500);
};

// Отправка после await (загрузка файла): если за это время окно переключили
// на другой аккаунт, chatId прежнего аккаунта адресовал бы чужой диалог.
const assertSameAccount = (accountIdAtSubmit: string | null): void => {
  if (useRealtimeStore.getState().accountId !== accountIdAtSubmit) {
    throw new Error("Во время загрузки сменился аккаунт — отправка отменена.");
  }
};



// Fallback-список языков на случай, когда серверный конфиг переводчика
// (/api/translator/config) ещё не подтянулся. Основной источник — настройки
// админа (языки, промпты и привязки к аккаунтам редактируются в Настройках).

type PendingAttachment = {
  file: File;
  kind: MediaUploadKind;
  previewUrl: string | null;
  waveform?: string;
  durationSeconds?: number;
  width?: number;
  height?: number;
};

// Состояние модалки редактирования сохранённого шаблона (с сохранением, а не
// «правка перед отправкой»): тексты + оставленные прежние медиа-контенты +
// новые файлы, которые загрузятся при сохранении.
type TrustEditState = {
  asset: InternalTrustAssetRecord;
  title: string;
  description: string;
  supportDescription: string;
  // link — URL; payment — реквизит; для медиа не используется.
  textValue: string;
  // Прежние медиа шаблона (content + extraContents), которые пользователь
  // оставил (крестик убирает из списка).
  keptContents: MessageContent[];
  newAttachments: PendingAttachment[];
  // «Группировать»: мультифото альбомом (true) или раздельно (false).
  groupMedia: boolean;
};

// Заменить/удалить caption у медиа-контента, не трогая остальные поля.
const applyContentCaption = (content: MessageContent, caption: string): MessageContent => {
  if (content.kind === "text") {
    return content;
  }
  const { caption: _previous, ...rest } = content as MessageContent & { caption?: string };
  return caption.length > 0
    ? ({ ...rest, caption } as MessageContent)
    : (rest as MessageContent);
};

// Человекочитаемое имя медиа-контента для списков в модалках шаблона.
const trustContentLabel = (content: MessageContent): string => {
  if (content.kind === "text") {
    return content.text.slice(0, 40);
  }
  const named = content as { fileName?: string };
  if (named.fileName) {
    return named.fileName;
  }
  return content.kind === "photo"
    ? "Фото"
    : content.kind === "video"
      ? "Видео"
      : content.kind === "voice"
        ? "Голосовое"
        : content.kind === "videoNote"
          ? "Кружок"
          : "Файл";
};

const EMPTY_MESSAGES: MessageViewModel[] = [];
const EMPTY_SCHEDULED: ScheduledMessageViewModel[] = [];
const EMPTY_FREQUENT_CHATS: FrequentChatEntry[] = [];

// Форматирование времени/дат живёт в lib/datetime.ts и всегда получает
// часовой пояс саппорта (ui-prefs.timeZone) — раньше здесь были локальные
// Intl-форматтеры без timeZone, из-за чего у саппорта вне МСК и время, и
// границы суток уезжали.

// Диагностика скролла ленты (раунд 46, «перекидывает вверх»): включается
// localStorage.setItem("tg-client:debug-scroll", "1") — в консоль пишутся
// причины каждого программного scrollToIndex/догрузки истории.
const scrollDebugEnabled = ((): boolean => {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem("tg-client:debug-scroll") === "1";
  } catch {
    return false;
  }
})();
const debugScroll = (reason: string, details?: Record<string, unknown>): void => {
  if (scrollDebugEnabled) {
    console.info(`[scroll] ${reason}`, details ?? {});
  }
};

// Окно после программного скролла, в котором событие scroll не считается
// пользовательским и не запускает догрузку истории. Клампинг scrollTop при
// смене чата/усадке ленты порождал scroll с scrollTop<120 → догрузка → якорь
// на первое сообщение → лента уезжала в верх.
const PROGRAMMATIC_SCROLL_WINDOW_MS = 300;

// Лента короче этого числа строк при hasMore=true считается недобранной
// страницей — зонд короткой истории дозапрашивает её независимо от геометрии.
const SHORT_HISTORY_MIN_ROWS = 8;

const messageActorLabel = (message: MessageViewModel): string =>
  message.internalAuthorName ??
  message.senderName ??
  (message.outgoing ? "Вы" : "Сообщение");

const formatFileSize = (sizeBytes: number | undefined): string | null => {
  if (!sizeBytes || sizeBytes <= 0) {
    return null;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (sizeBytes >= 1024) {
    return `${Math.ceil(sizeBytes / 1024)} KB`;
  }

  return `${sizeBytes} B`;
};

// Лимит Telegram на видеосообщение (кружок): 12 МБ. Больший файл TDLib
// отклоняет с голым INTERNAL_ERROR, поэтому проверяем ещё при выборе.
const VIDEO_NOTE_MAX_BYTES = 12_582_912;

const videoNoteTooBigMessage = (sizeBytes: number): string =>
  `Кружок весит ${(sizeBytes / (1024 * 1024)).toFixed(1)} МБ — Telegram принимает видеосообщения до 12 МБ. Сожмите или укоротите видео.`;

const pickAttachmentKind = (file: File): MediaUploadKind => {
  if (file.type.startsWith("image/")) {
    return "photo";
  }
  if (file.type.startsWith("video/")) {
    return "video";
  }
  if (file.type.startsWith("audio/")) {
    return "voice";
  }
  return "document";
};

const preferredVoiceMimeTypes = [
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm"
];

const pickVoiceRecordingMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") {
    return undefined;
  }

  return preferredVoiceMimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
};

const extensionFromMimeType = (mimeType: string): string => {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes("ogg")) {
    return ".ogg";
  }
  if (normalized.includes("mp4")) {
    return ".m4a";
  }
  if (normalized.includes("mpeg")) {
    return ".mp3";
  }
  if (normalized.includes("webm")) {
    return ".webm";
  }
  return ".bin";
};

const buildTelegramWaveform = (values: number[]): string => {
  const bytes = new Uint8Array(Math.ceil((values.length * 5) / 8));
  values.forEach((rawValue, index) => {
    const value = Math.max(0, Math.min(31, Math.round(rawValue)));
    const bitOffset = index * 5;
    const byteIndex = bitOffset >> 3;
    const shift = bitOffset & 7;
    bytes[byteIndex] = (bytes[byteIndex] ?? 0) | (value << shift);
    if (shift > 3 && byteIndex + 1 < bytes.length) {
      bytes[byteIndex + 1] = (bytes[byteIndex + 1] ?? 0) | (value >> (8 - shift));
    }
  });

  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

// Decodes the recorded blob once to derive BOTH the waveform and an exact
// duration. AudioBuffer.duration is the true decoded length; MediaRecorder
// blobs routinely report Infinity/NaN via <audio>.duration, which used to
// push us onto wall-clock time and send a too-short duration to TDLib —
// Telegram then clipped playback. Round the duration UP so the tail is
// never cut off.
const analyzeRecordedAudio = async (
  file: File
): Promise<{ waveform?: string; durationSeconds?: number }> => {
  if (typeof window === "undefined") {
    return {};
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return {};
  }

  const audioContext = new AudioContextCtor();
  try {
    const buffer = await file.arrayBuffer();
    const decoded = await audioContext.decodeAudioData(buffer.slice(0));

    const durationSeconds =
      Number.isFinite(decoded.duration) && decoded.duration > 0
        ? Math.max(1, Math.ceil(decoded.duration))
        : undefined;
    const withDuration = (rest: { waveform?: string }): {
      waveform?: string;
      durationSeconds?: number;
    } => ({ ...rest, ...(durationSeconds ? { durationSeconds } : {}) });

    const channelData = decoded.getChannelData(0);
    if (channelData.length === 0) {
      return withDuration({});
    }

    // Ровно 100 сэмплов (= 63 байта по 5 бит) — стандарт официальных клиентов
    // Telegram. Мобильный клиент получателя раскладывает waveform по сетке из
    // 100 слотов: наши прежние 64 сэмпла заполняли только ~2/3 ширины, и
    // голосовое выглядело «обрезанным наполовину», хотя звук был целым.
    const sampleCount = 100;
    const blockSize = Math.max(1, Math.floor(channelData.length / sampleCount));
    const buckets: number[] = [];
    let peak = 0;

    for (let index = 0; index < sampleCount; index += 1) {
      const start = index * blockSize;
      const end = Math.min(channelData.length, start + blockSize);
      let sumSquares = 0;
      let count = 0;

      for (let offset = start; offset < end; offset += 1) {
        const sample = channelData[offset] ?? 0;
        sumSquares += sample * sample;
        count += 1;
      }

      const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
      buckets.push(rms);
      peak = Math.max(peak, rms);
    }

    if (peak <= 0) {
      return withDuration({});
    }

    return withDuration({
      waveform: buildTelegramWaveform(buckets.map((bucket) => (bucket / peak) * 31))
    });
  } catch {
    return {};
  } finally {
    void audioContext.close().catch(() => undefined);
  }
};

// Превью вложения продолжает жить в плейсхолдере ленты после отправки —
// objectURL отзываем с запасом, когда настоящее сообщение уже заменило его.
const PREVIEW_REVOKE_DELAY_MS = 5 * 60_000;
const revokeAttachmentPreviewLater = (attachment: PendingAttachment): void => {
  const url = attachment.previewUrl;
  if (!url) {
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), PREVIEW_REVOKE_DELAY_MS);
};

const revokeAttachmentPreview = (attachment: PendingAttachment | null): void => {
  if (attachment?.previewUrl) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
};

// Замер метаданных вложения не должен блокировать отправку: файл, который
// браузер не может декодировать, раньше держал промис бесконечно.
const METADATA_PROBE_TIMEOUT_MS = 4_000;

const readAttachmentMetadata = (
  file: File,
  kind: MediaUploadKind,
  previewUrl: string | null
): Promise<Pick<PendingAttachment, "durationSeconds" | "width" | "height" | "waveform">> =>
  Promise.race([
    readAttachmentMetadataUnbounded(file, kind, previewUrl),
    new Promise<Pick<PendingAttachment, never>>((resolve) =>
      window.setTimeout(() => resolve({}), METADATA_PROBE_TIMEOUT_MS)
    )
  ]);

const readAttachmentMetadataUnbounded = async (
  file: File,
  kind: MediaUploadKind,
  previewUrl: string | null
): Promise<Pick<PendingAttachment, "durationSeconds" | "width" | "height" | "waveform">> => {
  if (!previewUrl) {
    return {};
  }

  if (kind === "photo") {
    const image = new Image();
    return new Promise((resolve) => {
      const finish = (): void =>
        resolve({
          ...(Number.isFinite(image.naturalWidth) && image.naturalWidth > 0
            ? { width: image.naturalWidth }
            : {}),
          ...(Number.isFinite(image.naturalHeight) && image.naturalHeight > 0
            ? { height: image.naturalHeight }
            : {})
        });
      image.onload = finish;
      image.onerror = finish;
      image.src = previewUrl;
    });
  }

  if (kind === "video" || kind === "videoNote") {
    const video = document.createElement("video");
    video.preload = "metadata";
    const meta = await new Promise<{ width?: number; height?: number; durationSeconds?: number }>(
      (resolve) => {
        const finish = (): void =>
          resolve({
            ...(Number.isFinite(video.videoWidth) && video.videoWidth > 0
              ? { width: video.videoWidth }
              : {}),
            ...(Number.isFinite(video.videoHeight) && video.videoHeight > 0
              ? { height: video.videoHeight }
              : {}),
            ...(Number.isFinite(video.duration) && video.duration > 0
              ? { durationSeconds: Math.round(video.duration) }
              : {})
          });
        video.onloadedmetadata = finish;
        video.onerror = finish;
        video.src = previewUrl;
      }
    );
    video.remove();
    return meta;
  }

  if (kind === "voice") {
    // Меряем длительность ДВУМЯ способами и берём максимум: decodeAudioData
    // (точный, но падает на неподдержанных кодеках) и <audio>.duration
    // (переживает больше форматов, но у VBR-файлов занижает оценку).
    // Заниженный duration уезжает в TDLib — и Telegram обрезает
    // воспроизведение (так голосовые из шаблонов приходили «половинками»).
    const analysis = await analyzeRecordedAudio(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    const elementDuration = await new Promise<number | undefined>((resolve) => {
      const finish = (): void =>
        resolve(
          Number.isFinite(audio.duration) && audio.duration > 0
            ? Math.ceil(audio.duration)
            : undefined
        );
      audio.onloadedmetadata = finish;
      audio.onerror = finish;
      audio.src = previewUrl;
    });
    audio.remove();
    const durationSeconds = Math.max(analysis.durationSeconds ?? 0, elementDuration ?? 0);
    return {
      ...(durationSeconds > 0 ? { durationSeconds } : {}),
      ...(analysis.waveform ? { waveform: analysis.waveform } : {})
    };
  }

  void file;
  return {};
};

// Быстрый ряд реакций (в контекстном меню на сообщение) — 8 самых частых.
const QUICK_REACTIONS = ["👍", "❤️", "🔥", "😂", "😮", "😢", "🎉", "🙏"];
// Расширенная палитра для dropdown-пикера реакций у пузыря сообщения.
// Верхний ряд повторяет QUICK_REACTIONS, дальше — набор из основного TG,
// упорядоченный от чаще к реже. Обёрнут в скролл-контейнер, чтобы не
// растягивать чат при малой ширине.
const EXTENDED_REACTIONS = [
  "👍", "❤️", "🔥", "😂", "😮", "😢", "🎉", "🙏",
  "👏", "😍", "💯", "👌", "🤔", "🤯", "🥰", "😎",
  "😅", "😉", "🤗", "🤝", "💪", "🙌", "✅", "❗",
  "❓", "🤣", "😭", "😱", "🤬", "😴", "🙄", "😇",
  "🥳", "🤩", "😐", "😢", "🥺", "🤡", "💩", "🤮",
  "🍾", "🎂", "🌚", "🌭", "🦄", "🕊", "❤️‍🔥", "💔"
];

// Telegram отдаёт emoji реакций в канонической форме БЕЗ variation selector
// (❤ = U+2764, а не U+2764 U+FE0F) — часть браузеров рендерит такие
// BMP-символы чёрным текстовым глифом. Для отображения дорисовываем FE0F;
// в команды всегда уходит исходная строка (worker сам нормализует).
const emojiForDisplay = (emoji: string): string =>
  /[\u{1F000}-\u{1FAFF}]/u.test(emoji) || emoji.includes("\uFE0F")
    ? emoji
    : `${emoji}\uFE0F`;

const wrapEntity = (entity: MessageTextEntity, content: React.ReactNode, key: number) => {
  switch (entity.type) {
    case "bold":
      return <strong key={key}>{content}</strong>;
    case "italic":
      return <em key={key}>{content}</em>;
    case "underline":
      return <u key={key}>{content}</u>;
    case "strike":
      return <s key={key}>{content}</s>;
    case "spoiler":
      return (
        <span key={key} className="message-spoiler">
          {content}
        </span>
      );
    case "code":
      return <code key={key} className="message-code">{content}</code>;
    case "pre":
      return (
        <pre key={key} className="message-pre">
          <code>{content}</code>
        </pre>
      );
    case "link": {
      // Голый URL в тексте приходит с url="" (адрес = сам текст) — раньше
      // href="" открывал текущую страницу. Схемы — только безопасные: в
      // href из чужого сообщения не должен попасть javascript:.
      const raw = (entity.url || (typeof content === "string" ? content : "")).trim();
      const href = /^(https?:|tg:|mailto:)/i.test(raw)
        ? raw
        : /^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(raw)
          ? `https://${raw}`
          : undefined;
      return href ? (
        <a key={key} href={href} target="_blank" rel="noreferrer" title={href}>
          {content}
        </a>
      ) : (
        <span key={key}>{content}</span>
      );
    }
    case "mention":
    case "hashtag":
      return (
        <span key={key} className="message-mention">
          {content}
        </span>
      );
  }
};

const renderTextWithEntities = (
  text: string,
  entities: MessageTextEntity[] | undefined
): React.ReactNode => {
  if (!entities || entities.length === 0) {
    return text;
  }
  const sorted = [...entities].sort((a, b) => a.offset - b.offset);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let keyCounter = 0;
  for (const entity of sorted) {
    if (entity.offset < cursor) {
      continue;
    }
    if (entity.offset > cursor) {
      nodes.push(text.slice(cursor, entity.offset));
    }
    const slice = text.slice(entity.offset, entity.offset + entity.length);
    nodes.push(wrapEntity(entity, slice, keyCounter++));
    cursor = entity.offset + entity.length;
  }
  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }
  return nodes;
};

// Оценка высоты строки ленты по содержимому — нужна виртуализатору ДО того,
// как строка отрисована и померена. Точность важна для сообщений ботов: у
// «трекера депов» пузырь с текстом и десятком inline-кнопок занимает 300–500px
// против прежней константы в 64px, и при быстром скролле такое сообщение
// успевало нарисоваться поверх соседних, пока не приезжал реальный замер.
// Цифры — грубые средние по текущим стилям (см. .message-bubble* в styles.css);
// промахнуться на десяток пикселей не страшно, реальная высота всё равно
// домеряется ResizeObserver'ом.
const MESSAGE_TEXT_CHARS_PER_LINE = 46;
const MESSAGE_LINE_HEIGHT = 21;

// Габариты медиа-рамки из размеров TDLib (width/height фото/видео). Рамка
// получает фиксированный aspect-ratio ДО загрузки картинки, поэтому высота
// ряда не меняется в момент подгрузки — раньше плейсхолдер (160×220) и
// загруженное фото (до 380×420) были разного размера, виртуализатор
// перемерял ряд и лента прыгала вверх-вниз. Ограничения повторяют CSS:
// ширина ≤ 380, высота ≤ 420.
const MEDIA_FRAME_MAX_WIDTH = 380;
const MEDIA_FRAME_MAX_HEIGHT = 420;
const MEDIA_FRAME_MIN_WIDTH = 160;
const mediaFrameSize = (
  width: number | undefined,
  height: number | undefined
): { width: number; height: number } | null => {
  if (!width || !height || width <= 0 || height <= 0) {
    return null;
  }
  let frameWidth = Math.min(MEDIA_FRAME_MAX_WIDTH, width);
  let frameHeight = (frameWidth * height) / width;
  if (frameHeight > MEDIA_FRAME_MAX_HEIGHT) {
    frameHeight = MEDIA_FRAME_MAX_HEIGHT;
    frameWidth = (frameHeight * width) / height;
  }
  if (frameWidth < MEDIA_FRAME_MIN_WIDTH) {
    frameWidth = MEDIA_FRAME_MIN_WIDTH;
    frameHeight = Math.min(MEDIA_FRAME_MAX_HEIGHT, (frameWidth * height) / width);
  }
  return { width: Math.round(frameWidth), height: Math.round(frameHeight) };
};
const mediaFrameStyle = (
  width: number | undefined,
  height: number | undefined
): React.CSSProperties | undefined => {
  const size = mediaFrameSize(width, height);
  if (!size) {
    return undefined;
  }
  return {
    width: `${size.width}px`,
    maxWidth: "100%",
    aspectRatio: `${size.width} / ${size.height}`
  };
};

const estimateMessageHeight = (message: MessageViewModel | undefined): number => {
  if (!message) {
    return 64;
  }
  // Паддинги пузыря + строка «время + статус».
  let height = 40;
  if (message.replyTo) {
    height += 38;
  }
  if (message.forwardedFrom) {
    height += 18;
  }
  if (message.senderName && !message.outgoing) {
    height += 18;
  }
  if (message.reactions && message.reactions.length > 0) {
    height += 28;
  }

  const content = message.content;
  const caption = content.kind === "text" ? content.text : message.text;
  const explicitLines = caption ? caption.split("\n").length : 0;
  const wrappedLines = caption
    ? Math.ceil(caption.length / MESSAGE_TEXT_CHARS_PER_LINE)
    : 0;
  height += Math.max(explicitLines, wrappedLines) * MESSAGE_LINE_HEIGHT;

  switch (content.kind) {
    case "photo":
    case "video": {
      const frame = mediaFrameSize(content.width, content.height);
      height += frame ? frame.height : 220;
      break;
    }
    case "videoNote":
      height += 200;
      break;
    case "sticker":
      height += 128;
      break;
    case "voice":
      height += 48;
      break;
    case "document":
      height += 44;
      break;
    default:
      break;
  }

  if (message.replyMarkup && message.replyMarkup.rows.length > 0) {
    // Ряд кнопок ≈ 32px + 4px gap; кнопки переносятся редко.
    height += message.replyMarkup.rows.length * 36 + 6;
  }

  return Math.max(44, Math.round(height));
};

interface MediaRenderContext {
  senderLabel: string;
  // Открыть фото/видео в просмотрщике по центру экрана. Лента подставляет
  // сюда обработчик, который собирает соседние медиа для листания ←/→.
  onOpenMedia(payload: {
    kind: "photo" | "video";
    url: string;
    mimeType?: string;
    caption?: string;
    senderLabel: string;
  }): void;
  // Запуск транскрибации голосового; не передаётся там, где распознавание
  // не имеет смысла (например, снапшоты удалённых сообщений).
  onTranscribeVoice?: () => void;
}

// Сетка альбома (медиагруппы): фото/видео всех сообщений альбома одним
// пузырём, подпись — первая непустая. Клик по плитке открывает просмотрщик
// на этом элементе (лента собирает соседние медиа для листания).
const renderAlbumContent = (items: MessageViewModel[], context: MediaRenderContext) => {
  const tiles = items.filter(
    (item) => item.content.kind === "photo" || item.content.kind === "video"
  );
  const caption =
    items
      .map((item) =>
        "caption" in item.content && typeof item.content.caption === "string"
          ? item.content.caption.trim()
          : ""
      )
      .find((text) => text.length > 0) ?? "";
  const count = Math.min(tiles.length, 10);
  return (
    <div className="message-media">
      <div className={`message-album message-album--${count}`}>
        {tiles.map((item) => {
          const content = item.content as Extract<MessageContent, { kind: "photo" | "video" }>;
          const previewUrl = content.url ?? content.thumbnailUrl;
          const openUrl = content.kind === "video" ? content.url : (content.url ?? previewUrl);
          return (
            <button
              type="button"
              key={item.id}
              className="message-album__tile"
              data-message-id={item.id}
              disabled={!openUrl}
              onClick={() => {
                if (!openUrl) return;
                context.onOpenMedia({
                  kind: content.kind,
                  url: openUrl,
                  ...(content.kind === "video" && content.mimeType
                    ? { mimeType: content.mimeType }
                    : {}),
                  ...(caption ? { caption } : {}),
                  senderLabel: context.senderLabel
                });
              }}
              title={content.kind === "video" ? "Видео" : "Фото"}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={caption || (content.kind === "video" ? "Видео" : "Фотография")}
                  loading="lazy"
                  onError={retryMediaImageOnce}
                  decoding="async"
                />
              ) : (
                <span className="message-album__placeholder">Загружаем…</span>
              )}
              {content.kind === "video" ? (
                <span className="message-album__play" aria-hidden="true">
                  ▶
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="message-media__document-hint">
        От: {context.senderLabel} · альбом, {tiles.length}{" "}
        {tiles.length === 1 ? "файл" : tiles.length < 5 ? "файла" : "файлов"}
      </div>
      {caption ? (
        <div className="message-bubble__text" dir="auto">
          {caption}
        </div>
      ) : null}
    </div>
  );
};

const renderMessageContent = (content: MessageContent, context: MediaRenderContext) => {
  switch (content.kind) {
    case "text":
      return (
        <div className="message-bubble__text" dir="auto">
          {renderTextWithEntities(content.text, content.entities)}
        </div>
      );
    case "service":
      // Сервисное как обычный текст — на случай снапшотов удалённых
      // сообщений и других flow, где сервисное всё-таки попадает в пузырь.
      return (
        <div className="message-bubble__text" dir="auto">
          {content.text}
        </div>
      );
    case "photo": {
      const previewUrl = content.url ?? content.thumbnailUrl;
      const frameStyle = mediaFrameStyle(content.width, content.height);
      return (
        <div className="message-media">
          {previewUrl ? (
            <a
              href={content.url ?? previewUrl}
              target="_blank"
              rel="noreferrer"
              className={`message-media__photo-link${frameStyle ? " message-media__frame" : ""}`}
              style={frameStyle}
              onClick={(event) => {
                event.preventDefault();
                context.onOpenMedia({
                  kind: "photo",
                  url: content.url ?? previewUrl,
                  ...(content.caption ? { caption: content.caption } : {}),
                  senderLabel: context.senderLabel
                });
              }}
            >
              <img
                src={previewUrl}
                alt={content.caption ?? "Фотография"}
                loading="lazy"
                onError={retryMediaImageOnce}
                decoding="async"
                className="message-media__photo"
                {...(content.width && content.height
                  ? { width: content.width, height: content.height }
                  : {})}
              />
            </a>
          ) : (
            <div
              className={`message-media__placeholder${frameStyle ? " message-media__frame" : ""}`}
              style={frameStyle}
            >
              Загружаем фото…
            </div>
          )}
          <div className="message-media__document-hint">От: {context.senderLabel}</div>
          {content.caption ? <div className="message-bubble__text" dir="auto">{content.caption}</div> : null}
        </div>
      );
    }
    case "video": {
      const previewUrl = content.url ?? content.thumbnailUrl;
      const frameStyle = mediaFrameStyle(content.width, content.height);
      return (
        <div className="message-media">
          {content.url ? (
            <>
              <video
                className={`message-media__video${frameStyle ? " message-media__frame" : ""}`}
                style={frameStyle}
                controls
                preload="metadata"
                poster={content.thumbnailUrl}
                {...(content.width && content.height
                  ? { width: content.width, height: content.height }
                  : {})}
              >
                <source src={content.url} type={content.mimeType ?? "video/mp4"} />
              </video>
              <button
                type="button"
                className="message-media__lightbox-trigger"
                onClick={() =>
                  context.onOpenMedia({
                    kind: "video",
                    url: content.url!,
                    ...(content.mimeType ? { mimeType: content.mimeType } : {}),
                    ...(content.caption ? { caption: content.caption } : {}),
                    senderLabel: context.senderLabel
                  })
                }
              >
                Открыть на весь экран
              </button>
            </>
          ) : previewUrl ? (
            <div
              className={`message-media__photo-link${frameStyle ? " message-media__frame" : ""}`}
              style={frameStyle}
            >
              <img
                src={previewUrl}
                alt={content.caption ?? content.fileName ?? "Видео"}
                loading="lazy"
                onError={retryMediaImageOnce}
                decoding="async"
                className="message-media__photo"
                {...(content.width && content.height
                  ? { width: content.width, height: content.height }
                  : {})}
              />
            </div>
          ) : (
            <div
              className={`message-media__placeholder${frameStyle ? " message-media__frame" : ""}`}
              style={frameStyle}
            >
              Загружаем видео…
            </div>
          )}
          <div className="message-media__document-hint">
            {[
              `От: ${context.senderLabel}`,
              formatFileSize(content.sizeBytes),
              content.durationSeconds ? `${content.durationSeconds} c` : null
            ]
              .filter((value): value is string => Boolean(value))
              .join(" · ")}
          </div>
          {content.caption ? <div className="message-bubble__text" dir="auto">{content.caption}</div> : null}
        </div>
      );
    }
    case "document": {
      const secondaryLabel =
        [formatFileSize(content.sizeBytes), content.mimeType]
          .filter((value): value is string => Boolean(value))
          .join(" · ") || (content.url ? "Нажмите, чтобы открыть" : "Загружаем файл…");

      return (
        <div className="message-media message-media--document">
          {content.thumbnailUrl ? (
            <img
              src={content.thumbnailUrl}
              alt={content.fileName ?? "Файл"}
              loading="lazy"
              decoding="async"
              className="message-media__document-thumb"
            />
          ) : (
            <div className="message-media__document-icon">FILE</div>
          )}
          <div className="message-media__document-body">
            <div className="message-media__document-name">
              {content.fileName ?? "Файл"}
            </div>
            <div className="message-media__document-hint">
              От: {context.senderLabel}
              {secondaryLabel ? ` · ${secondaryLabel}` : ""}
            </div>
            {content.caption ? (
              <div className="message-media__document-caption">{content.caption}</div>
            ) : null}
          </div>
          {content.url ? (
            <a
              href={content.url}
              target="_blank"
              rel="noreferrer"
              className="message-media__action"
            >
              Открыть
            </a>
          ) : (
            <span className="message-media__action message-media__action--muted">...</span>
          )}
        </div>
      );
    }
    case "voice":
      return (
        <div className="message-media message-media--voice">
          <VoiceMessage
            url={content.url}
            mimeType={content.mimeType}
            waveform={content.waveform}
            durationSeconds={content.durationSeconds}
            transcript={content.transcript}
            transcriptStatus={content.transcriptStatus}
            onTranscribe={context.onTranscribeVoice}
          />
          {content.caption ? <div className="message-bubble__text" dir="auto">{content.caption}</div> : null}
        </div>
      );
    case "videoNote":
      return (
        <div className="message-media message-media--video-note">
          <VideoNoteMessage
            url={content.url}
            thumbnailUrl={content.thumbnailUrl}
            mimeType={content.mimeType}
            durationSeconds={content.durationSeconds}
            transcript={content.transcript}
            transcriptStatus={content.transcriptStatus}
            onTranscribe={context.onTranscribeVoice}
          />
        </div>
      );
    case "sticker": {
      // Telegram-стиль: стикер занимает фикс. ширину (≈192px), плавающая
      // высота сохраняет соотношение. Если thumbUrl ещё не докачался —
      // показываем emoji-фолбэк, чтобы не было «битой картинки».
      const aspect =
        typeof content.width === "number" &&
        typeof content.height === "number" &&
        content.width > 0 &&
        content.height > 0
          ? content.width / content.height
          : 1;
      return (
        <div className="message-media message-media--sticker">
          {content.thumbUrl ? (
            <img
              src={content.thumbUrl}
              alt={content.emoji ?? "стикер"}
              className="message-media__sticker-image"
              style={{ aspectRatio: aspect }}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="message-media__sticker-glyph" aria-label="Стикер">
              {content.emoji ?? "🖼️"}
            </div>
          )}
          <div className="message-media__document-hint">
            {content.emoji ? `${content.emoji} ` : ""}Стикер от {context.senderLabel}
          </div>
        </div>
      );
    }
  }
};

// Содержимое плейсхолдера ДО загрузки файла: вместо fileId — локальный
// objectURL в url, чтобы пузырь сразу показывал само фото/видео.
const buildLocalPreviewContent = (
  attachment: PendingAttachment,
  caption: string
): MessageContent => {
  const url = attachment.previewUrl ?? undefined;
  switch (attachment.kind) {
    case "photo":
      return {
        kind: "photo",
        fileId: "",
        ...(url ? { url } : {}),
        ...(caption ? { caption } : {}),
        ...(attachment.width ? { width: attachment.width } : {}),
        ...(attachment.height ? { height: attachment.height } : {})
      };
    case "video":
      return {
        kind: "video",
        fileId: "",
        fileName: attachment.file.name,
        ...(url ? { url } : {}),
        ...(attachment.file.type ? { mimeType: attachment.file.type } : {}),
        sizeBytes: attachment.file.size,
        ...(caption ? { caption } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {}),
        ...(attachment.width ? { width: attachment.width } : {}),
        ...(attachment.height ? { height: attachment.height } : {})
      };
    case "voice":
      return {
        kind: "voice",
        fileId: "",
        ...(url ? { url } : {}),
        ...(attachment.file.type ? { mimeType: attachment.file.type } : {}),
        ...(caption ? { caption } : {}),
        ...(attachment.waveform ? { waveform: attachment.waveform } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {})
      };
    case "videoNote":
      return {
        kind: "videoNote",
        fileId: "",
        ...(url ? { url } : {}),
        ...(attachment.file.type ? { mimeType: attachment.file.type } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {}),
        ...(attachment.width && attachment.height
          ? { length: Math.min(attachment.width, attachment.height) }
          : {})
      };
    case "document":
      return {
        kind: "document",
        fileId: "",
        fileName: attachment.file.name,
        ...(attachment.file.type ? { mimeType: attachment.file.type } : {}),
        sizeBytes: attachment.file.size,
        ...(caption ? { caption } : {})
      };
  }
};

// Загрузка вложений на сервер параллельно (не больше UPLOAD_PARALLELISM
// одновременно), результат — в исходном порядке. Раньше файлы грузились
// строго по одному: альбом из 5 фото ждал пять полных загрузок подряд.
const UPLOAD_PARALLELISM = 3;
type UploadedHandle = Awaited<ReturnType<typeof mediaApi.uploadFile>>;
const uploadAttachmentsParallel = async (
  wsUrl: string,
  attachments: readonly PendingAttachment[]
): Promise<UploadedHandle[]> => {
  const results: UploadedHandle[] = new Array(attachments.length);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < attachments.length) {
      const index = cursor;
      cursor += 1;
      const attachment = attachments[index]!;
      results[index] = await mediaApi.uploadFile(wsUrl, attachment.file, attachment.kind);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_PARALLELISM, attachments.length) }, () => worker())
  );
  return results;
};

const buildAttachmentContent = (
  attachment: PendingAttachment,
  uploaded: { fileId: string; fileName: string; mimeType?: string; sizeBytes?: number },
  caption: string
): MessageContent => {
  switch (attachment.kind) {
    case "photo":
      return {
        kind: "photo",
        fileId: uploaded.fileId,
        ...(caption ? { caption } : {}),
        ...(attachment.width ? { width: attachment.width } : {}),
        ...(attachment.height ? { height: attachment.height } : {})
      };
    case "video":
      return {
        kind: "video",
        fileId: uploaded.fileId,
        fileName: uploaded.fileName,
        ...(uploaded.mimeType ? { mimeType: uploaded.mimeType } : {}),
        ...(uploaded.sizeBytes ? { sizeBytes: uploaded.sizeBytes } : {}),
        ...(caption ? { caption } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {}),
        ...(attachment.width ? { width: attachment.width } : {}),
        ...(attachment.height ? { height: attachment.height } : {})
      };
    case "voice":
      return {
        kind: "voice",
        fileId: uploaded.fileId,
        ...(uploaded.mimeType ? { mimeType: uploaded.mimeType } : {}),
        ...(uploaded.sizeBytes ? { sizeBytes: uploaded.sizeBytes } : {}),
        ...(caption ? { caption } : {}),
        ...(attachment.waveform ? { waveform: attachment.waveform } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {})
      };
    case "videoNote":
      // Кружок: caption у Telegram не поддерживается, length — сторона
      // квадрата (min(width, height) исходного видео).
      return {
        kind: "videoNote",
        fileId: uploaded.fileId,
        ...(uploaded.mimeType ? { mimeType: uploaded.mimeType } : {}),
        ...(uploaded.sizeBytes ? { sizeBytes: uploaded.sizeBytes } : {}),
        ...(attachment.durationSeconds ? { durationSeconds: attachment.durationSeconds } : {}),
        ...(attachment.width && attachment.height
          ? { length: Math.min(attachment.width, attachment.height) }
          : {})
      };
    case "document":
      return {
        kind: "document",
        fileId: uploaded.fileId,
        fileName: uploaded.fileName,
        ...(uploaded.mimeType ? { mimeType: uploaded.mimeType } : {}),
        ...(uploaded.sizeBytes ? { sizeBytes: uploaded.sizeBytes } : {}),
        ...(caption ? { caption } : {})
      };
  }
};

/**
 * Попал ли двойной клик по слову внутри пузыря.
 *
 * Браузер успевает выделить слово ДО того, как прилетит `dblclick`, поэтому
 * достаточно посмотреть на текущее выделение. Проверяем две вещи:
 *   * выделение живёт внутри этого пузыря (а не осталось от прошлого клика
 *     в другом сообщении);
 *   * точка клика попала в прямоугольник выделенного слова — иначе это был
 *     клик по пустому месту пузыря, где Chrome «дотягивает» каретку до
 *     ближайшего слова.
 *
 * Rect'ы гоняем через visualRectOf: при UI-масштабе (zoom на <html>) в Safari
 * они логические, а clientX/Y — всегда визуальные.
 */
const doubleClickSelectedWord = (
  bubble: HTMLElement,
  clientX: number,
  clientY: number
): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return false;
  }
  if (selection.toString().length === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!bubble.contains(range.commonAncestorContainer)) {
    return false;
  }
  return Array.from(range.getClientRects()).some((domRect) => {
    const rect = visualRectOf(domRect);
    return (
      clientX >= rect.left - 2 &&
      clientX <= rect.right + 2 &&
      clientY >= rect.top - 2 &&
      clientY <= rect.bottom + 2
    );
  });
};

interface MessageRowProps {
  message: MessageViewModel;
  index: number;
  translateY: number;
  measureElement: (node: Element | null) => void;
  timeZone: TimeZonePref;
  selectedChatId: string | null;
  isGroupChat: boolean;
  lastReadOutboxMessageId: string | undefined;
  lastReadOutboxAt: string | undefined;
  isPinnedMessage: boolean;
  showDaySeparator: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  // Альбом (медиагруппа TDLib): первое сообщение альбома несёт все его
  // элементы (albumItems) и рисует сетку фото одним пузырём, как в Telegram;
  // остальные сообщения альбома — строки нулевой высоты (albumHidden), чтобы
  // виртуализатор и переходы по messageId продолжали работать.
  albumItems: MessageViewModel[] | null;
  albumHidden: boolean;
  showUnreadDivider: boolean;
  isFlash: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  translation:
    | { text: string; entities?: MessageTextEntity[]; targetLanguage: string }
    | undefined;
  reactionPickerOpen: boolean;
  onToggleSelection: (messageId: string) => void;
  onReply: (messageId: string) => void;
  onScrollToMessage: (messageId: string) => void;
  onOpenMedia: MediaRenderContext["onOpenMedia"];
  onTranscribeVoice: (chatId: string, messageId: string) => void;
  onClearTranslation: (messageId: string) => void;
  onPressInlineButton: (chatId: string, messageId: string, data: string) => void;
  onToggleReaction: (chatId: string, messageId: string, emoji: string) => void;
  onOpenReactionPicker: (messageId: string | null) => void;
  onOpenContextMenu: (menu: { messageId: string; x: number; y: number } | null) => void;
  // Клик по сервисному сообщению об автоудалении → окно «Никогда»
  // (см. renderMessageContent + service kind). Прочие serviceKind клик
  // просто игнорируют — как в оригинальном TG.
  onOpenAutoDeleteEditor: () => void;
}

// Строка ленты, вынесенная из ChatView и обёрнутая в memo. Раньше строки
// рендерились инлайном в JSX ChatView: ЛЮБОЙ setState компонента (каждый
// символ в композере, тик таймера записи 250 мс, floating-day при скролле)
// перерендеривал все ~30 видимых сообщений с полным renderMessageContent.
// Все пропсы — скаляры, стабильные колбэки или стабильные per-message
// объекты, поэтому дефолтного shallow-сравнения memo достаточно.
const MessageRow = memo(
  ({
    message,
    index,
    translateY,
    measureElement,
    timeZone,
    selectedChatId,
    isGroupChat,
    lastReadOutboxMessageId,
    lastReadOutboxAt,
    isPinnedMessage,
    showDaySeparator,
    isFirstInGroup,
    isLastInGroup,
    albumItems,
    albumHidden,
    showUnreadDivider,
    isFlash,
    selectionMode,
    isSelected,
    translation,
    reactionPickerOpen,
    onToggleSelection,
    onReply,
    onScrollToMessage,
    onOpenMedia,
    onTranscribeVoice,
    onClearTranslation,
    onPressInlineButton,
    onToggleReaction,
    onOpenReactionPicker,
    onOpenContextMenu,
    onOpenAutoDeleteEditor
  }: MessageRowProps) => {
    const status = message.isDeleted
      ? null
      : messageStatusLabel(
          message.outgoing,
          message.id,
          message.occurredAt,
          message.isPending,
          lastReadOutboxMessageId,
          lastReadOutboxAt
        );
    const showPeerAvatar = isGroupChat && !message.outgoing;

    // Принудительный перезамер строки при смене её содержимого. Голого
    // ResizeObserver (он внутри measureElement) не хватает: бот, у которого
    // нажали inline-кнопку, редактирует сообщение и меняет клавиатуру, пока
    // строка размонтирована (вне overscan). Виртуализатор оставляет в кэше
    // СТАРУЮ высоту, а при возврате скроллом отводит строке меньше места, чем
    // она занимает — соседние сообщения наезжают друг на друга.
    const rowRef = useRef<HTMLDivElement | null>(null);
    const attachRow = useCallback(
      (node: HTMLDivElement | null) => {
        rowRef.current = node;
        measureElement(node);
      },
      [measureElement]
    );
    useLayoutEffect(() => {
      if (rowRef.current) {
        measureElement(rowRef.current);
      }
    }, [
      measureElement,
      message,
      translation,
      showDaySeparator,
      showUnreadDivider,
      isFirstInGroup,
      isLastInGroup,
      albumItems,
      albumHidden,
      reactionPickerOpen,
      selectionMode
    ]);

    if (albumHidden) {
      // Элемент альбома, показанный в сетке первого сообщения: строка есть
      // (индексы виртуализатора и scrollToMessage по id живы), высоты нет.
      return (
        <div
          data-index={index}
          ref={attachRow}
          className="message-list__item message-list__item--album-hidden"
          data-message-id={message.id}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${translateY}px)`
          }}
        />
      );
    }

    return (
      <div
        data-index={index}
        ref={attachRow}
        className="message-list__item"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${translateY}px)`
        }}
      >
        {showDaySeparator ? (
          <div className="day-separator" role="separator">
            <span
              className="day-separator__badge"
              title={formatFullDateTime(message.occurredAt, timeZone)}
            >
              {formatDaySeparator(message.occurredAt, timeZone)}
            </span>
          </div>
        ) : null}
        {showUnreadDivider ? (
          <div className="unread-divider" role="separator">
            Непрочитанные сообщения
          </div>
        ) : null}
        {message.content.kind === "service" ? (
          <div
            className={
              message.content.serviceKind === "chatSetMessageAutoDelete"
                ? "service-message service-message--interactive"
                : "service-message"
            }
            data-message-id={message.id}
            role={
              message.content.serviceKind === "chatSetMessageAutoDelete"
                ? "button"
                : undefined
            }
            tabIndex={
              message.content.serviceKind === "chatSetMessageAutoDelete" ? 0 : undefined
            }
            onClick={() => {
              if (
                (message.content as { kind: string; serviceKind?: string }).serviceKind ===
                "chatSetMessageAutoDelete"
              ) {
                onOpenAutoDeleteEditor();
              }
            }}
            onKeyDown={(event) => {
              if (
                (message.content as { kind: string; serviceKind?: string }).serviceKind !==
                "chatSetMessageAutoDelete"
              ) {
                return;
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenAutoDeleteEditor();
              }
            }}
            onContextMenu={(event) => {
              // ПКМ по сервисному — то же контекстное меню, что у обычного
              // сообщения: даёт «Удалить у обеих сторон» и прочее.
              event.preventDefault();
              onOpenContextMenu({
                messageId: message.id,
                x: event.clientX,
                y: event.clientY
              });
            }}
            title={
              message.content.serviceKind === "chatSetMessageAutoDelete"
                ? "Нажмите, чтобы изменить автоудаление"
                : undefined
            }
          >
            {message.content.text}
          </div>
        ) : (
        <div
          className={[
            "mrow",
            message.outgoing ? "mrow--out" : "mrow--in",
            isLastInGroup ? "mrow--last" : "",
            isFlash ? "mrow--flash" : ""
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {showPeerAvatar ? (
            isLastInGroup ? (
              <ChatAvatar
                title={message.senderName ?? "?"}
                className={`avatar mrow__avatar ${avatarGradientClass(message.senderId)}`}
              />
            ) : (
              <div className="mrow__avatar-spacer" aria-hidden="true" />
            )
          ) : null}
          <div
            data-message-id={message.id}
            className={[
              "message-bubble",
              message.outgoing ? "message-bubble--outgoing" : "",
              message.isDeleted ? "message-bubble--deleted" : "",
              isLastInGroup ? "message-bubble--tail" : "",
              albumItems ? "message-bubble--album" : "",
              selectionMode ? "message-bubble--selectable" : "",
              isSelected ? "message-bubble--selected" : ""
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={(event) => {
              // В режиме выбора клик по пузырю переключает выбор.
              // Пропускаем клики по интерактивным элементам внутри
              // пузыря (ссылки, кнопки) и по выделенному тексту.
              if (!selectionMode) return;
              if (window.getSelection()?.toString().length) return;
              const target = event.target as HTMLElement | null;
              if (target?.closest("a,button")) return;
              event.preventDefault();
              onToggleSelection(message.id);
            }}
            onDoubleClick={(event) => {
              // Двойной клик по сообщению — ответить на него (как в ТГ).
              // Но если клик пришёлся по тексту, браузер уже выделил слово —
              // оставляем выделение, чтобы его можно было скопировать.
              if (selectionMode || message.isDeleted || message.isPending) return;
              const target = event.target as HTMLElement | null;
              if (target?.closest("a,button,video,audio,input,textarea")) return;
              if (
                doubleClickSelectedWord(event.currentTarget, event.clientX, event.clientY)
              ) {
                return;
              }
              event.preventDefault();
              window.getSelection()?.removeAllRanges();
              onReply(message.id);
            }}
            onContextMenu={(event) => {
              if (message.isDeleted) {
                return;
              }
              // Не перехватываем ПКМ, только если выделение живёт ВНУТРИ
              // самого пузыря сообщения (юзер целится копировать текст).
              // Раньше проверяли просто `getSelection().toString()`, но на
              // Windows Chrome оставленное в композере выделение не сбрасывалось
              // при клике по ленте — и по ПКМ поверх сообщения открывалось
              // родное браузерное меню вместо нашего.
              const sel = window.getSelection();
              const selectedText = sel?.toString() ?? "";
              if (selectedText.length > 0) {
                const anchor = sel?.anchorNode ?? null;
                const anchorElement =
                  anchor && anchor.nodeType === Node.ELEMENT_NODE
                    ? (anchor as Element)
                    : anchor?.parentElement ?? null;
                const selectionInsideBubble =
                  anchorElement?.closest(".message-bubble") ?? null;
                if (selectionInsideBubble) {
                  return;
                }
                // Выделение осталось в композере / сайдбаре — гасим его
                // сами, чтобы не мешало последующему копированию из нашего меню.
                sel?.removeAllRanges();
              }
              event.preventDefault();
              // В режиме выбора ПКМ также переключает выбор
              // (быстрее, чем открывать контекстное меню, которое
              // и так не имеет действий на выбранное множество).
              if (selectionMode) {
                onToggleSelection(message.id);
                return;
              }
              onOpenReactionPicker(null);
              onOpenContextMenu({
                messageId: message.id,
                x: event.clientX,
                y: event.clientY
              });
            }}
          >
            {selectionMode ? (
              <span
                className={
                  isSelected
                    ? "message-bubble__checkbox message-bubble__checkbox--on"
                    : "message-bubble__checkbox"
                }
                aria-hidden="true"
              >
                {isSelected ? <i className="ic ic-check" aria-hidden="true" /> : null}
              </span>
            ) : null}
            {isPinnedMessage ? (
              <div className="message-bubble__pin-indicator">Закреплено</div>
            ) : null}
            {message.outgoing && message.internalAuthorName ? (
              <div className="message-bubble__internal-author">
                {message.internalAuthorName}
              </div>
            ) : null}
            {isGroupChat && !message.outgoing && message.senderName && isFirstInGroup ? (
              <div
                className="message-bubble__sender"
                style={{ color: peerNameColor(message.senderId) }}
              >
                {message.senderName}
              </div>
            ) : null}
            {message.forwardedFrom ? (
              <div className="message-bubble__forwarded">
                <span className="message-bubble__forwarded-icon" aria-hidden="true">
                  ↪
                </span>
                <span className="message-bubble__forwarded-copy">
                  <span className="message-bubble__forwarded-label">
                    Переслано от
                  </span>
                  {message.forwardedFrom.senderId ? (
                    <button
                      type="button"
                      className="message-bubble__forwarded-name"
                      onClick={(event) => {
                        event.stopPropagation();
                        const senderId = message.forwardedFrom?.senderId;
                        if (!senderId) return;
                        const state = useRealtimeStore.getState();
                        if (state.chats[senderId]) {
                          state.selectChat(senderId);
                          return;
                        }
                        // Приватного чата с этим юзером ещё нет — подгружаем в
                        // фоне через глобальный поиск (он умеет по числовому
                        // ID) и параллельно копируем ID: если резолв не найдёт
                        // юзера, саппорт сможет вставить ID в общий поиск.
                        state.searchDialogs(senderId);
                        void copyTextToClipboard(senderId).then((ok) => {
                          if (ok) {
                            pushToast("ID пользователя скопирован");
                          }
                        });
                      }}
                      title="Открыть чат / скопировать ID"
                    >
                      {message.forwardedFrom.name}
                    </button>
                  ) : (
                    <strong>{message.forwardedFrom.name}</strong>
                  )}
                  {message.forwardedFrom.authorSignature ? (
                    <em> · {message.forwardedFrom.authorSignature}</em>
                  ) : null}
                </span>
              </div>
            ) : null}
            {message.isDeleted ? (
              <div className="message-bubble__deleted-label">
                Удалено
                {message.deletedAt ? ` · ${formatTime(message.deletedAt, timeZone)}` : ""}
              </div>
            ) : null}
            {message.replyTo ? (
              <button
                type="button"
                className="message-reply"
                onClick={() => onScrollToMessage(message.replyTo!.messageId)}
              >
                <strong>{message.replyTo.senderName ?? "Ответ"}</strong>
                <span>{message.replyTo.text}</span>
              </button>
            ) : null}
            <div className={message.isDeleted ? "message-bubble__content message-bubble__content--deleted" : "message-bubble__content"}>
              {albumItems ? (
                renderAlbumContent(albumItems, {
                  senderLabel: message.outgoing
                    ? message.internalAuthorName
                      ? `Вы · ${message.internalAuthorName}`
                      : "Вы"
                    : message.senderName ?? "Собеседник",
                  onOpenMedia
                })
              ) : null}
              {albumItems ? null : renderMessageContent(message.content, {
                senderLabel: message.outgoing
                  ? message.internalAuthorName
                    ? `Вы · ${message.internalAuthorName}`
                    : "Вы"
                  : message.senderName ?? "Собеседник",
                onOpenMedia,
                ...((message.content.kind === "voice" ||
                  message.content.kind === "videoNote") &&
                !message.isDeleted &&
                selectedChatId
                  ? {
                      onTranscribeVoice: () =>
                        onTranscribeVoice(selectedChatId, message.id)
                    }
                  : {})
              })}
            </div>
            {translation ? (
              <div className="message-bubble__translation">
                <div className="message-bubble__translation-label">
                  Перевод ({translation.targetLanguage}) ·{" "}
                  <button
                    type="button"
                    className="message-bubble__translation-clear"
                    onClick={() => onClearTranslation(message.id)}
                  >
                    скрыть
                  </button>
                </div>
                <div className="message-bubble__text" dir="auto">
                  {renderTextWithEntities(translation.text, translation.entities)}
                </div>
              </div>
            ) : null}
            {message.replyMarkup && message.replyMarkup.rows.length > 0 ? (
              <div className="message-bubble__keyboard" role="group" aria-label="Кнопки бота">
                {message.replyMarkup.rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="message-bubble__keyboard-row">
                    {row.buttons.map((button, buttonIndex) => {
                      const key = `${rowIndex}:${buttonIndex}`;
                      if (button.type === "callback") {
                        return (
                          <button
                            key={key}
                            type="button"
                            className="message-bubble__keyboard-button"
                            onClick={() => {
                              if (selectedChatId) {
                                onPressInlineButton(selectedChatId, message.id, button.data);
                              }
                            }}
                          >
                            {button.text}
                          </button>
                        );
                      }
                      if (button.type === "url" || button.type === "loginUrl") {
                        return (
                          <a
                            key={key}
                            className="message-bubble__keyboard-button message-bubble__keyboard-button--link"
                            href={button.url}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {button.text}
                          </a>
                        );
                      }
                      return (
                        <button
                          key={key}
                          type="button"
                          className="message-bubble__keyboard-button message-bubble__keyboard-button--disabled"
                          disabled
                          title="Этот тип кнопки не поддерживается клиентом"
                        >
                          {button.text}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : null}
            {message.reactions && message.reactions.length > 0 ? (
              <div className="message-bubble__reactions">
                {message.reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    type="button"
                    className={
                      reaction.chosen
                        ? "message-bubble__reaction message-bubble__reaction--chosen"
                        : "message-bubble__reaction"
                    }
                    onClick={() => {
                      if (selectedChatId) {
                        onToggleReaction(selectedChatId, message.id, reaction.emoji);
                      }
                    }}
                  >
                    <span>{emojiForDisplay(reaction.emoji)}</span>
                    <span className="message-bubble__reaction-count">{reaction.count}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {reactionPickerOpen ? (
              <div className="message-bubble__reaction-picker" role="menu">
                {EXTENDED_REACTIONS.map((emoji, index2) => (
                  <button
                    // Дедуп ключ: EXTENDED_REACTIONS содержит
                    // повторы (например ❤️ дважды из разных наборов),
                    // без индекса React ругается на duplicate key.
                    key={`${emoji}:${index2}`}
                    type="button"
                    className="message-bubble__reaction-picker-item"
                    onClick={() => {
                      if (selectedChatId) {
                        onToggleReaction(selectedChatId, message.id, emoji);
                      }
                      onOpenReactionPicker(null);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="message-bubble__footer">
              <div className="message-bubble__meta">
                {message.isDeleted ? <span>удалено</span> : null}
                {message.isEdited && !message.isDeleted ? <span>изменено</span> : null}
                {/* Подсказка с секундами: саппорты держат паузы между
                    отправками и сверяются именно по секундам. */}
                <span title={formatExactDateTime(message.occurredAt, timeZone)}>
                  {formatTime(message.occurredAt, timeZone)}
                </span>
                {status ? (
                  <span
                    title={`${
                      status === "✓✓"
                        ? "Прочитано"
                        : status === "✓"
                          ? "Отправлено"
                          : "Отправляется"
                    } · ${formatExactDateTime(message.occurredAt, timeZone)}`}
                    className={
                      status === "✓✓"
                        ? "message-bubble__status message-bubble__status--read"
                        : "message-bubble__status"
                    }
                  >
                    {status === "✓✓" ? (
                      <i className="ic ic-2checks" aria-label="Прочитано" />
                    ) : status === "✓" ? (
                      <i className="ic ic-1check" aria-label="Отправлено" />
                    ) : (
                      <i className="ic ic-sending" aria-label="Отправляется" />
                    )}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    );
  }
);
MessageRow.displayName = "MessageRow";

// memo без props = полная изоляция от ре-рендеров App: набор в поиске
// сайдбара, открытие меню/шитов и resize-стейт больше не перерисовывают
// это 5к-строчное дерево — оно рендерится только от собственного state и
// своих store-подписок (жалоба «низкая герцовка при взаимодействии»).
export const ChatView = memo(() => {
  const [trustMediaKind, setTrustMediaKind] = useState<MediaUploadKind | "link" | "payment">(
    "document"
  );
  const [trustLinkUrl, setTrustLinkUrl] = useState<string>("");
  const [trustPaymentText, setTrustPaymentText] = useState<string>("");
  const [draft, setDraft] = useState("");
  // Свой undo/redo для композера. Textarea контролируемая и её value
  // переписывается программно (черновик при смене чата, очистка после
  // отправки, дозаливка из стора) — нативный стек браузера от этого ломается
  // и Ctrl+Z возвращал «всё содержимое за последнее время» (в т.ч. уже
  // отправленные тексты). Стек живёт на один открытый чат: сбрасывается при
  // смене чата, после отправки и при входе в режим редактирования.
  type DraftSnapshot = { value: string; start: number; end: number };
  const draftHistoryRef = useRef<{
    past: DraftSnapshot[];
    future: DraftSnapshot[];
    lastEditAt: number;
  }>({ past: [], future: [], lastEditAt: 0 });
  const draftSelectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  // Актуальное значение черновика для колбэков со стабильной ссылкой
  // (вход в режим редактирования вызывается из memo-строк ленты).
  const draftRef = useRef("");
  draftRef.current = draft;
  // Черновик, который лежал в поле до входа в режим редактирования: на время
  // правки поле занято текстом сообщения, после выхода — возвращаем черновик.
  // Он не должен теряться ни при отмене, ни после сохранения правки (как в
  // оригинальном Telegram).
  const draftBeforeEditRef = useRef<string>("");
  const pendingDraftSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const DRAFT_HISTORY_LIMIT = 200;
  const DRAFT_HISTORY_GROUP_MS = 600;
  const resetDraftHistory = (): void => {
    draftHistoryRef.current = { past: [], future: [], lastEditAt: 0 };
  };
  const rememberDraftSelection = (el: HTMLTextAreaElement | null): void => {
    if (!el) return;
    draftSelectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
  };
  const handleDraftChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const next = event.target.value;
    const history = draftHistoryRef.current;
    const now = Date.now();
    // Быстрый набор склеиваем в один шаг (как в TG): новый снимок кладём
    // только после паузы > DRAFT_HISTORY_GROUP_MS или если это не набор
    // по одному символу (вставка/удаление куска — всегда отдельный шаг).
    const isSingleCharTyping = next.length === draft.length + 1 && next.startsWith(draft.slice(0, draftSelectionRef.current.start));
    const grouped =
      isSingleCharTyping && now - history.lastEditAt < DRAFT_HISTORY_GROUP_MS && history.past.length > 0;
    if (!grouped) {
      history.past.push({
        value: draft,
        start: Math.min(draftSelectionRef.current.start, draft.length),
        end: Math.min(draftSelectionRef.current.end, draft.length)
      });
      if (history.past.length > DRAFT_HISTORY_LIMIT) {
        history.past.splice(0, history.past.length - DRAFT_HISTORY_LIMIT);
      }
    }
    history.future = [];
    history.lastEditAt = now;
    rememberDraftSelection(event.target);
    setDraft(next);
  };
  const applyDraftSnapshot = (snapshot: DraftSnapshot): void => {
    pendingDraftSelectionRef.current = { start: snapshot.start, end: snapshot.end };
    draftSelectionRef.current = { start: snapshot.start, end: snapshot.end };
    setDraft(snapshot.value);
  };
  // «Добавить ссылку» к выделенному тексту (ПКМ по полю / Ctrl+K), см.
  // composer-link.tsx. Диапазон запоминаем в момент открытия: пока открыто
  // окно, фокус уходит из поля и selectionStart/End уже ненадёжны.
  const [composerLinkMenu, setComposerLinkMenu] = useState<{
    x: number;
    y: number;
    start: number;
    end: number;
  } | null>(null);
  const [linkEditor, setLinkEditor] = useState<{
    start: number;
    end: number;
    label: string;
    url: string;
    isExisting: boolean;
  } | null>(null);
  const openLinkEditor = (start: number, end: number): boolean => {
    const current = draftRef.current;
    const existing = findMarkdownLinkAt(current, start, end);
    if (existing) {
      setLinkEditor({ ...existing, isExisting: true });
      return true;
    }
    if (start === end) {
      return false;
    }
    setLinkEditor({ start, end, label: current.slice(start, end), url: "", isExisting: false });
    return true;
  };
  // Замена куска черновика одним шагом undo + курсор сразу за вставкой.
  const replaceDraftRange = (start: number, end: number, replacement: string): void => {
    const current = draftRef.current;
    const history = draftHistoryRef.current;
    history.past.push({ value: current, start, end });
    if (history.past.length > DRAFT_HISTORY_LIMIT) {
      history.past.splice(0, history.past.length - DRAFT_HISTORY_LIMIT);
    }
    history.future = [];
    history.lastEditAt = 0;
    const next = current.slice(0, start) + replacement + current.slice(end);
    const caret = start + replacement.length;
    pendingDraftSelectionRef.current = { start: caret, end: caret };
    draftSelectionRef.current = { start: caret, end: caret };
    setDraft(next);
    requestAnimationFrame(() => composerTextareaRef.current?.focus());
  };
  const undoDraft = (el: HTMLTextAreaElement): void => {
    const history = draftHistoryRef.current;
    const previous = history.past.pop();
    if (!previous) return;
    history.future.push({ value: draft, start: el.selectionStart, end: el.selectionEnd });
    history.lastEditAt = 0;
    applyDraftSnapshot(previous);
  };
  // Ctrl/Cmd+Enter в композере — перевод строки в позиции курсора. Браузер
  // по этому сочетанию в textarea не делает ничего, поэтому вставляем сами
  // и кладём шаг в свой undo-стек, как обычную правку текста.
  const insertDraftNewline = (el: HTMLTextAreaElement): void => {
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? start;
    const history = draftHistoryRef.current;
    history.past.push({ value: draft, start, end });
    if (history.past.length > DRAFT_HISTORY_LIMIT) {
      history.past.splice(0, history.past.length - DRAFT_HISTORY_LIMIT);
    }
    history.future = [];
    history.lastEditAt = 0;
    const caret = start + 1;
    pendingDraftSelectionRef.current = { start: caret, end: caret };
    draftSelectionRef.current = { start: caret, end: caret };
    setDraft(`${draft.slice(0, start)}\n${draft.slice(end)}`);
  };
  const redoDraft = (el: HTMLTextAreaElement): void => {
    const history = draftHistoryRef.current;
    const next = history.future.pop();
    if (!next) return;
    history.past.push({ value: draft, start: el.selectionStart, end: el.selectionEnd });
    history.lastEditAt = 0;
    applyDraftSnapshot(next);
  };
  // Несколько вложений за раз (paste/drag нескольких файлов) — отправляются
  // отдельными сообщениями, подпись уходит с первым.
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  // «Группировать» (2026-08-06): несколько фото/видео из композера — одним
  // альбомом. По умолчанию включено, как в оригинальном TG.
  const [composerGroupMedia, setComposerGroupMedia] = useState(true);
  // Индекс фото из pendingAttachments, открытого в редакторе стрелок.
  const [annotatingIndex, setAnnotatingIndex] = useState<number | null>(null);
  // Тост «✓ Фото скопировано» после «Копировать фото» из контекстного меню:
  // меню закрывается сразу, без подтверждения саппорт не понимает, сработало ли.
  const [copyToast, setCopyToast] = useState<string | null>(null);
  useEffect(() => {
    if (!copyToast) {
      return;
    }
    const handle = window.setTimeout(() => setCopyToast(null), 1600);
    return () => window.clearTimeout(handle);
  }, [copyToast]);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Панели над чатом (Поиск/Теги/Удалённые/Шаблоны) взаимоисключающие:
  // открытие одной закрывает предыдущую, иначе все четыре разом съедали
  // высоту и самого чата не было видно.
  const [activePanel, setActivePanel] = useState<
    "search" | "tags" | "deleted" | "trust" | null
  >(null);
  const tagsOpen = activePanel === "tags";
  const deletedOpen = activePanel === "deleted";
  const trustOpen = activePanel === "trust";
  const [trustCreateOpen, setTrustCreateOpen] = useState(false);
  // Вкладка панели шаблонов: трастовые шаблоны или личные «Скрипты».
  const [trustTab, setTrustTab] = useState<"assets" | "scripts">("assets");
  // Мини-переводчик: автономная модалка с textarea, дергает /api/translate
  // (тот же OpenRouter ключ, что у режима «перевод исходящего черновика»).
  const [translatorOpen, setTranslatorOpen] = useState(false);
  const [translatorInput, setTranslatorInput] = useState("");
  const [translatorResult, setTranslatorResult] = useState("");
  // Дефолт мини-переводчика = язык аккаунта; синхронизируется при первом
  // открытии модалки (см. эффект ниже), чтобы не дёргать state на каждый чат.
  const [translatorTargetLang, setTranslatorTargetLang] = useState("ru");
  const [translatorError, setTranslatorError] = useState<string | null>(null);
  const [isTranslatorBusy, setIsTranslatorBusy] = useState(false);
  const [translatorCopied, setTranslatorCopied] = useState(false);
  // Диалог закрепления в личном чате: по умолчанию закрепляем только у себя
  // (only_for_self), чекбокс включает закреп и на стороне собеседника.
  const [pinDialogMessageId, setPinDialogMessageId] = useState<string | null>(null);
  const [pinAlsoForPeer, setPinAlsoForPeer] = useState(false);
  // Модалка удаления: список id (одно из ПКМ-меню или пачка из selection-bar)
  // + чекбокс «Также удалить для <имя>» (revoke). По умолчанию включён — как
  // в оригинальном TG; в группах чекбокса нет, там всегда revoke.
  const [deleteDialogMessageIds, setDeleteDialogMessageIds] = useState<string[] | null>(null);
  const [deleteAlsoForPeer, setDeleteAlsoForPeer] = useState(true);
  // Позиция плавающей кнопки переводчика (right, top в px). Хранится в
  // localStorage, чтобы юзер один раз перетащил подальше от индикатора
  // прочтения — и больше не возвращалось. Дефолт = центр правой границы.
  const [translatorFabPos, setTranslatorFabPos] = useState<{
    right: number;
    top: number | null;
  }>(() => {
    if (typeof window === "undefined") {
      return { right: 8, top: null };
    }
    try {
      const raw = window.localStorage.getItem("tg-client:translator-fab-pos");
      if (raw) {
        const parsed = JSON.parse(raw) as { right?: number; top?: number };
        if (
          typeof parsed.right === "number" &&
          typeof parsed.top === "number" &&
          Number.isFinite(parsed.right) &&
          Number.isFinite(parsed.top)
        ) {
          return { right: parsed.right, top: parsed.top };
        }
      }
    } catch {
      // ignore corrupt storage
    }
    return { right: 8, top: null };
  });
  const translatorFabRef = useRef<HTMLButtonElement | null>(null);
  // Drag-стейт: чтобы не «прыгал» при клике, переход в drag только после
  // движения > 4px от точки нажатия.
  const translatorFabDragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startTop: number;
    moved: boolean;
  } | null>(null);
  const [internalTagsInput, setInternalTagsInput] = useState("");
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [deletedMessages, setDeletedMessages] = useState<InternalDeletedMessageRecord[]>([]);
  const [deletedError, setDeletedError] = useState<string | null>(null);
  const [isDeletedLoading, setIsDeletedLoading] = useState(false);
  // Кэш «Удалённых» по чату (переживает переключение панелей и чатов, т.к.
  // ChatView остаётся смонтированным). Раньше список полностью перезапрашивался
  // при КАЖДОМ открытии панели — открыл-закрыл-открыл = три одинаковых запроса
  // со спиннером. Теперь показываем кэш мгновенно, сеть дёргаем только если
  // данные протухли.
  const deletedCacheRef = useRef<
    Map<string, { records: InternalDeletedMessageRecord[]; fetchedAt: number }>
  >(new Map());
  const [trustAssets, setTrustAssets] = useState<InternalTrustAssetRecord[]>([]);
  // «Уже использовали в этом чате»: assetId → отметка. Живёт на пару
  // (аккаунт, чат) — при смене чата сбрасывается вместе с остальным
  // состоянием панели шаблонов.
  const [trustUsage, setTrustUsage] = useState<Record<string, InternalTrustAssetUsageRecord>>({});
  const [trustUsageBusyId, setTrustUsageBusyId] = useState<string | null>(null);
  // Разделы/подразделы шаблонов (глобальные, ведёт админ) + навигация по ним:
  // root (список разделов) → раздел (его подразделы + прямые шаблоны) →
  // подраздел (шаблоны). "unsorted" — корзина «Без раздела» для шаблонов без
  // привязки, "all" — плоский список (кнопка «Способы оплаты» ищет по всем).
  const [trustSections, setTrustSections] = useState<InternalTemplateSectionRecord[]>([]);
  const [trustNav, setTrustNav] = useState<
    | { mode: "browse"; sectionId: string | null }
    | { mode: "unsorted" }
    | { mode: "all" }
  >({ mode: "browse", sectionId: null });
  const [trustNewSectionTitle, setTrustNewSectionTitle] = useState("");
  const [isSavingTrustSection, setIsSavingTrustSection] = useState(false);
  // Подраздел, в который сохранится новый шаблон ("" = без раздела).
  const [trustCreateSectionId, setTrustCreateSectionId] = useState("");
  const [trustError, setTrustError] = useState<string | null>(null);
  const [isTrustLoading, setIsTrustLoading] = useState(false);
  const [trustTitle, setTrustTitle] = useState("");
  const [trustDescription, setTrustDescription] = useState("");
  const [trustSupportDescription, setTrustSupportDescription] = useState("");
  // Файлы нового шаблона. Несколько (только фото/видео) = альбом при
  // отправке: первый — основной content, остальные — extraContents.
  const [trustAttachments, setTrustAttachments] = useState<PendingAttachment[]>([]);
  // Флаг «Группировать» нового шаблона: true — мультифото уходит альбомом,
  // false — раздельными сообщениями.
  const [trustGroupMedia, setTrustGroupMedia] = useState(true);
  const [isSavingTrustAsset, setIsSavingTrustAsset] = useState(false);
  // ⋯-меню на шаблоне (Выбрать/Скопировать/Переместить/Редактировать/Удалить).
  const [trustMenu, setTrustMenu] = useState<{
    assetId: string;
    x: number;
    y: number;
  } | null>(null);
  // Режим выбора шаблонов для массовых операций.
  const [trustSelectMode, setTrustSelectMode] = useState(false);
  const [trustSelectedIds, setTrustSelectedIds] = useState<Set<string>>(new Set());
  // Модалка выбора раздела для перемещения/копирования.
  const [trustSectionPicker, setTrustSectionPicker] = useState<{
    mode: "move" | "copy";
    assetIds: string[];
  } | null>(null);
  const [isTrustBulkBusy, setIsTrustBulkBusy] = useState(false);
  // Модалка редактирования сохранённого шаблона.
  const [trustEdit, setTrustEdit] = useState<TrustEditState | null>(null);
  const [isSavingTrustEdit, setIsSavingTrustEdit] = useState(false);
  const trustEditFileInputRef = useRef<HTMLInputElement | null>(null);
  // Guard двойного клика по кнопке reply-keyboard бота.
  const botKeyboardSubmittingRef = useRef(false);
  const [isSubmittingAttachment, setIsSubmittingAttachment] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [replyToMessageId, setReplyToMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const editingMessageIdRef = useRef<string | null>(null);
  editingMessageIdRef.current = editingMessageId;
  // Вход в редактирование: прячем черновик, чтобы вернуть его на выходе.
  // Вложения композера при входе/выходе из правки сбрасываем: в режиме
  // правки единственное вложение — замена медиа сообщения (раунд 48), и
  // оно не должно ни уйти новым сообщением, ни остаться после отмены.
  const clearPendingAttachments = useCallback((): void => {
    setPendingAttachments((previous) => {
      previous.forEach((attachment) => revokeAttachmentPreview(attachment));
      return previous.length === 0 ? previous : [];
    });
    setAnnotatingIndex(null);
  }, []);
  const startEditingMessage = useCallback((messageId: string): void => {
    if (editingMessageIdRef.current === null) {
      draftBeforeEditRef.current = draftRef.current;
    }
    setReplyToMessageId(null);
    clearPendingAttachments();
    setEditingMessageId(messageId);
  }, [clearPendingAttachments]);
  // Выход из редактирования (отмена или сохранение) — черновик возвращается.
  const cancelEditing = useCallback((): void => {
    if (editingMessageIdRef.current === null) {
      return;
    }
    const restored = draftBeforeEditRef.current;
    draftBeforeEditRef.current = "";
    setEditingMessageId(null);
    clearPendingAttachments();
    resetDraftHistory();
    setDraft(restored);
  }, [clearPendingAttachments]);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Всплывающая дата при прокрутке (как в Telegram): пилюля с датой верхнего
  // видимого сообщения, живёт поверх ленты и гаснет через паузу после скролла.
  const [floatingDayLabel, setFloatingDayLabel] = useState<string | null>(null);
  const floatingDayVisibleRef = useRef(false);
  const [isFloatingDayVisible, setIsFloatingDayVisible] = useState(false);
  const floatingDayTimerRef = useRef<number | null>(null);
  // Множественный выбор сообщений (checkbox-режим, в стиле TG). Локальный
  // Set<string> id-шников; когда size > 0 — активен режим выбора: клик по
  // пузырю переключает выбор вместо стандартного поведения, сверху видна
  // панель действий (Копировать/Переслать в Частые группы/Удалить/Отмена).
  // Эффекты, зависящие от selectedChatId, размещены ниже после его хука.
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(
    () => new Set()
  );
  const selectionMode = selectedMessageIds.size > 0;
  const replyToMessage = useCallback((messageId: string): void => {
    // На локальные сообщения (плашки автоудаления, ещё не подтверждённые
    // отправки) отвечать нельзя: TDLib примет reply_to и упадёт при следующем
    // старте, унося весь шард. Молча игнорируем такой запрос.
    if (!isServerMessageId(messageId)) {
      return;
    }
    cancelEditing();
    setReplyToMessageId(messageId);
    composerTextareaRef.current?.focus();
  }, [cancelEditing]);
  const toggleMessageSelection = useCallback((messageId: string): void => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);
  const exitSelectionMode = useCallback((): void => {
    setSelectedMessageIds(new Set());
  }, []);

  // Escape в режиме выбора обрабатывается общей иерархией ESC ниже
  // (см. handleKey). Отдельный listener здесь был бесполезен: иерархический
  // регистрировался раньше (при выборе чата) и успевал закрыть весь чат
  // до того, как режим выбора получал шанс выйти.
  const openForwardComposition = useRealtimeStore(
    (state) => state.openForwardComposition
  );
  const closeForwardComposition = useRealtimeStore(
    (state) => state.closeForwardComposition
  );
  const setForwardHideSenderName = useRealtimeStore(
    (state) => state.setForwardHideSenderName
  );
  const setForwardHideCaption = useRealtimeStore(
    (state) => state.setForwardHideCaption
  );
  const forwardMessage = useRealtimeStore((state) => state.forwardMessage);
  const forwardComposition = useRealtimeStore((state) => state.forwardComposition);
  const setChatAutoDeleteTime = useRealtimeStore((state) => state.setChatAutoDeleteTime);
  // Источниковый чат и текст сообщения для превью форварда. Подписки
  // отдельные — на изменение исходного чата или текста UI обновится,
  // не дёргая весь ChatView ради forwardComposition reference.
  const forwardSourceChatTitle = useRealtimeStore((state) =>
    state.forwardComposition
      ? state.chats[state.forwardComposition.sourceChatId]?.title ?? "Чат"
      : ""
  );
  const forwardSourceMessageText = useRealtimeStore((state) => {
    const composition = state.forwardComposition;
    if (!composition) {
      return "";
    }
    const messages = state.messagesByChat[composition.sourceChatId] ?? EMPTY_MESSAGES;
    return messages.find((entry) => entry.id === composition.sourceMessageId)?.text ?? "";
  });
  const [forwardPreviewMenu, setForwardPreviewMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Открыта ли модалка-пикер «куда переслать». TG-поток (превью +
  // навигация + send) остаётся, плюс эта кнопка даёт привычный
  // «выпадающий список чатов».
  const [forwardPickerOpen, setForwardPickerOpen] = useState(false);
  // Модалка «Экспорт» (раунд 46): скопировать/сохранить .txt весь чат или N.
  const [exportOpen, setExportOpen] = useState(false);
  // Отложенная отправка (TG-флоу): ПКМ по «Отправить» → меню → окно
  // выбора времени → сабмит композера с расписанием из scheduleRef →
  // сразу открывается окно «Отложенные сообщения».
  const scheduleRef = useRef<SchedulePickerResult | null>(null);
  const [sendMenu, setSendMenu] = useState<{ x: number; y: number } | null>(null);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [scheduledWindowOpen, setScheduledWindowOpen] = useState(false);
  const searchOpen = activePanel === "search";
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScope, setSearchScope] = useState<ChatSearchScope>("messages");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [trustFilterKind, setTrustFilterKind] = useState<
    "all" | MediaUploadKind | "link" | "payment"
  >("all");
  const [trustFilterQuery, setTrustFilterQuery] = useState("");
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const beforeListRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const anchorMessageIdRef = useRef<string | null>(null);
  // Положение якорного сообщения относительно верха вьюпорта в момент
  // постановки якоря: после prepend старой страницы восстанавливаем именно
  // его (scrollTop += сдвиг start якоря), а не scrollToIndex по оценкам высот.
  const anchorViewportOffsetRef = useRef<number>(0);
  const programmaticScrollUntilRef = useRef<number>(0);
  const previousChatIdRef = useRef<string | null>(null);
  // id последнего сообщения на момент прошлого прогона эффекта прилипания к
  // низу — прилипаем только когда оно сменилось (пришло новое), а не на
  // каждое изменение массива сообщений.
  const lastStickMessageIdRef = useRef<string | null>(null);
  // Режим «держать низ» после открытия чата. Начальная история приезжает
  // асинхронно (кэш → пачка из 80), а высоты строк — оценки, которые
  // домеряются после рендера (плюс докачка картинок). Один scrollToIndex в
  // момент смены чата поэтому оставлял ленту в середине или «почти в конце».
  // Пока флаг равен текущему chatId, каждое изменение ленты и её геометрии
  // пере-прижимает скролл к последнему сообщению. Снимается первым жестом
  // пользователя (колесо/тач/mousedown) или прыжком к конкретному сообщению.
  const initialStickChatIdRef = useRef<string | null>(null);
  // Всегда содержит актуальный выбранный чат — для отсева ответов async-запросов
  // (удалённые/переводы), пришедших после ухода пользователя в другой чат.
  const selectedChatIdRef = useRef<string | null>(null);
  // Разделитель «Непрочитанные сообщения»: baseline снимается при входе в чат
  // (markRead быстро сдвигает lastReadInboxMessageId, поэтому снапшот),
  // messageId вычисляется один раз после загрузки сообщений ("" = не нужен).
  const unreadDividerRef = useRef<{
    chatId: string;
    baseline: string | undefined;
    messageId: string | null;
  } | null>(null);
  const lastMarkedReadMessageIdByChatRef = useRef<Record<string, string>>({});
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingMimeTypeRef = useRef<string>("");
  const trustFileInputRef = useRef<HTMLInputElement | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const pending = pendingDraftSelectionRef.current;
    const el = composerTextareaRef.current;
    if (!pending || !el) return;
    pendingDraftSelectionRef.current = null;
    try {
      el.setSelectionRange(pending.start, pending.end);
    } catch {
      /* noop */
    }
  }, [draft]);
  // Auto-resize textarea: считаем scrollHeight и растягиваем до max-height,
  // дальше — внутренний скролл. Единственный механизм роста поля во всех
  // браузерах: CSS `field-sizing: content` намеренно НЕ используется — в
  // Chrome оно тянет ещё и ширину, и поле уезжает за правый край экрана
  // (см. комментарий у .composer__textarea в styles.css).
  useEffect(() => {
    const el = composerTextareaRef.current;
    if (!el) return;
    // Сброс на auto, чтобы scrollHeight пересчитался под текущий контент,
    // затем клампим в [MIN, MAX]. Пишем прямой inline-стилем — CSS max/min
    // остаются как safety-net.
    el.style.height = "auto";
    const next = Math.max(52, Math.min(220, el.scrollHeight));
    el.style.height = `${next}px`;
  }, [draft]);
  // dialogLists больше не нужен: единственным потребителем была строка
  // «В папке: …» под лентой, её убрали (папки — в списке диалогов и профиле).
  const stickerSets = useRealtimeStore(useShallow((state) => state.stickerSets));
  const stickerSetsLoading = useRealtimeStore((state) => state.stickerSetsLoading);
  const loadStickerSets = useRealtimeStore((state) => state.loadStickerSets);
  const accountId = useRealtimeStore((state) => state.accountId);
  // «Частые группы» (раунд 46, бывший Cash-чат): адресаты быстрой
  // пересылки одним кликом. Берём из серверного стора, а не из localStorage:
  // для саппорта это whitelist адресатов пересылки, и локальное значение
  // гейтвей всё равно не примет (см. guard на chat.forwardMessage).
  // Заголовок — из state.chats, если чат уже в стейте, иначе сохранённый.
  const frequentChats = useRealtimeStore(
    useShallow((state) =>
      accountId ? state.frequentChatsByAccount[accountId] ?? EMPTY_FREQUENT_CHATS : EMPTY_FREQUENT_CHATS
    )
  );
  const frequentTitlesById = useRealtimeStore(
    useShallow((state) =>
      Object.fromEntries(
        frequentChats.map((entry) => [entry.chatId, state.chats[entry.chatId]?.title ?? null])
      )
    )
  );
  const frequentChatTitle = (entry: { chatId: string; title: string }): string =>
    frequentTitlesById[entry.chatId] ??
    (entry.title.length > 0 ? entry.title : `Чат ${entry.chatId}`);
  // Поповер выбора частой группы: откуда открыт (координаты) и какие
  // сообщения пересылать. Клик по группе шлёт сразу (быстрый путь, как
  // раньше «В Cash»).
  const [frequentMenu, setFrequentMenu] = useState<{
    x: number;
    y: number;
    messageIds: string[];
  } | null>(null);
  // Масштаб интерфейса (`zoom` на <html>): нужен везде, где координаты мыши /
  // window.innerWidth (визуальные px) кладутся в style.top/left (логические).
  const uiScale = useUiPrefs((state) => (state.uiScale > 0 ? state.uiScale : 1));
  // Часовой пояс саппорта: время сообщений, разделители суток и отложенная
  // отправка считаются в нём. Смена пояса перерисовывает ленту.
  const timeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  // Имя нашего аккаунта для строк копирования/экспорта («[дата] Имя: текст»).
  const selfExportName = useRealtimeStore((state) => selfDisplayName(state.selfIdentity));
  const selectedChatId = useRealtimeStore((state) => state.selectedChatId);
  const wsUrl = useRealtimeStore((state) => state.wsUrl);
  const selectedChat = useRealtimeStore((state) =>
    state.selectedChatId ? (state.chats[state.selectedChatId] ?? null) : null
  );
  const activeDraft = useRealtimeStore((state) =>
    state.selectedChatId ? (state.draftsByChat[state.selectedChatId] ?? "") : ""
  );
  const messages = useRealtimeStore((state) =>
    state.selectedChatId ? (state.messagesByChat[state.selectedChatId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES
  );
  // Ctrl/Cmd+C в режиме выбора — то же, что кнопка «Копировать» в панели:
  // строки «[дата время] Имя: текст», как копирует оригинальный Telegram
  // (lib/message-export.ts). Не перехватываем, если
  // фокус в поле ввода или пользователь выделил текст мышью — там должно
  // работать нативное копирование. event.code — чтобы хоткей работал и на
  // русской раскладке (event.key там «с»).
  useEffect(() => {
    if (!selectedChatId || selectedMessageIds.size === 0) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) {
        return;
      }
      if (event.code !== "KeyC" && event.key.toLowerCase() !== "c") {
        return;
      }
      // Клик по ленте переводит фокус в композер (onMouseUp у .chat-view__body),
      // поэтому после выделения сообщений фокус почти всегда в textarea.
      // Уступаем нативному копированию только когда в поле реально выделен
      // текст — иначе Ctrl+C «не копировал ничего» (жалоба раунда 46).
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        const field = target as HTMLInputElement | HTMLTextAreaElement;
        if ((field.selectionStart ?? 0) !== (field.selectionEnd ?? 0)) {
          return;
        }
      } else if (window.getSelection()?.toString().length) {
        return;
      }
      const text = formatMessageLines(
        messages.filter((message) => selectedMessageIds.has(message.id)),
        { selfName: selfExportName, timeZone }
      );
      if (text.length === 0) {
        return;
      }
      event.preventDefault();
      void copyTextToClipboard(text);
      exitSelectionMode();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedChatId, selectedMessageIds, messages, exitSelectionMode, selfExportName, timeZone]);
  const pinnedMessage = useRealtimeStore((state) =>
    state.selectedChatId ? (state.pinnedMessageByChat[state.selectedChatId] ?? null) : null
  );
  const searchState = useRealtimeStore((state) =>
    state.selectedChatId ? state.searchStateByChat[state.selectedChatId] : undefined
  );
  const isHistoryLoading = useRealtimeStore((state) =>
    state.selectedChatId ? (state.historyLoadingByChat[state.selectedChatId] ?? false) : false
  );
  // Watchdog внутри store сбрасывает historyLoading через 60с (было 20с) даже
  // без ответа TDLib — для больших групп типа Cash это может случиться раньше
  // подтверждённо-пустого/непустого ответа. Empty-state рисуем только по
  // historyLoadedByChat, иначе саппорт видит «Сообщений пока нет» на чате,
  // где история ещё едет.
  const isHistoryLoaded = useRealtimeStore((state) =>
    state.selectedChatId ? (state.historyLoadedByChat[state.selectedChatId] ?? false) : false
  );
  const reachedStart = useRealtimeStore((state) =>
    state.selectedChatId ? (state.historyReachedStartByChat[state.selectedChatId] ?? false) : false
  );
  const setStoredDraft = useRealtimeStore((state) => state.setDraft);
  const clearStoredDraft = useRealtimeStore((state) => state.clearDraft);
  const sendMessage = useRealtimeStore((state) => state.sendMessage);
  const sendAlbum = useRealtimeStore((state) => state.sendAlbum);
  const quickReplies = useRealtimeStore((state) => state.quickReplies);
  const listQuickReplies = useRealtimeStore((state) => state.listQuickReplies);
  const sendQuickReply = useRealtimeStore((state) => state.sendQuickReply);
  // Быстрые ответы TG Business: дропдаун появляется, когда черновик начинается
  // с «/». Список запрашиваем лениво — при первом «/» за сессию.
  const quickRepliesRequestedRef = useRef(false);
  // При смене чата очищаем множественное выделение — id-шники теряют
  // контекст, а действия относятся к сообщениям конкретного чата.
  useEffect(() => {
    setSelectedMessageIds(new Set());
    setExportOpen(false);
  }, [selectedChatId]);
  const botReplyKeyboard = useRealtimeStore((state) =>
    state.selectedChatId ? state.botReplyKeyboardByChat[state.selectedChatId] ?? null : null
  );
  const clearBotReplyKeyboard = useRealtimeStore((state) => state.clearBotReplyKeyboard);
  const editMessage = useRealtimeStore((state) => state.editMessage);
  const deleteMessage = useRealtimeStore((state) => state.deleteMessage);
  const toggleMessagePinned = useRealtimeStore((state) => state.toggleMessagePinned);
  const setChatInternalTags = useRealtimeStore((state) => state.setChatInternalTags);
  const setTyping = useRealtimeStore((state) => state.setTyping);
  const loadOlderHistory = useRealtimeStore((state) => state.loadOlderHistory);
  const markRead = useRealtimeStore((state) => state.markRead);
  const pageActive = useRealtimeStore((state) => state.pageActive);
  const searchMessages = useRealtimeStore((state) => state.searchMessages);
  const clearSearch = useRealtimeStore((state) => state.clearSearch);
  // Единая точка переключения панелей: повторный клик закрывает панель,
  // при уходе с поиска подчищаем его состояние в сторе.
  const switchPanel = (
    next: "search" | "tags" | "deleted" | "trust" | null
  ): void => {
    const resolved = activePanel === next ? null : next;
    if (activePanel === "search" && resolved !== "search" && selectedChatId) {
      clearSearch(selectedChatId);
    }
    setActivePanel(resolved);
  };

  // Ctrl/Cmd+F — поиск по текущему чату, как в оригинальном ТГ (вместо
  // браузерного поиска по странице). Повторное нажатие — фокус в поле.
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        !event.altKey &&
        // event.code — чтобы хоткей работал и в русской раскладке
        // (event.key там «а»).
        (event.code === "KeyF" || event.key.toLowerCase() === "f")
      ) {
        event.preventDefault();
        setActivePanel("search");
        window.requestAnimationFrame(() => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedChatId]);

  // Открытие поиска из кнопки 🔍 в шапке (мост через ui-prefs.chatSearchNonce).
  const chatSearchNonce = useUiPrefs((state) => state.chatSearchNonce);
  useEffect(() => {
    if (chatSearchNonce === 0 || !selectedChatId) {
      return;
    }
    setActivePanel("search");
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
  }, [chatSearchNonce, selectedChatId]);

  // Открытие управления шаблонами (админ-редактор) из правой панели редизайна.
  // Открываем КОРЕНЬ дерева разделов, а не плоский «Все шаблоны»: форма
  // «Создать раздел / подраздел» живёт только в режиме browse, и с 23.09
  // (мост открывал mode: "all") админы её не видели (запрос №26). Плоский
  // список остаётся доступен плиткой «Все шаблоны» в корне.
  const trustManageNonce = useUiPrefs((state) => state.trustManageNonce);
  useEffect(() => {
    if (trustManageNonce === 0 || !selectedChatId) {
      return;
    }
    setTrustNav({ mode: "browse", sectionId: null });
    setActivePanel("trust");
  }, [trustManageNonce, selectedChatId]);


  // Поиск по мере ввода (без кнопки «Найти»): дебаунс 250 мс, от 2 символов.
  // Кнопка/Enter остаются как немедленный запуск (одинаковый запрос в полёте
  // стор не дублирует).
  const searchOpenForEffect = activePanel === "search";
  useEffect(() => {
    if (!searchOpenForEffect || !selectedChatId) {
      return;
    }
    const query = searchQuery.trim();
    if (query.length === 0) {
      clearSearch(selectedChatId);
      return;
    }
    if (query.length < 2) {
      return;
    }
    const timer = window.setTimeout(() => {
      searchMessages(selectedChatId, query, searchScope, 30);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchQuery, searchScope, searchOpenForEffect, selectedChatId]);

  useEffect(() => {
    if (searchOpenForEffect) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [searchOpenForEffect]);
  const translateMessage = useRealtimeStore((state) => state.translateMessage);
  const transcribeVoice = useRealtimeStore((state) => state.transcribeVoice);
  const loadScheduledMessages = useRealtimeStore((state) => state.loadScheduledMessages);
  const scheduledMessages = useRealtimeStore((state) =>
    state.selectedChatId ? state.scheduledByChat[state.selectedChatId] ?? EMPTY_SCHEDULED : EMPTY_SCHEDULED
  );
  const beginOptimisticSend = useRealtimeStore((state) => state.beginOptimisticSend);
  const cancelOptimisticSend = useRealtimeStore((state) => state.cancelOptimisticSend);
  const clearMessageTranslation = useRealtimeStore((state) => state.clearMessageTranslation);
  // MessageRow знает только messageId — chatId подставляем из выбранного чата.
  const clearTranslationForSelectedChat = useCallback(
    (messageId: string): void => {
      const chatId = useRealtimeStore.getState().selectedChatId;
      if (chatId) {
        clearMessageTranslation(chatId, messageId);
      }
    },
    [clearMessageTranslation]
  );
  const setChatTranslation = useRealtimeStore((state) => state.setChatTranslation);
  const retranslateMissing = useRealtimeStore((state) => state.retranslateMissing);
  const rememberOutgoingOriginal = useRealtimeStore(
    (state) => state.rememberOutgoingOriginal
  );
  const isChatTranslated = useRealtimeStore((state) =>
    state.selectedChatId ? Boolean(state.translatedChats[state.selectedChatId]) : false
  );
  const toggleReaction = useRealtimeStore((state) => state.toggleReaction);
  const pressInlineButton = useRealtimeStore((state) => state.pressInlineButton);
  const listForumTopics = useRealtimeStore((state) => state.listForumTopics);
  const setActiveForumTopic = useRealtimeStore((state) => state.setActiveForumTopic);
  const isSupportRole = useRealtimeStore((state) => state.appRole === "support");
  // Общий «Переслать» открывает пикер с ВСЕМИ чатами активной папки и с
  // 2026-09-23 доступен всем ролям для любых сообщений (гейтвей пишет аудит).
  const isAdminRole = useRealtimeStore(
    (state) => state.appRole === "admin" || state.appRole === "geo_admin"
  );
  const forumTopics = useRealtimeStore((state) =>
    state.selectedChatId ? (state.forumTopicsByChat[state.selectedChatId] ?? null) : null
  );
  const activeForumTopic = useRealtimeStore((state) =>
    state.selectedChatId ? (state.activeForumTopicByChat[state.selectedChatId] ?? null) : null
  );
  const [reactionPickerForMessage, setReactionPickerForMessage] = useState<string | null>(null);
  // ПКМ-меню действий с сообщением: по hover-кнопкам было невозможно
  // попасть (зазор между пузырём и меню), действия переехали сюда.
  const [messageContextMenu, setMessageContextMenu] = useState<{
    messageId: string;
    x: number;
    y: number;
  } | null>(null);
  // Модалка «Отключить автоудаление» — открывается по ЛКМ на сервисном
  // сообщении «Включено автоудаление сообщений через …». Единственное
  // действие — «Никогда» (setChatAutoDeleteTime(chatId, 0)).
  const [autoDeleteEditorOpen, setAutoDeleteEditorOpen] = useState(false);
  // Пред-отправочный редактор шаблона (Feat 6): открывается по кнопке
  // «Изменить» рядом с «Отправить». Для текстовых шаблонов правится сам
  // текст; для медиа — caption. Send отсылает asset с изменённым текстом.
  const [templateEditor, setTemplateEditor] = useState<{
    asset: InternalTrustAssetRecord;
    text: string;
  } | null>(null);
  // Единый «целевой язык» per chat живёт в store: используется и кнопкой
  // «Перевести весь чат», и глобусом перевода исходящего черновика. Раньше
  // тут был отдельный локальный state, а кнопка «Перевести весь чат» молча
  // хардкодила "ru" — выбор языка в select ни на что не влиял.
  // Язык по умолчанию аккаунта — из настроек переводчика (админ может
  // выставить, например, EN на конкретный аккаунт вместо общего RU).
  const accountDefaultTranslationLang = useRealtimeStore((state) =>
    resolveAccountDefaultTranslationLanguage(state)
  );
  const chatTranslationLang = useRealtimeStore((state) =>
    state.selectedChatId
      ? (state.translationLanguageByChat[state.selectedChatId] ??
        resolveAccountDefaultTranslationLanguage(state))
      : resolveAccountDefaultTranslationLanguage(state)
  );
  const setChatTranslationLanguage = useRealtimeStore(
    (state) => state.setChatTranslationLanguage
  );
  // Язык, НА который переводится исходящий черновик (кнопка «Перевести» в
  // композере). Отдельный от языка чтения: читаем входящие по-русски, а
  // клиенту пишем на его языке.
  const outgoingLang = useRealtimeStore((state) =>
    resolveOutgoingLanguage(state, state.selectedChatId)
  );
  const setOutgoingLanguage = useRealtimeStore((state) => state.setOutgoingLanguage);
  // Языки для селекторов: серверный список, отфильтрованный привязкой
  // аккаунта (если админ ограничил набор). Пустой конфиг → fallback.
  const translateLanguages = useRealtimeStore(
    useShallow((state) => {
      const all =
        state.translatorLanguages.length > 0
          ? state.translatorLanguages
          : FALLBACK_TRANSLATE_LANGUAGES;
      const binding = state.accountId ? state.translatorBindings[state.accountId] : undefined;
      const allowed =
        binding?.languages && binding.languages.length > 0
          ? all.filter((language) => binding.languages!.includes(language.code))
          : all;
      return allowed.length > 0 ? allowed : all;
    })
  );
  // Смена аккаунта (или прилетевший конфиг) обновляет дефолт мини-переводчика.
  useEffect(() => {
    setTranslatorTargetLang(accountDefaultTranslationLang);
  }, [accountDefaultTranslationLang]);
  const [isTranslatingOutgoing, setIsTranslatingOutgoing] = useState(false);
  const [outgoingTranslationError, setOutgoingTranslationError] = useState<string | null>(null);
  // Последний перевод черновика: пока поле дословно равно переводу, рядом
  // висит кнопка «Оригинал» (вернуть русский текст). Как только саппорт
  // правит поле руками — кнопка исчезает, чтобы она не подставила чужой
  // текст в уже новое сообщение.
  const [outgoingTranslated, setOutgoingTranslated] = useState<{
    original: string;
    translated: string;
    // Обратный перевод на язык саппорта — «что прочитает клиент». Приходит
    // со сверкой; null, если сверка не удалась.
    back: string | null;
    // true — сверка нашла расхождение и подставила исправленный перевод.
    corrected: boolean;
  } | null>(null);
  const translationsByMessage = useRealtimeStore(
    useShallow((state) => state.translationsByMessage)
  );
  const chatTranslationProgress = useMemo(() => {
    const translatable = messages.filter(
      (message) =>
        !message.outgoing &&
        !message.isDeleted &&
        // Тот же критерий, что и в очереди перевода (store): подписи к медиа
        // и расшифровки голосовых считаются, лейблы превью — нет.
        translationSourceText(message.content).length > 0
    );
    const total = translatable.length;
    const done = translatable.filter((message) =>
      Boolean(
        selectedChatId && translationsByMessage[translationKey(selectedChatId, message.id)]
      )
    ).length;
    return { total, done, complete: total > 0 && done === total };
  }, [messages, translationsByMessage, selectedChatId]);
  // forwardTargets / forwardTarget вычисления переехали в ForwardPickerModal —
  // ChatView больше не подписывается на state.chats и chatIdsByList ради
  // forward, что устраняет лишние ре-рендеры этого огромного компонента.
  const normalizedTrustQuery = trustFilterQuery.trim().toLowerCase();
  const trustSectionIds = useMemo(
    () => new Set(trustSections.map((section) => section.sectionId)),
    [trustSections]
  );
  // Если открытый раздел удалили (другой админ / рефреш) — падаем на корень,
  // а не показываем пустую «дыру».
  const trustNavResolved = useMemo(
    () =>
      trustNav.mode === "browse" &&
      trustNav.sectionId !== null &&
      !trustSectionIds.has(trustNav.sectionId)
        ? ({ mode: "browse", sectionId: null } as const)
        : trustNav,
    [trustNav, trustSectionIds]
  );
  // Пока админ не создал ни одного раздела — работаем по-старому, плоским
  // списком, без промежуточной навигации.
  const trustHasSections = trustSections.length > 0;
  const trustRootSections = useMemo(
    () => trustSections.filter((section) => section.parentId === null),
    [trustSections]
  );
  // Кол-во шаблонов по секциям; шаблоны с битой/отсутствующей привязкой
  // считаются «Без раздела».
  const trustSectionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    let orphans = 0;
    for (const asset of trustAssets) {
      if (asset.sectionId && trustSectionIds.has(asset.sectionId)) {
        counts.set(asset.sectionId, (counts.get(asset.sectionId) ?? 0) + 1);
      } else {
        orphans += 1;
      }
    }
    return { counts, orphans };
  }, [trustAssets, trustSectionIds]);
  const countTrustSectionDeep = useCallback(
    (sectionId: string): number =>
      (trustSectionCounts.counts.get(sectionId) ?? 0) +
      trustSections
        .filter((section) => section.parentId === sectionId)
        .reduce(
          (sum, child) => sum + (trustSectionCounts.counts.get(child.sectionId) ?? 0),
          0
        ),
    [trustSectionCounts, trustSections]
  );
  // Показываем ли список шаблонов на текущем уровне навигации (на корне при
  // наличии разделов видны только плитки разделов).
  const trustAssetsVisible =
    !trustHasSections ||
    trustNavResolved.mode === "all" ||
    trustNavResolved.mode === "unsorted" ||
    (trustNavResolved.mode === "browse" && trustNavResolved.sectionId !== null);
  const filteredTrustAssets = useMemo(
    () =>
      trustAssets.filter((asset) => {
        if (trustHasSections) {
          if (trustNavResolved.mode === "unsorted") {
            if (asset.sectionId && trustSectionIds.has(asset.sectionId)) {
              return false;
            }
          } else if (trustNavResolved.mode === "browse") {
            if (trustNavResolved.sectionId === null) {
              return false;
            }
            if (asset.sectionId !== trustNavResolved.sectionId) {
              return false;
            }
          }
          // mode === "all": секции не фильтруем.
        }

        if (trustFilterKind !== "all" && asset.mediaKind !== trustFilterKind) {
          return false;
        }

        if (normalizedTrustQuery.length === 0) {
          return true;
        }

        const haystack = `${asset.title} ${asset.description ?? ""} ${
          asset.supportDescription ?? ""
        }`.toLowerCase();
        return haystack.includes(normalizedTrustQuery);
      }),
    [
      normalizedTrustQuery,
      trustAssets,
      trustFilterKind,
      trustHasSections,
      trustNavResolved,
      trustSectionIds
    ]
  );

  const selectedChatTagsKey = selectedChat?.internalTags.join(", ") ?? "";
  const hasTypingContent = draft.trim().length > 0;
  const replyTarget = useMemo(
    () => messages.find((message) => message.id === replyToMessageId) ?? null,
    [messages, replyToMessageId]
  );
  const editingTarget = useMemo(
    () => messages.find((message) => message.id === editingMessageId) ?? null,
    [editingMessageId, messages]
  );
  // Для колбэков без пересоздания (attachFiles): что именно правим.
  const editingTargetRef = useRef<MessageViewModel | null>(null);
  editingTargetRef.current = editingTarget;
  // Ленту откладываем ВМЕСТЕ с её chatId. useDeferredValue отдаёт предыдущее
  // значение, пока идёт переход, а selectedChatId меняется сразу — на голом
  // useDeferredValue(messages) только что открытый чат какое-то время (на
  // загруженной вкладке — заметное) рисует ленту предыдущего. Если снимок
  // отстал от выбранного чата, берём актуальный: показать чужие сообщения
  // хуже, чем один лишний синхронный рендер.
  const feedSnapshot = useMemo(
    () => ({ chatId: selectedChatId, messages }),
    [selectedChatId, messages]
  );
  const deferredFeed = useDeferredValue(feedSnapshot);
  const feed = deferredFeed.chatId === selectedChatId ? deferredFeed : feedSnapshot;
  const deferredMessages = feed.messages;
  // Если выбрана тема форума — отфильтровываем сообщения из других тем.
  // Сообщения без messageThreadId (служебные) показываем всегда — это создание темы и т.п.
  // Просмотрщик медиа из ленты: собираем все фото/видео текущего окна
  // истории, чтобы листать ←/→. Через ref — колбэк стабилен, MessageRow
  // (memo) не перерисовывается на каждое событие.
  const visibleMessagesRef = useRef<MessageViewModel[]>([]);
  const openFeedMedia = useCallback<MediaRenderContext["onOpenMedia"]>((payload) => {
    const chatId = useRealtimeStore.getState().selectedChatId;
    const items: MediaViewerItem[] = [];
    let index = -1;
    for (const message of visibleMessagesRef.current) {
      if (message.isDeleted) continue;
      const content = message.content;
      if (content.kind !== "photo" && content.kind !== "video") continue;
      const url = content.url ?? (content.kind === "photo" ? content.thumbnailUrl : undefined);
      if (!url) continue;
      if (content.kind === "video" && !content.url) continue;
      if (url === payload.url) {
        index = items.length;
      }
      items.push({
        kind: content.kind,
        url,
        ...(content.thumbnailUrl ? { thumbnailUrl: content.thumbnailUrl } : {}),
        ...(content.kind === "video" && content.mimeType ? { mimeType: content.mimeType } : {}),
        ...(content.caption ? { caption: content.caption } : {}),
        senderLabel: message.outgoing ? "Вы" : message.senderName ?? "Собеседник",
        occurredAt: message.occurredAt,
        ...(chatId ? { chatId, messageId: message.id } : {})
      });
    }
    if (index < 0) {
      openMediaViewer(
        [
          {
            kind: payload.kind,
            url: payload.url,
            ...(payload.mimeType ? { mimeType: payload.mimeType } : {}),
            ...(payload.caption ? { caption: payload.caption } : {}),
            senderLabel: payload.senderLabel
          }
        ],
        0
      );
      return;
    }
    openMediaViewer(items, index);
  }, []);

  const visibleMessages = useMemo(() => {
    const base = activeForumTopic
      ? deferredMessages.filter(
          (message) =>
            !message.messageThreadId || message.messageThreadId === activeForumTopic
        )
      : deferredMessages;
    // Страховка от одинаковых messageId в одном срезе ленты: раньше редкий
    // дубль (гонка reducer'а между sendSucceeded и messageUpserted) мог
    // оставить две строки с одним id — виртуализатор выставлял им один
    // ключ, React переиспользовал ноду, и в ленте появлялся «съезд»
    // (одна на другой) + двойной разделитель «Непрочитанные». Оставляем
    // первое вхождение — оно уже прошло весь mount+measure.
    const seen = new Set<string>();
    const deduped: MessageViewModel[] = [];
    let hasDuplicates = false;
    for (const message of base) {
      if (seen.has(message.id)) {
        hasDuplicates = true;
        continue;
      }
      seen.add(message.id);
      deduped.push(message);
    }
    if (hasDuplicates && typeof console !== "undefined") {
      // Дубль — сигнал регрессии в store: reducer где-то не заменил, а
      // добавил. Логируем один раз на кадр, без спама.
      console.warn("chat-view: dropped duplicate message ids in visible feed");
    }
    return deduped;
  }, [activeForumTopic, deferredMessages]);
  visibleMessagesRef.current = visibleMessages;

  // Альбомы: первое сообщение серии с одним mediaAlbumId получает список
  // всех элементов, остальные помечаются скрытыми. Серия рвётся на другом
  // отправителе, смене суток и удалённом сообщении — иначе сетка «потечёт»
  // через разделитель дня.
  const albumLayout = useMemo(() => {
    const itemsByStartId = new Map<string, MessageViewModel[]>();
    const hiddenIds = new Set<string>();
    let index = 0;
    while (index < visibleMessages.length) {
      const first = visibleMessages[index]!;
      const albumId = first.mediaAlbumId;
      if (!albumId || first.isDeleted || (first.content.kind !== "photo" && first.content.kind !== "video")) {
        index += 1;
        continue;
      }
      const firstDay = dayKey(first.occurredAt, timeZone);
      const items: MessageViewModel[] = [first];
      let cursor = index + 1;
      while (cursor < visibleMessages.length) {
        const candidate = visibleMessages[cursor]!;
        if (
          candidate.mediaAlbumId !== albumId ||
          candidate.isDeleted ||
          candidate.outgoing !== first.outgoing ||
          candidate.senderId !== first.senderId ||
          (candidate.content.kind !== "photo" && candidate.content.kind !== "video") ||
          dayKey(candidate.occurredAt, timeZone) !== firstDay
        ) {
          break;
        }
        items.push(candidate);
        cursor += 1;
      }
      if (items.length > 1) {
        itemsByStartId.set(first.id, items);
        for (const item of items.slice(1)) {
          hiddenIds.add(item.id);
        }
      }
      index = cursor > index + 1 ? cursor : index + 1;
    }
    return { itemsByStartId, hiddenIds };
  }, [visibleMessages, timeZone]);

  // Снапшот baseline при смене чата и одноразовое вычисление позиции
  // разделителя непрочитанных (идемпотентно — безопасно в рендере).
  if (unreadDividerRef.current?.chatId !== selectedChatId) {
    unreadDividerRef.current = selectedChatId
      ? {
          chatId: selectedChatId,
          baseline: selectedChat?.lastReadInboxMessageId,
          messageId: null
        }
      : null;
  }
  const unreadDivider = unreadDividerRef.current;
  if (unreadDivider && unreadDivider.messageId === null && visibleMessages.length > 0) {
    const { baseline } = unreadDivider;
    if (!baseline) {
      unreadDivider.messageId = "";
    } else {
      const firstUnread = visibleMessages.find(
        (message) => !message.outgoing && isMessageIdAfter(message.id, baseline)
      );
      unreadDivider.messageId = firstUnread?.id ?? "";
    }
  }
  // Индекс единственного сообщения, у которого показывается разделитель
  // «Непрочитанные сообщения». Считаем один раз на кадр — раньше в каждом
  // MessageRow сравнивали messageId, и при повторах id (гонки при быстрой
  // смене чатов, дубли, оставленные reducer'ом) разделитель мог возникнуть
  // сразу под несколькими сообщениями. Отсутствие или пустой id → -1.
  const unreadDividerIndex =
    unreadDivider && unreadDivider.chatId === selectedChatId && unreadDivider.messageId
      ? visibleMessages.findIndex((message) => message.id === unreadDivider.messageId)
      : -1;

  const [messageScrollMargin, setMessageScrollMargin] = useState(0);
  useEffect(() => {
    const before = beforeListRef.current;
    if (!before || typeof ResizeObserver === "undefined") {
      return;
    }
    const updateMargin = (): void => {
      setMessageScrollMargin(before.offsetHeight);
    };
    updateMargin();
    // Колбэк наблюдателя сам меняет геометрию (высота отступа → перекладка
    // ленты), из-за чего браузер сыпал «ResizeObserver loop completed with
    // undelivered notifications» — в журнале это выглядело как падение
    // вкладки. Переносим реакцию на следующий кадр: замер завершается, петля
    // не возникает.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateMargin();
      });
    });
    observer.observe(before);
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      observer.disconnect();
    };
  }, [selectedChatId, isHistoryLoading, reachedStart, visibleMessages.length]);

  const messageVirtualizer = useVirtualizer({
    count: visibleMessages.length,
    getScrollElement: () => bodyRef.current,
    // Оценка высоты строки ДО замера. Константа 64 (обычное текстовое
    // сообщение) сильно занижала высокие сообщения ботов с inline-клавиатурой:
    // при быстром скролле такая строка успевала отрисоваться в отведённом ей
    // куске в 64px и наезжала на соседние, пока не приезжал реальный замер.
    // Считаем по содержимому — промах становится десятками пикселей вместо
    // сотен, и наложение не успевает стать видимым.
    estimateSize: (index) => {
      const message = visibleMessages[index];
      if (message && albumLayout.hiddenIds.has(message.id)) {
        return 0;
      }
      const items = message ? albumLayout.itemsByStartId.get(message.id) : undefined;
      if (items) {
        // Сетка 2 колонки, плитки ~190px: ряды + подпись/футер.
        return Math.ceil(items.length / 2) * 194 + 60;
      }
      return estimateMessageHeight(message);
    },
    overscan: 12,
    // Ключ строки обязан включать chatId. TDLib нумерует сообщения ВНУТРИ
    // чата (id = server_id << 20), поэтому у разных диалогов id совпадают
    // массово — у первых сообщений почти всегда. Виртуализатор держит
    // itemSizeCache: Map<itemKey, высота> на весь срок жизни ChatView (а он
    // memo и при смене чата не размонтируется), так что по голому message.id
    // сообщение нового чата получало ИЗМЕРЕННУЮ высоту одноимённого
    // сообщения предыдущего: фото на 400px под однострочный текст и наоборот
    // — строки наезжают друг на друга. Тот же ключ идёт в React key, и по
    // совпавшему ключу React переиспользовал DOM-узел строки старого чата.
    getItemKey: (index) => {
      const message = visibleMessages[index];
      return message ? `${feed.chatId ?? ""}:${message.id}` : index;
    },
    scrollMargin: messageScrollMargin
  });

  const stopRecording = (): void => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      if (recorder.state === "recording") {
        try {
          recorder.requestData();
        } catch {
          // Some browsers throw if there is no buffered chunk yet.
        }
      }
      recorder.stop();
      return;
    }

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;
    mediaChunksRef.current = [];
    recordingStartedAtRef.current = null;
    recordingMimeTypeRef.current = "";
    setIsRecordingVoice(false);
    setRecordingElapsedSeconds(0);
  };

  const resetTrustAttachments = (): void => {
    trustAttachments.forEach((attachment) => revokeAttachmentPreview(attachment));
    setTrustAttachments([]);
  };

  // TTL кэша удалённых: показываем сохранённое сразу, рефрешим в фоне только
  // если старше этого порога (или force — при ручном «Обновить» / после
  // удаления сообщения в этом чате).
  const DELETED_CACHE_TTL_MS = 20_000;
  const loadDeletedMessages = useCallback(
    async (options?: { force?: boolean }) => {
      if (!accountId || !selectedChatId) {
        setDeletedMessages([]);
        return;
      }
      const chatId = selectedChatId;
      const cached = deletedCacheRef.current.get(chatId);
      if (cached) {
        // Мгновенно показываем кэш — без спиннера и без мигания «пусто».
        setDeletedMessages(cached.records);
      }
      const fresh = cached && Date.now() - cached.fetchedAt < DELETED_CACHE_TTL_MS;
      if (fresh && !options?.force) {
        return;
      }

      setIsDeletedLoading(true);
      setDeletedError(null);
      try {
        const response = await internalApi.listDeletedMessages(accountId, chatId, wsUrl, 100);
        deletedCacheRef.current.set(chatId, {
          records: response.messages,
          fetchedAt: Date.now()
        });
        // Не перетираем список, если пользователь уже ушёл в другой чат.
        if (selectedChatIdRef.current === chatId) {
          setDeletedMessages(response.messages);
        }
      } catch (cause) {
        if (selectedChatIdRef.current === chatId) {
          setDeletedError(
            cause instanceof Error ? cause.message : "Не удалось загрузить удалённые сообщения."
          );
        }
      } finally {
        if (selectedChatIdRef.current === chatId) {
          setIsDeletedLoading(false);
        }
      }
    },
    [accountId, selectedChatId, wsUrl]
  );

  const loadTrustAssets = useCallback(async () => {
    if (!accountId) {
      setTrustAssets([]);
      return;
    }

    setIsTrustLoading(true);
    setTrustError(null);
    try {
      const [assetsResponse, sectionsResponse] = await Promise.all([
        internalApi.listTrustAssets(accountId, wsUrl),
        internalApi.listTemplateSections(accountId, wsUrl)
      ]);
      setTrustAssets(assetsResponse.assets);
      setTrustSections(sectionsResponse.sections);
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось загрузить шаблоны."
      );
    } finally {
      setIsTrustLoading(false);
    }
  }, [accountId, wsUrl]);

  useEffect(
    () => () => {
      pendingAttachments.forEach((attachment) => revokeAttachmentPreview(attachment));
    },
    [pendingAttachments]
  );

  useEffect(() => {
    if (!isRecordingVoice) {
      setRecordingElapsedSeconds(0);
      return;
    }

    const update = (): void => {
      const startedAt = recordingStartedAtRef.current;
      setRecordingElapsedSeconds(
        startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0
      );
    };

    update();
    const intervalId = window.setInterval(update, 250);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isRecordingVoice]);

  useEffect(() => {
    const nextDraft = selectedChatId ? activeDraft : "";
    selectedChatIdRef.current = selectedChatId;
    setComposerError(null);
    setIsSubmittingAttachment(false);
    setActivePanel(null);
    setMessageContextMenu(null);
    // Показываем кэш удалённых нового чата (или пусто), а не мигаем пустотой —
    // фактический рефреш случится при открытии панели, если данные протухли.
    setDeletedMessages(
      selectedChatId ? deletedCacheRef.current.get(selectedChatId)?.records ?? [] : []
    );
    setDeletedError(null);
    setTagsError(null);
    setTrustError(null);
    setTrustAssets([]);
    setTrustUsage({});
    setTrustTitle("");
    setTrustDescription("");
    setTrustSupportDescription("");
    setTrustMediaKind("document");
    setTrustFilterKind("all");
    setTrustFilterQuery("");
    resetTrustAttachments();
    setTrustMenu(null);
    setTrustSelectMode(false);
    setTrustSelectedIds(new Set());
    setTrustSectionPicker(null);
    setTrustEdit(null);
    stopRecording();
    pendingAttachments.forEach((attachment) => revokeAttachmentPreview(attachment));
    setPendingAttachments([]);
    setReplyToMessageId(null);
    setEditingMessageId(null);
    draftBeforeEditRef.current = "";
    // forwardComposition НЕ сбрасываем: превью пересылки в TG-стиле едет за
    // юзером при смене чата, send в новом чате отправит форвард сюда.
    resetDraftHistory();
    setDraft(nextDraft);
    setSearchQuery("");
    setPickerOpen(false);
  }, [selectedChatId]);

  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    // Дозаливка черновика из стора — только пока юзер ничего не трогал в
    // поле (стек undo пуст). Иначе после Ctrl+Z до пустой строки или после
    // Ctrl+A+Delete сюда возвращался старый черновик целиком.
    const history = draftHistoryRef.current;
    if (
      draft.length === 0 &&
      activeDraft.length > 0 &&
      history.past.length === 0 &&
      history.future.length === 0
    ) {
      setDraft(activeDraft);
    }
  }, [activeDraft, selectedChatId]);

  // Ленивая загрузка быстрых ответов TG Business: первый «/» за сессию.
  useEffect(() => {
    if (draft.startsWith("/") && !quickRepliesRequestedRef.current) {
      quickRepliesRequestedRef.current = true;
      listQuickReplies();
    }
  }, [draft, listQuickReplies]);

  // Ревокация превью — только на unmount: при добавлении файлов массив
  // меняется, но прежние вложения остаются в нём, их previewUrl ещё нужен.
  // Явные удаления revoke'ают точечно (resetTrustAttachments / крестик).
  const trustAttachmentsRef = useRef<PendingAttachment[]>([]);
  trustAttachmentsRef.current = trustAttachments;
  useEffect(
    () => () => {
      trustAttachmentsRef.current.forEach((attachment) =>
        revokeAttachmentPreview(attachment)
      );
    },
    []
  );

  useEffect(() => {
    setInternalTagsInput(selectedChatTagsKey);
  }, [selectedChatId, selectedChatTagsKey]);

  const draftSyncSkipFirstRef = useRef<string | null>(null);
  useEffect(() => {
    draftSyncSkipFirstRef.current = selectedChatId;
  }, [selectedChatId]);

  // Forum-чаты: при выборе чата дёргаем список тем один раз. Сам по себе SPA
  // их не получает (TDLib шлёт обновления только по подписке через openChat).
  useEffect(() => {
    if (!selectedChatId || !selectedChat?.isForum) {
      return;
    }
    if (forumTopics === null) {
      listForumTopics(selectedChatId);
    }
  }, [forumTopics, listForumTopics, selectedChat?.isForum, selectedChatId]);

  // ESC закрывает по иерархии: сначала локальные оверлеи (reaction picker / редактирование /
  // ответ / forward / поиск / drawers), и только если ничего не открыто — снимает выбор чата.
  const selectChat = useRealtimeStore((state) => state.selectChat);
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      if (messageContextMenu) {
        setMessageContextMenu(null);
        return;
      }
      // Режим множественного выбора — тоже «оверлей»: Esc снимает выбор,
      // а не закрывает чат целиком.
      if (selectionMode) {
        exitSelectionMode();
        return;
      }
      if (reactionPickerForMessage) {
        setReactionPickerForMessage(null);
        return;
      }
      if (editingMessageId) {
        cancelEditing();
        return;
      }
      if (replyToMessageId) {
        setReplyToMessageId(null);
        return;
      }
      if (activePanel !== null) {
        if (activePanel === "search") {
          clearSearch(selectedChatId);
        }
        setActivePanel(null);
        return;
      }
      selectChat(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [
    activePanel,
    cancelEditing,
    clearSearch,
    editingMessageId,
    exitSelectionMode,
    messageContextMenu,
    reactionPickerForMessage,
    replyToMessageId,
    selectChat,
    selectedChatId,
    selectionMode
  ]);

  // Автофокус композера: при выборе чата + при возврате во вкладку (focus/visibility).
  // Цель — саппорт переключается Alt+Tab и сразу печатает, не тыкая мышью в поле.
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const target = composerTextareaRef.current;
    if (!target || target.disabled) {
      return;
    }
    target.focus();
  }, [selectedChatId]);

  useEffect(() => {
    const focusComposer = (): void => {
      const target = composerTextareaRef.current;
      if (!target || target.disabled) {
        return;
      }
      // Не отбираем фокус, если пользователь сейчас в другом инпуте/textarea/contenteditable.
      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        active !== target &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          (active as HTMLElement).isContentEditable)
      ) {
        return;
      }
      target.focus();
    };
    const handleVisibility = (): void => {
      if (document.visibilityState === "visible") {
        focusComposer();
      }
    };
    window.addEventListener("focus", focusComposer);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", focusComposer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (!selectedChatId || editingMessageId) {
      return;
    }

    if (draftSyncSkipFirstRef.current === selectedChatId) {
      draftSyncSkipFirstRef.current = null;
      return;
    }

    if (draft.trim().length === 0) {
      clearStoredDraft(selectedChatId);
      return;
    }

    setStoredDraft(selectedChatId, draft);
  }, [clearStoredDraft, draft, editingMessageId, selectedChatId, setStoredDraft]);

  useEffect(() => {
    if (!editingTarget) {
      return;
    }
    resetDraftHistory();
    // Текст — сам текст; медиа — его подпись (пустая, если подписи нет).
    setDraft(
      editingTarget.content.kind === "text"
        ? editingTarget.content.text
        : "caption" in editingTarget.content
          ? editingTarget.content.caption ?? ""
          : ""
    );
  }, [editingTarget]);

  // Счётчик «Отложенные · N» в панели чата: полку подтягиваем один раз
  // на чат, с задержкой — чтобы не толкаться с загрузкой истории.
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const chatId = selectedChatId;
    const timer = window.setTimeout(() => {
      const state = useRealtimeStore.getState();
      if (state.selectedChatId !== chatId || state.scheduledByChat[chatId] !== undefined) {
        return;
      }
      loadScheduledMessages(chatId);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [loadScheduledMessages, selectedChatId]);
  // Смена чата закрывает окно отложенных и меню отправки.
  useEffect(() => {
    setScheduledWindowOpen(false);
    setSendMenu(null);
    setSchedulePickerOpen(false);
    scheduleRef.current = null;
  }, [selectedChatId]);

  useEffect(() => () => stopRecording(), []);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !selectedChatId || visibleMessages.length === 0) {
      return;
    }

    if (previousChatIdRef.current !== selectedChatId) {
      previousChatIdRef.current = selectedChatId;
      initialStickChatIdRef.current = selectedChatId;
      programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
      debugScroll("chat switched", { selectedChatId });
    }

    if (initialStickChatIdRef.current === selectedChatId) {
      // Открытие чата: прижимаем низ на каждое изменение ленты, пока история
      // и замеры не устаканились (или пользователь сам не начал скроллить).
      // Якорь дозагрузки в этом режиме не нужен — его мог поставить только
      // probe короткой истории, где позиция скролла не имеет значения.
      anchorMessageIdRef.current = null;
      lastStickMessageIdRef.current = visibleMessages.at(-1)?.id ?? null;
      programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
      debugScroll("initial stick → bottom", { count: visibleMessages.length });
      messageVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: "end" });
      // Высоты строк — оценки; реальные приезжают после замера. Второй проход
      // на следующем кадре добивает скролл до настоящего низа. Проверка флага
      // внутри кадра обязательна: pending-прыжок к сообщению (поиск/закреп)
      // успевает снять режим между эффектами, и без неё кадр вернул бы ленту
      // в конец поверх прыжка.
      const frame = requestAnimationFrame(() => {
        if (initialStickChatIdRef.current === selectedChatId) {
          programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
          messageVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: "end" });
        }
      });
      return () => cancelAnimationFrame(frame);
    }

    const anchorId = anchorMessageIdRef.current;
    if (anchorId) {
      const anchorIndex = visibleMessages.findIndex((message) => message.id === anchorId);
      // Якорь ставится ПЕРЕД дозагрузкой старой истории и должен срабатывать
      // ТОЛЬКО когда она реально приехала — то есть выше якоря появились
      // сообщения (anchorIndex > 0). Раньше сюда попадало любое изменение
      // ленты (отправка своего сообщения, приход перевода, редактирование):
      // эффект видел живой якорь и уводил скролл на его позицию — юзера
      // «перекидывало в середину чата».
      if (anchorIndex > 0) {
        programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
        // Восстанавливаем ПОЛОЖЕНИЕ якоря во вьюпорте, а не «якорь в верх»
        // (scrollToIndex по оценкам высот новых строк промахивался, и лента
        // уезжала вверх). Замеры новых строк выше вьюпорта виртуализатор
        // потом компенсирует сам (shouldAdjustScrollPositionOnItemSizeChange).
        const anchorStart = messageVirtualizer.getOffsetForIndex(anchorIndex, "start")?.[0];
        if (anchorStart !== undefined && Number.isFinite(anchorStart)) {
          const nextScrollTop = Math.max(0, anchorStart - anchorViewportOffsetRef.current);
          debugScroll("older page → restore anchor", {
            anchorIndex,
            anchorStart,
            viewportOffset: anchorViewportOffsetRef.current,
            nextScrollTop
          });
          body.scrollTop = nextScrollTop;
        } else {
          debugScroll("older page → scrollToIndex fallback", { anchorIndex });
          messageVirtualizer.scrollToIndex(anchorIndex, { align: "start" });
        }
        anchorMessageIdRef.current = null;
        return;
      }
      if (anchorIndex < 0) {
        // Якорное сообщение пропало (сменился чат/тема) — забываем про него.
        anchorMessageIdRef.current = null;
      }
      // anchorIndex === 0: догрузка ещё в пути. Якорь оставляем и идём
      // дальше по обычной логике — иначе лента перестала бы прилипать к низу.
    }

    // Прилипаем к низу только когда в ленте появилось РЕАЛЬНО новое последнее
    // сообщение. Раньше эффект дергал scrollToIndex на любое изменение массива
    // (эдит, реакция, перевод, статус доставки): в активной группе с потоком
    // апдейтов юзера, начавшего скроллить вверх, каждый раз возвращало вниз —
    // ощущалось как «скролл не работает».
    const lastMessage = visibleMessages.at(-1);
    const lastId = lastMessage?.id ?? null;
    const lastChanged = lastId !== lastStickMessageIdRef.current;
    lastStickMessageIdRef.current = lastId;
    if (!lastChanged) {
      return;
    }

    // Своё только что отправленное сообщение уводит ленту вниз ВСЕГДА — как в
    // оригинальном Telegram. Раньше работал общий порог «мы почти внизу», и
    // отправка из середины истории оставляла саппорта там же: он не видел, что
    // именно ушло. Признак своей отправки — новый последний элемент, который
    // outgoing и ещё не подтверждён сервером (оптимистичный).
    const isOwnJustSent = Boolean(lastMessage?.outgoing && lastMessage.isPending);
    const distanceToBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
    if (isOwnJustSent || distanceToBottom < 140) {
      programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
      debugScroll(isOwnJustSent ? "own message sent → bottom" : "new tail near bottom → bottom", {
        distanceToBottom
      });
      messageVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: "end" });
      if (isOwnJustSent) {
        // Высота свежей строки — пока оценка, реальная приезжает после
        // замера. Без второго прохода лента останавливается «почти внизу».
        const frame = requestAnimationFrame(() => {
          programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
          messageVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: "end" });
        });
        return () => cancelAnimationFrame(frame);
      }
    }
    return;
  }, [messageVirtualizer, selectedChatId, visibleMessages]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !selectedChatId) {
      return;
    }

    const updateScrollDown = (): void => {
      const distanceToBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
      // Двойной критерий: пиксельная дистанция И «последняя строка не в
      // видимом диапазоне виртуализатора». Одной дистанции мало: высоты
      // строк — оценки, которые виртуализатор домеряет после скролла, и
      // при устаревшем scrollHeight кнопка пропадала из середины чата.
      const range = messageVirtualizer.range;
      const lastRowHidden =
        range !== null && visibleMessages.length > 0
          ? range.endIndex < visibleMessages.length - 1
          : false;
      setShowScrollDown(distanceToBottom > 200 || lastRowHidden);
    };

    // Дата верхнего ВИДИМОГО сообщения. range.startIndex не годится — он
    // включает overscan (строки выше вьюпорта), и дата отставала на экран.
    const topVisibleMessage = (): MessageViewModel | null => {
      const scrollTop = body.scrollTop;
      for (const item of messageVirtualizer.getVirtualItems()) {
        // item.start уже задан с учётом scrollMargin (виртуализатор создан с
        // ним), поэтому это и есть смещение строки от верха скролл-контента —
        // вычитать messageScrollMargin ещё раз нельзя, дата отставала бы на
        // высоту шапки списка.
        if (item.start + item.size > scrollTop) {
          return visibleMessages[item.index] ?? null;
        }
      }
      return visibleMessages.at(-1) ?? null;
    };

    const showFloatingDay = (): void => {
      const message = topVisibleMessage();
      if (message) {
        setFloatingDayLabel(formatDaySeparator(message.occurredAt, timeZone));
      }
      if (!floatingDayVisibleRef.current) {
        floatingDayVisibleRef.current = true;
        setIsFloatingDayVisible(true);
      }
      if (floatingDayTimerRef.current !== null) {
        window.clearTimeout(floatingDayTimerRef.current);
      }
      floatingDayTimerRef.current = window.setTimeout(() => {
        floatingDayTimerRef.current = null;
        floatingDayVisibleRef.current = false;
        setIsFloatingDayVisible(false);
      }, 1400);
    };

    const setHistoryAnchor = (): void => {
      anchorMessageIdRef.current = visibleMessages[0]?.id ?? null;
      const firstStart = messageVirtualizer.getOffsetForIndex(0, "start")?.[0] ?? 0;
      anchorViewportOffsetRef.current = firstStart - body.scrollTop;
    };

    const handleScroll = (): void => {
      // Догрузка истории — только по ПОЛЬЗОВАТЕЛЬСКОМУ скроллу к верху.
      // Программные прыжки (прижатие низа при открытии чата/отправке,
      // клампинг scrollTop при усадке ленты) сюда попадали с scrollTop<120,
      // ставили якорь на первое сообщение и после prepend уводили ленту вверх.
      const programmatic =
        Date.now() < programmaticScrollUntilRef.current ||
        initialStickChatIdRef.current === selectedChatId;
      if (body.scrollTop < 120 && !isHistoryLoading && !reachedStart) {
        if (programmatic) {
          debugScroll("scroll<120 ignored (programmatic window)", { scrollTop: body.scrollTop });
        } else {
          debugScroll("scroll<120 → load older", { scrollTop: body.scrollTop });
          setHistoryAnchor();
          loadOlderHistory(selectedChatId, 40);
        }
      }
      updateScrollDown();
      showFloatingDay();
    };

    // Жест пользователя снимает режим «держать низ» после открытия чата:
    // колесо/тач/mousedown (в т.ч. перетаскивание скроллбара). Программные
    // scrollToIndex этих событий не порождают — только scroll, поэтому его
    // здесь нет.
    const cancelInitialStick = (): void => {
      initialStickChatIdRef.current = null;
    };

    const onGeometryChange = (): void => {
      // Пока активен режим открытия чата, любое изменение геометрии ленты
      // (замер строки, докачанная картинка) пере-прижимает низ — иначе
      // выросший после scrollToIndex контент оставляет ленту «почти в конце».
      if (initialStickChatIdRef.current === selectedChatId && visibleMessages.length > 0) {
        programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
        messageVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: "end" });
      }
      updateScrollDown();
    };

    updateScrollDown();
    body.addEventListener("scroll", handleScroll, { passive: true });
    body.addEventListener("wheel", cancelInitialStick, { passive: true });
    body.addEventListener("touchstart", cancelInitialStick, { passive: true });
    body.addEventListener("mousedown", cancelInitialStick, { passive: true });
    // Геометрия ленты меняется и без скролла: строки домеряются после
    // оценки, докачиваются картинки, меняется высота композера. Следим за
    // размерами контейнера и самого списка, чтобы состояние не протухало.
    // Тот же приём, что и с отступом: onGeometryChange умеет скроллить ленту
    // (initial stick), а скролл внутри колбэка наблюдателя и порождает
    // «ResizeObserver loop …».
    let geometryFrame = 0;
    const onGeometryChangeDeferred = (): void => {
      if (geometryFrame) {
        return;
      }
      geometryFrame = window.requestAnimationFrame(() => {
        geometryFrame = 0;
        onGeometryChange();
      });
    };
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onGeometryChangeDeferred)
        : null;
    if (resizeObserver) {
      resizeObserver.observe(body);
      const list = body.querySelector(".message-list");
      if (list) {
        resizeObserver.observe(list);
      }
    }
    return () => {
      body.removeEventListener("scroll", handleScroll);
      body.removeEventListener("wheel", cancelInitialStick);
      body.removeEventListener("touchstart", cancelInitialStick);
      body.removeEventListener("mousedown", cancelInitialStick);
      if (geometryFrame) {
        window.cancelAnimationFrame(geometryFrame);
      }
      resizeObserver?.disconnect();
      if (floatingDayTimerRef.current !== null) {
        window.clearTimeout(floatingDayTimerRef.current);
        floatingDayTimerRef.current = null;
      }
      floatingDayVisibleRef.current = false;
      setIsFloatingDayVisible(false);
    };
  }, [
    isHistoryLoading,
    loadOlderHistory,
    messageVirtualizer,
    reachedStart,
    selectedChatId,
    timeZone,
    visibleMessages
  ]);

  useEffect(() => {
    if (selectedChatId) {
      return;
    }

    previousChatIdRef.current = null;
    anchorMessageIdRef.current = null;
    initialStickChatIdRef.current = null;
  }, [selectedChatId]);

  // Если воркер вернул пустую пачку с hasMore=true, повторный запрос без этого
  // guard'а уходил бы бесконечно (visibleMessages не меняется → эффект
  // перезапускается на каждом isHistoryLoading-переключении). Повторяем
  // догрузку только когда прошлая реально продвинула историю.
  const shortHistoryProbeRef = useRef<{ chatId: string | null; firstId: string | null }>({
    chatId: null,
    firstId: null
  });
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !selectedChatId || visibleMessages.length === 0 || isHistoryLoading || reachedStart) {
      return;
    }

    // Второй критерий — число строк: TDLib на первый запрос по «холодному»
    // чату отдаёт одно сообщение (last_message), воркер теперь добирает
    // страницу сам, но если она всё же пришла короткой, одно высокое фото
    // при мелком вьюпорте (зум) делало ленту «выше экрана» — пиксельный
    // критерий молчал, и саппорт видел одно сообщение без догрузки.
    if (
      body.scrollHeight <= body.clientHeight + 24 ||
      visibleMessages.length < SHORT_HISTORY_MIN_ROWS
    ) {
      const firstId = visibleMessages[0]?.id ?? null;
      const probe = shortHistoryProbeRef.current;
      if (probe.chatId === selectedChatId && probe.firstId === firstId) {
        return;
      }
      shortHistoryProbeRef.current = { chatId: selectedChatId, firstId };
      // При активном initial-stick якорь ставить нельзя: initialStickChatIdRef
      // уже держит скролл у низа, а любой тик колеса/тачпада снимает эту
      // защиту (см. cancelInitialStick) — тогда restore-anchor физически
      // толкает вьюпорт на позицию «первого КЭШИРОВАННОГО» сообщения (это
      // середина после prepend). Симптом «чат сам улетает в середину при
      // открытии» — раунд 53, баг 9.
      if (initialStickChatIdRef.current !== selectedChatId) {
        anchorMessageIdRef.current = firstId;
        anchorViewportOffsetRef.current =
          (messageVirtualizer.getOffsetForIndex(0, "start")?.[0] ?? 0) - body.scrollTop;
      }
      debugScroll("short history probe → load older", { firstId });
      loadOlderHistory(selectedChatId, 40);
    }
  }, [isHistoryLoading, loadOlderHistory, reachedStart, selectedChatId, visibleMessages]);

  useEffect(() => {
    // Чат открыт, но вкладка в фоне / окно без фокуса — оператор его не
    // видит, входящие остаются непрочитанными до возвращения (pageActive).
    if (!selectedChatId || !pageActive) {
      return;
    }

    // Отметку прочтения шлём, пока маркер TDLib отстаёт от САМОГО СВЕЖЕГО
    // сообщения чата, а не только от последнего входящего. Отправленное по
    // расписанию исходящее TDLib считает непрочитанным (has_incoming_
    // notification → unread_count + 1); раньше при «последнее входящее уже
    // прочитано» markRead не уходил вовсе, и бейдж после открытия чата не
    // снимался (запрос №11, 2026-10-13). Воркер читает до последнего
    // сообщения чата (см. chat.markRead), поэтому id входящего остаётся
    // точкой отсчёта, а при его отсутствии шлём id последнего сообщения.
    const reversed = [...messages].reverse();
    const latestMessage = reversed.find((message) => /^\d+$/.test(message.id));
    const latestIncomingMessage = reversed.find((message) => !message.outgoing);
    const target = latestIncomingMessage ?? latestMessage;
    if (!target || !latestMessage) {
      return;
    }

    const readMarker = selectedChat?.lastReadInboxMessageId;
    if (readMarker && isMessageIdAtMost(latestMessage.id, readMarker)) {
      lastMarkedReadMessageIdByChatRef.current[selectedChatId] = latestMessage.id;
      return;
    }

    if (lastMarkedReadMessageIdByChatRef.current[selectedChatId] === latestMessage.id) {
      return;
    }

    lastMarkedReadMessageIdByChatRef.current[selectedChatId] = latestMessage.id;
    markRead(selectedChatId, target.id);
  }, [markRead, messages, pageActive, selectedChat?.lastReadInboxMessageId, selectedChatId]);

  useEffect(() => {
    if (!deletedOpen) {
      return;
    }

    void loadDeletedMessages();
  }, [deletedOpen, loadDeletedMessages]);

  // Escape для форвард-превью: сбрасываем композицию + ПКМ-меню. Пикер
  // имеет собственный Escape-listener.
  useEffect(() => {
    if (!forwardComposition) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closeForwardComposition();
        setForwardPreviewMenu(null);
        setForwardPickerOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [forwardComposition, closeForwardComposition]);

  // Если forwardComposition закрылся снаружи (Send, например) — пикер тоже
  // прячем, иначе он мог бы остаться открытым с пустым контекстом.
  useEffect(() => {
    if (!forwardComposition && forwardPickerOpen) {
      setForwardPickerOpen(false);
    }
  }, [forwardComposition, forwardPickerOpen]);

  useEffect(() => {
    if (!trustOpen) {
      return;
    }

    void loadTrustAssets();
  }, [loadTrustAssets, trustOpen]);

  // Отметки «уже отправляли в этот чат» — отдельным запросом: они зависят от
  // чата, а список шаблонов от него не зависит и перечитывать его при каждом
  // переключении чата ни к чему.
  useEffect(() => {
    if (!trustOpen || !accountId || !selectedChatId) {
      return;
    }
    let cancelled = false;
    void internalApi
      .listTrustAssetUsage(accountId, selectedChatId, wsUrl)
      .then(({ usage }) => {
        if (cancelled) {
          return;
        }
        setTrustUsage(
          Object.fromEntries(usage.map((entry) => [entry.assetId, entry]))
        );
      })
      .catch(() => {
        // Плашка косметическая: молча живём без неё, чтобы не подсвечивать
        // саппорту ошибку там, где ничего не сломалось.
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, selectedChatId, trustOpen, wsUrl]);

  useEffect(() => {
    if (!selectedChatId || isRecordingVoice) {
      return;
    }

    if (!hasTypingContent) {
      setTyping(selectedChatId, false);
      return;
    }

    setTyping(selectedChatId, true);
    const intervalId = window.setInterval(() => {
      setTyping(selectedChatId, true);
    }, 4_500);

    return () => {
      window.clearInterval(intervalId);
      setTyping(selectedChatId, false);
    };
  }, [hasTypingContent, isRecordingVoice, selectedChatId, setTyping]);

  // Вспышка строки после перехода по reply/закрепу — иначе глаз теряет цель.
  const [flashMessageId, setFlashMessageId] = useState<string | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const flashMessage = useCallback((messageId: string): void => {
    if (flashTimeoutRef.current !== null) {
      window.clearTimeout(flashTimeoutRef.current);
    }
    setFlashMessageId(messageId);
    flashTimeoutRef.current = window.setTimeout(() => {
      setFlashMessageId(null);
      flashTimeoutRef.current = null;
    }, 1_400);
  }, []);

  const scrollToMessage = (messageId: string): void => {
    // Прыжок к конкретному сообщению отменяет «держать низ» открытого чата —
    // иначе следующий же замер строки вернул бы ленту в конец.
    initialStickChatIdRef.current = null;
    const index = visibleMessages.findIndex((message) => message.id === messageId);
    if (index < 0) {
      // Сообщение ещё не в загруженном окне истории (типично для старого
      // закрепа): ставим pending-цель — эффект ниже дозагрузит историю
      // и доскроллит, когда сообщение появится.
      if (selectedChatId) {
        requestScrollToMessage(selectedChatId, messageId);
      }
      return;
    }
    messageVirtualizer.scrollToIndex(index, { align: "center", behavior: "smooth" });
    flashMessage(messageId);
  };
  // Стабильная обёртка для MessageRow: сам scrollToMessage пересоздаётся на
  // каждом рендере (замыкает visibleMessages) и ломал бы memo строк.
  const scrollToMessageRef = useRef<(messageId: string) => void>(scrollToMessage);
  scrollToMessageRef.current = scrollToMessage;
  const scrollToMessageStable = useCallback((messageId: string) => {
    scrollToMessageRef.current(messageId);
  }, []);

  // Внешний триггер скролла (из chat-profile media-tabs): когда там кликают
  // по элементу, в store ставится pendingScrollMessageId. Чат пытается
  // скроллить когда сообщение прогрузилось.
  const pendingScrollMessageId = useRealtimeStore((state) => state.pendingScrollMessageId);
  const requestScrollToMessage = useRealtimeStore((state) => state.requestScrollToMessage);
  const consumePendingScrollMessage = useRealtimeStore(
    (state) => state.consumePendingScrollMessage
  );
  // Лимит пачек догрузки на одну pending-цель: без него клик по очень старому
  // сообщению (reply/закреп/медиа из профиля) линейно выкачивал историю до
  // упора — каждая пачка = полный ре-рендер + scrollToIndex, лента прыгает,
  // стор пухнет на десятки тысяч сообщений, UI фактически заморожен.
  const pendingScrollLoadRef = useRef<{ target: string | null; batches: number }>({
    target: null,
    batches: 0
  });
  useEffect(() => {
    if (!pendingScrollMessageId || !selectedChatId) {
      return;
    }
    // Внешний прыжок к сообщению (медиа из профиля, закреп) отменяет
    // «держать низ» открытого чата — иначе режим утащил бы ленту обратно.
    initialStickChatIdRef.current = null;
    const index = visibleMessages.findIndex(
      (message) => message.id === pendingScrollMessageId
    );
    if (index >= 0) {
      messageVirtualizer.scrollToIndex(index, { align: "center", behavior: "smooth" });
      flashMessage(pendingScrollMessageId);
      consumePendingScrollMessage();
      return;
    }
    // Сообщения нет в окне — активно дозагружаем историю вглубь, пока цель
    // не появится. Достигли начала чата и не нашли — сдаёмся, иначе флаг
    // навсегда держал бы чат в режиме догрузки.
    if (reachedStart) {
      consumePendingScrollMessage();
      return;
    }
    if (pendingScrollLoadRef.current.target !== pendingScrollMessageId) {
      pendingScrollLoadRef.current = { target: pendingScrollMessageId, batches: 0 };
    }
    if (pendingScrollLoadRef.current.batches >= 8) {
      consumePendingScrollMessage();
      return;
    }
    if (!isHistoryLoading) {
      pendingScrollLoadRef.current.batches += 1;
      anchorMessageIdRef.current = visibleMessages[0]?.id ?? null;
      anchorViewportOffsetRef.current =
        (messageVirtualizer.getOffsetForIndex(0, "start")?.[0] ?? 0) -
        (bodyRef.current?.scrollTop ?? 0);
      debugScroll("jump-to-message → load older", { batches: pendingScrollLoadRef.current.batches });
      loadOlderHistory(selectedChatId, 100);
    }
  }, [
    pendingScrollMessageId,
    selectedChatId,
    visibleMessages,
    messageVirtualizer,
    consumePendingScrollMessage,
    flashMessage,
    isHistoryLoading,
    reachedStart,
    loadOlderHistory
  ]);

  const attachFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      setComposerError(null);
      stopRecording();
      // Режим правки: вложение = замена медиа сообщения. В текстовое
      // сообщение Telegram медиа добавить не даёт; заменить можно одним
      // фото/видео/файлом (последнее вставленное побеждает).
      const editing = editingTargetRef.current;
      if (editing) {
        if (editing.content.kind === "text") {
          setComposerError(
            "В текстовое сообщение нельзя добавить фото — отправьте его отдельным сообщением."
          );
          return;
        }
        const file = files[files.length - 1]!;
        const kind = pickAttachmentKind(file);
        if (kind !== "photo" && kind !== "video" && kind !== "document") {
          setComposerError("Заменить медиа можно только фото, видео или файлом.");
          return;
        }
        const previewUrl = kind === "photo" || kind === "video" ? URL.createObjectURL(file) : null;
        const replacement: PendingAttachment = {
          file,
          kind,
          previewUrl,
          ...(await readAttachmentMetadata(file, kind, previewUrl))
        };
        setPendingAttachments((previous) => {
          previous.forEach((attachment) => revokeAttachmentPreview(attachment));
          return [replacement];
        });
        return;
      }
      const next: PendingAttachment[] = [];
      for (const file of files) {
        const kind = pickAttachmentKind(file);
        const previewUrl =
          kind === "photo" || kind === "video" || kind === "voice"
            ? URL.createObjectURL(file)
            : null;
        next.push({
          file,
          kind,
          previewUrl,
          ...(await readAttachmentMetadata(file, kind, previewUrl))
        });
      }
      setPendingAttachments((previous) => {
        const merged = [...previous, ...next];
        if (merged.length > 10) {
          setComposerError("Не больше 10 вложений за раз.");
          merged
            .slice(10)
            .forEach((attachment) => revokeAttachmentPreview(attachment));
        }
        return merged.slice(0, 10);
      });
    },
    []
  );

  // Клик по миниатюре редактируемого фото: скачиваем текущее изображение,
  // делаем его заменой (pendingAttachments[0]) и сразу открываем редактор
  // стрелок — сохранение уйдёт как editMessageMedia с новым файлом.
  const openCurrentMediaForEdit = useCallback(async (): Promise<void> => {
    const target = editingTargetRef.current;
    if (!target || target.content.kind !== "photo") {
      return;
    }
    const existing = pendingAttachments[0];
    if (existing && existing.kind === "photo") {
      setAnnotatingIndex(0);
      return;
    }
    const sourceUrl = target.content.url ?? target.content.thumbnailUrl;
    if (!sourceUrl) {
      setComposerError("Фото ещё не загружено — попробуйте чуть позже.");
      return;
    }
    try {
      const response = await fetch(sourceUrl, { credentials: "same-origin" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const mime = blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
      const file = new File([blob], `photo-${target.id}.${mime === "image/png" ? "png" : "jpg"}`, {
        type: mime
      });
      await attachFiles([file]);
      setAnnotatingIndex(0);
    } catch (cause) {
      setComposerError(
        cause instanceof Error ? `Не удалось открыть фото: ${cause.message}` : "Не удалось открыть фото"
      );
    }
  }, [attachFiles, pendingAttachments]);

  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const handleBodyDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    if (!selectedChatId || isSubmittingAttachment || isRecordingVoice) {
      return;
    }
    if (!event.dataTransfer?.types.includes("Files")) {
      return;
    }
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleBodyDragLeave = (): void => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleBodyDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    if (!selectedChatId || isSubmittingAttachment || isRecordingVoice) {
      return;
    }
    if (!event.dataTransfer?.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleBodyDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (!selectedChatId || isSubmittingAttachment || isRecordingVoice) {
      return;
    }
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length === 0) {
      return;
    }
    void attachFiles(files);
  };

  const composerFormRef = useRef<HTMLFormElement | null>(null);
  // Bug: два быстрых Enter/клика Send в одном React-tick'е брали ОДНО и то же
  // значение `draft` (setDraft ещё не отрендерился), в результате отправлялись
  // два сообщения с разными localId — одно нормально уходило, второе висло
  // «в вечной загрузке». Guard-ref блокирует повторный submit до тех пор,
  // пока текущий не завершится (для sync-пути — до следующего тика).
  const composerSubmittingRef = useRef(false);

  // Выделение протяжкой ЛКМ (как в Telegram Desktop): зажали на сообщении,
  // повели вверх/вниз — выделяются все сообщения между якорем и курсором.
  // Пока курсор в пределах якорного ряда — работает обычное выделение текста;
  // как только он пересёк соседний ряд, переключаемся на выделение сообщений
  // (снимаем текстовое выделение, вешаем user-select: none на body).
  // Диапазон считаем по индексам visibleMessages (виртуализатор не рендерит
  // ряды вне вьюпорта), ряд под курсором ищем через elementFromPoint.
  const dragSelectRef = useRef<{ anchorIndex: number; active: boolean } | null>(null);
  // visibleMessagesRef объявлен выше (общий с просмотрщиком медиа).
  const rowIndexFromPoint = (x: number, y: number): number | null => {
    const element = document.elementFromPoint(x, y);
    const row = element?.closest<HTMLElement>(".message-list__item[data-index]");
    if (!row) return null;
    const index = Number(row.dataset.index);
    return Number.isFinite(index) ? index : null;
  };
  const handleListMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (
      !target ||
      target.closest("a,button,input,textarea,select,video,audio,[contenteditable=\"true\"]")
    ) {
      return;
    }
    const row = target.closest<HTMLElement>(".message-list__item[data-index]");
    if (!row) return;
    const index = Number(row.dataset.index);
    if (!Number.isFinite(index)) return;
    dragSelectRef.current = { anchorIndex: index, active: false };
  };
  useEffect(() => {
    if (!selectedChatId) return;
    const applyRange = (from: number, to: number): void => {
      const list = visibleMessagesRef.current;
      const lo = Math.max(0, Math.min(from, to));
      const hi = Math.min(list.length - 1, Math.max(from, to));
      const ids = new Set<string>();
      for (let i = lo; i <= hi; i += 1) {
        const message = list[i];
        if (message && !message.isDeleted) {
          ids.add(message.id);
        }
      }
      setSelectedMessageIds((previous) => {
        if (previous.size === ids.size && [...ids].every((id) => previous.has(id))) {
          return previous;
        }
        return ids;
      });
    };
    const onMove = (event: MouseEvent): void => {
      const state = dragSelectRef.current;
      if (!state) return;
      if ((event.buttons & 1) === 0) {
        dragSelectRef.current = null;
        document.body.classList.remove("is-drag-selecting");
        return;
      }
      const body = bodyRef.current;
      const rect = body ? visualRect(body) : null;
      // Автоскролл у краёв ленты; точку поиска ряда клампим внутрь ленты.
      let y = event.clientY;
      if (rect) {
        if (event.clientY < rect.top + 40) body!.scrollTop -= 12;
        else if (event.clientY > rect.bottom - 40) body!.scrollTop += 12;
        y = Math.max(rect.top + 2, Math.min(rect.bottom - 2, event.clientY));
      }
      const index = rowIndexFromPoint(event.clientX, y);
      if (index === null) return;
      if (!state.active) {
        if (index === state.anchorIndex) return;
        state.active = true;
        window.getSelection()?.removeAllRanges();
        document.body.classList.add("is-drag-selecting");
      }
      event.preventDefault();
      applyRange(state.anchorIndex, index);
    };
    const onUp = (): void => {
      const state = dragSelectRef.current;
      dragSelectRef.current = null;
      if (!state?.active) return;
      document.body.classList.remove("is-drag-selecting");
      // Следующий click (по пузырю под курсором) переключил бы выбор —
      // гасим его один раз на capture-фазе.
      window.addEventListener(
        "click",
        (clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
        },
        { capture: true, once: true }
      );
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      dragSelectRef.current = null;
      document.body.classList.remove("is-drag-selecting");
    };
  }, [selectedChatId]);

  const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Снимок выделения ДО мутации — для снимка undo в handleDraftChange.
    rememberDraftSelection(event.currentTarget);
    // Ctrl/Cmd+Z — undo, Ctrl/Cmd+Shift+Z и Ctrl+Y — redo. Нативный стек
    // браузера не используем (см. draftHistoryRef).
    if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.nativeEvent.isComposing) {
      const key = event.key.toLowerCase();
      // Ctrl/Cmd+K — ссылка на выделенный текст (как в Telegram Desktop на
      // Windows); на Mac TG Desktop использует ⌘U («Преобразования →
      // Добавить ссылку», скрин в ТЗ) — поддерживаем оба. event.code — чтобы
      // работало и на русской раскладке («л» / «г»).
      const isLinkShortcut =
        ((key === "k" || event.code === "KeyK") ||
          (event.metaKey && !event.ctrlKey && (key === "u" || event.code === "KeyU"))) &&
        !event.shiftKey;
      if (isLinkShortcut) {
        event.preventDefault();
        const el = event.currentTarget;
        if (!openLinkEditor(el.selectionStart, el.selectionEnd)) {
          setComposerError("Выделите текст, к которому нужно привязать ссылку.");
        }
        return;
      }
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        undoDraft(event.currentTarget);
        return;
      }
      if ((key === "z" && event.shiftKey) || (key === "y" && !event.shiftKey)) {
        event.preventDefault();
        redoDraft(event.currentTarget);
        return;
      }
    }
    // Ctrl/Cmd + ArrowUp / ArrowDown — навигация по сообщениям чата для reply,
    // как в Telegram Desktop. Стартовый Ctrl+↑ без активного reply выбирает
    // последнее валидное сообщение (любое, включая свои). Повторные Ctrl+↑
    // двигают курсор вверх по истории; Ctrl+↓ — вниз. На нижнем конце
    // Ctrl+↓ снимает reply. Скроллим к выбранному, чтобы его было видно.
    // Пропускаем deleted/pending — на них ответить нельзя.
    if (
      (event.key === "ArrowUp" || event.key === "ArrowDown") &&
      (event.ctrlKey || event.metaKey) &&
      !event.nativeEvent.isComposing &&
      !editingMessageId
    ) {
      const isSelectable = (message: (typeof visibleMessages)[number]): boolean =>
        !message.isDeleted && !message.isPending && isServerMessageId(message.id);
      const currentIndex =
        replyToMessageId !== null
          ? visibleMessages.findIndex((message) => message.id === replyToMessageId)
          : -1;
      if (event.key === "ArrowUp") {
        // Стартовая позиция для поиска вверх: индекс до текущего, либо конец
        // списка если reply ещё не выбран.
        const from = currentIndex >= 0 ? currentIndex - 1 : visibleMessages.length - 1;
        for (let i = from; i >= 0; i -= 1) {
          const candidate = visibleMessages[i];
          if (candidate && isSelectable(candidate)) {
            event.preventDefault();
            setReplyToMessageId(candidate.id);
            scrollToMessage(candidate.id);
            return;
          }
        }
        // Валидного кандидата выше нет — оставляем текущий выбор как есть.
        event.preventDefault();
        return;
      }
      // ArrowDown: если reply не выбран, ничего не делаем (мы уже "внизу").
      if (currentIndex < 0) {
        return;
      }
      for (let i = currentIndex + 1; i < visibleMessages.length; i += 1) {
        const candidate = visibleMessages[i];
        if (candidate && isSelectable(candidate)) {
          event.preventDefault();
          setReplyToMessageId(candidate.id);
          scrollToMessage(candidate.id);
          return;
        }
      }
      // За текущим reply нет ни одного валидного — уходим "в композер",
      // снимая reply-состояние (как в Telegram Desktop).
      event.preventDefault();
      setReplyToMessageId(null);
      return;
    }

    // Стрелка вверх в пустом композере — редактировать последнее своё
    // текстовое сообщение (как в Telegram).
    if (
      event.key === "ArrowUp" &&
      !event.nativeEvent.isComposing &&
      draft.length === 0 &&
      !editingMessageId &&
      pendingAttachments.length === 0
    ) {
      const lastOwnText = [...visibleMessages]
        .reverse()
        .find(
          (message) =>
            message.outgoing &&
            !message.isDeleted &&
            !message.isPending &&
            message.content.kind === "text"
        );
      if (lastOwnText) {
        event.preventDefault();
        startEditingMessage(lastOwnText.id);
      }
      return;
    }

    if (event.key !== "Enter" || event.nativeEvent.isComposing) {
      return;
    }
    // Ctrl/Cmd+Shift+Enter — перевести написанное на язык клиента прямо в
    // поле (то же, что значок перевода). Отправка остаётся на обычном Enter,
    // чтобы переведённое можно было прочитать перед отправкой. Раньше
    // перевод висел на Ctrl+Enter, но по ТЗ 2026-09-09 это сочетание отдано
    // под перевод строки.
    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      event.preventDefault();
      void translateDraftForSending();
      return;
    }
    // Shift+Enter — нативный перевод строки, не перехватываем.
    if (event.shiftKey) {
      return;
    }
    // Ctrl/Cmd+Enter — тоже перевод строки (ТЗ 2026-09-09): саппорты
    // набирают абзацы этим сочетанием и раньше случайно отправляли
    // недописанное.
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      insertDraftNewline(event.currentTarget);
      return;
    }
    event.preventDefault();
    composerFormRef.current?.requestSubmit();
  };

  const saveInternalTags = async (): Promise<void> => {
    if (!accountId || !selectedChatId) {
      return;
    }

    const nextTags = internalTagsInput
      .split(/[,\n;]+/)
      .map((tag) => tag.trim())
      .filter((tag, index, tags) => tag.length > 0 && tags.indexOf(tag) === index)
      .slice(0, 24);

    setIsSavingTags(true);
    setTagsError(null);
    try {
      const response = await internalApi.updateChatTags(accountId, selectedChatId, nextTags, wsUrl);
      setChatInternalTags(selectedChatId, response.tags);
      setInternalTagsInput(response.tags.join(", "));
    } catch (cause) {
      setTagsError(cause instanceof Error ? cause.message : "Не удалось сохранить теги.");
    } finally {
      setIsSavingTags(false);
    }
  };

  const runTranslator = async (): Promise<void> => {
    const text = translatorInput.trim();
    if (text.length === 0) {
      setTranslatorError("Введите текст для перевода.");
      return;
    }
    const targetLanguage = translatorTargetLang.trim().toLowerCase();
    if (targetLanguage.length === 0) {
      setTranslatorError("Укажите целевой язык.");
      return;
    }
    setIsTranslatorBusy(true);
    setTranslatorError(null);
    setTranslatorCopied(false);
    try {
      const apiBase = httpBaseFromWsUrl(wsUrl);
      const response = await fetch(`${apiBase}/api/translate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        // accountId обязателен: без него шлюз ищет язык только в овнерском
        // списке и не находит языки гео-админов (eg_m, ma_w и т.п.) — тогда в
        // LLM уезжает голый код «EG_M», и перевод приходит на английском.
        body: JSON.stringify({ text, targetLanguage, accountId })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        const message =
          response.status === 503
            ? "Переводчик не настроен. Включите интеграцию «Переводчик» в Настройках."
            : response.status === 502
              ? `Провайдер перевода вернул ошибку — проверьте ключ и модель в Настройках → Интеграции. (${
                  payload?.message ?? "нет деталей"
                })`
              : payload?.message ?? "Не удалось получить перевод.";
        setTranslatorError(String(message));
        setTranslatorResult("");
        return;
      }
      setTranslatorResult(String(payload.text ?? ""));
    } catch (cause) {
      setTranslatorError(cause instanceof Error ? cause.message : "Сетевая ошибка.");
      setTranslatorResult("");
    } finally {
      setIsTranslatorBusy(false);
    }
  };

  // «Пишем по-русски → уходит на языке клиента»: кнопка подставляет перевод
  // прямо в поле ввода, отправка остаётся вторым, осознанным действием.
  // Русский оригинал возвращается кнопкой «Оригинал» или Ctrl/Cmd+Z.
  const translateDraftForSending = async (): Promise<void> => {
    const text = draft.trim();
    if (!selectedChatId || isTranslatingOutgoing) {
      return;
    }
    if (text.length === 0) {
      setOutgoingTranslationError("Сначала напишите текст в поле ввода.");
      return;
    }
    if (outgoingLang.length === 0) {
      setOutgoingTranslationError(
        "Не задан язык клиента. Выберите его кнопкой языков в шапке чата или добавьте язык аккаунту в Настройках → Переводчик."
      );
      return;
    }
    setIsTranslatingOutgoing(true);
    setOutgoingTranslationError(null);
    try {
      const apiBase = httpBaseFromWsUrl(wsUrl);
      const response = await fetch(`${apiBase}/api/translate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        // accountId обязателен: без него шлюз не находит языки гео-админов
        // (eg_m, ma_w) и в LLM уезжает голый код — перевод придёт не на том
        // языке. Та же грабля, что была у мини-переводчика.
        body: JSON.stringify({
          text,
          targetLanguage: outgoingLang,
          accountId,
          // Сверка вторым проходом: перевод уходит живому клиенту, поэтому
          // модель ещё раз переводит его обратно и правит, если смысл ушёл.
          // Для потока входящих сверка не включается — см. шлюз.
          verify: true,
          sourceLanguage: chatTranslationLang
        })
      });
      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; text?: string; message?: string; back?: string | null; corrected?: boolean }
        | null;
      if (!response.ok || !payload?.ok || typeof payload.text !== "string") {
        setOutgoingTranslationError(
          response.status === 503
            ? "Переводчик не настроен. Включите интеграцию «Переводчик» в Настройках."
            : response.status === 502
              ? `Провайдер перевода вернул ошибку (${payload?.message ?? "нет деталей"}).`
              : (payload?.message ?? "Не удалось перевести текст.")
        );
        return;
      }
      const translated = payload.text.trim();
      if (translated.length === 0) {
        setOutgoingTranslationError("Провайдер вернул пустой перевод — попробуйте ещё раз.");
        return;
      }
      // Кладём оригинал в undo-стек композера: Ctrl/Cmd+Z работает как везде.
      const history = draftHistoryRef.current;
      history.past.push({ value: draft, start: draft.length, end: draft.length });
      history.future = [];
      history.lastEditAt = 0;
      setOutgoingTranslated({
        original: draft,
        translated,
        back: typeof payload.back === "string" && payload.back.trim().length > 0
          ? payload.back.trim()
          : null,
        corrected: payload.corrected === true
      });
      setDraft(translated);
      requestAnimationFrame(() => {
        const target = composerTextareaRef.current;
        if (!target) {
          return;
        }
        target.focus();
        target.setSelectionRange(translated.length, translated.length);
      });
    } catch (cause) {
      setOutgoingTranslationError(
        cause instanceof Error ? cause.message : "Сетевая ошибка при переводе."
      );
    } finally {
      setIsTranslatingOutgoing(false);
    }
  };

  // Страховка Ctrl/Cmd+Shift+Enter — жалоба саппортов после редизайна: локальный
  // onKeyDown у textarea иногда не приходит (мы точной причины не поймали;
  // возможно, extension/скрин-ридер съедает событие в bubbling). Здесь ловим
  // на window: если фокус в композере — тем же путём, что и textarea handler.
  // Ref-паттерн — чтобы не пересобирать effect на каждый рендер (translateDraftForSending
  // без useCallback, ссылка меняется).
  const translateDraftRef = useRef(translateDraftForSending);
  translateDraftRef.current = translateDraftForSending;
  useEffect(() => {
    if (!selectedChatId) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (
        event.key !== "Enter" ||
        !event.shiftKey ||
        !(event.ctrlKey || event.metaKey) ||
        event.altKey
      ) {
        return;
      }
      // Если textarea handler уже отработал — не дублируем.
      if (event.defaultPrevented) return;
      // И только если фокус реально в композере — не тревожим модалки/пикеры.
      if (document.activeElement !== composerTextareaRef.current) return;
      event.preventDefault();
      void translateDraftRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedChatId]);

  const createTrustAsset = async (): Promise<void> => {
    if (!accountId) {
      setTrustError("Аккаунт не выбран.");
      return;
    }

    const title = trustTitle.trim();
    if (title.length === 0) {
      setTrustError("Укажите название шаблона.");
      return;
    }

    if (trustMediaKind === "link") {
      const url = trustLinkUrl.trim();
      if (!/^https?:\/\//i.test(url)) {
        setTrustError("URL должен начинаться с http:// или https://");
        return;
      }
      setIsSavingTrustAsset(true);
      setTrustError(null);
      try {
        const response = await internalApi.createTrustAsset(
          {
            accountId,
            title,
            ...(trustDescription.trim().length > 0
              ? { description: trustDescription.trim() }
              : {}),
            ...(trustSupportDescription.trim().length > 0
              ? { supportDescription: trustSupportDescription.trim() }
              : {}),
            mediaKind: "link",
            content: { kind: "text", text: url },
            ...(trustCreateSectionId ? { sectionId: trustCreateSectionId } : {})
          },
          wsUrl
        );
        setTrustAssets((current) => [response.asset, ...current]);
        setTrustTitle("");
        setTrustDescription("");
        setTrustSupportDescription("");
        setTrustLinkUrl("");
      } catch (cause) {
        setTrustError(
          cause instanceof Error ? cause.message : "Не удалось создать шаблон-ссылку."
        );
      } finally {
        setIsSavingTrustAsset(false);
      }
      return;
    }

    if (trustMediaKind === "payment") {
      const text = trustPaymentText.trim();
      if (text.length < 4) {
        setTrustError("Введите реквизит (номер карты, кошелька, IBAN и т.п.).");
        return;
      }
      // К реквизиту можно приложить фото (например, QR или скрин реквизитов):
      // тогда content = фото с реквизитом в caption — антифрод-фильтр gateway
      // сверяет caption у payment-шаблонов.
      if (trustAttachments.some((attachment) => attachment.kind !== "photo")) {
        setTrustError("К реквизиту можно прикрепить только фото.");
        return;
      }
      setIsSavingTrustAsset(true);
      setTrustError(null);
      try {
        const contents: MessageContent[] = [];
        for (const [index, attachment] of trustAttachments.entries()) {
          const uploaded = await mediaApi.uploadFile(wsUrl, attachment.file, attachment.kind);
          contents.push(
            buildAttachmentContent(attachment, uploaded, index === 0 ? text : "")
          );
        }
        const response = await internalApi.createTrustAsset(
          {
            accountId,
            title,
            ...(trustDescription.trim().length > 0
              ? { description: trustDescription.trim() }
              : {}),
            ...(trustSupportDescription.trim().length > 0
              ? { supportDescription: trustSupportDescription.trim() }
              : {}),
            mediaKind: "payment",
            content: contents[0] ?? { kind: "text", text },
            ...(contents.length > 1 ? { extraContents: contents.slice(1) } : {}),
            ...(contents.length > 1 ? { groupMedia: trustGroupMedia } : {}),
            ...(trustCreateSectionId ? { sectionId: trustCreateSectionId } : {})
          },
          wsUrl
        );
        setTrustAssets((current) => [response.asset, ...current]);
        setTrustTitle("");
        setTrustDescription("");
        setTrustSupportDescription("");
        setTrustPaymentText("");
        resetTrustAttachments();
      } catch (cause) {
        setTrustError(
          cause instanceof Error ? cause.message : "Не удалось создать шаблон-реквизит."
        );
      } finally {
        setIsSavingTrustAsset(false);
      }
      return;
    }

    if (trustAttachments.length === 0) {
      setTrustError("Сначала выберите файл.");
      return;
    }

    // Несколько файлов = альбом Telegram: в него входят только фото/видео.
    if (
      trustAttachments.length > 1 &&
      trustAttachments.some(
        (attachment) => attachment.kind !== "photo" && attachment.kind !== "video"
      )
    ) {
      setTrustError("Несколько файлов в одном шаблоне — только фото/видео (альбом).");
      return;
    }

    // Без валидной длительности TDLib получит duration=0 и Telegram обрежет
    // воспроизведение у получателя — такой шаблон сломан навсегда.
    const brokenVoice = trustAttachments.find(
      (attachment) =>
        (attachment.kind === "voice" || attachment.kind === "videoNote") &&
        !(attachment.durationSeconds && attachment.durationSeconds > 0)
    );
    if (brokenVoice) {
      setTrustError(
        "Не удалось определить длительность аудио — шаблон пришёл бы обрезанным. " +
          "Конвертируйте файл в OGG/MP3 и загрузите заново."
      );
      return;
    }

    setIsSavingTrustAsset(true);
    setTrustError(null);
    try {
      const contents: MessageContent[] = [];
      for (const [index, attachment] of trustAttachments.entries()) {
        const uploaded = await mediaApi.uploadFile(wsUrl, attachment.file, attachment.kind);
        // Подпись — только у первого контента: у альбома Telegram показывает
        // общую подпись, если она стоит на одном сообщении группы.
        contents.push(
          buildAttachmentContent(
            attachment,
            uploaded,
            index === 0 ? trustDescription.trim() : ""
          )
        );
      }
      const primary = trustAttachments[0]!;
      const response = await internalApi.createTrustAsset(
        {
          accountId,
          title,
          ...(trustDescription.trim().length > 0
            ? { description: trustDescription.trim() }
            : {}),
          ...(trustSupportDescription.trim().length > 0
            ? { supportDescription: trustSupportDescription.trim() }
            : {}),
          mediaKind: trustMediaKind,
          content: contents[0]!,
          ...(contents.length > 1 ? { extraContents: contents.slice(1) } : {}),
          ...(contents.length > 1 ? { groupMedia: trustGroupMedia } : {}),
          ...(trustCreateSectionId ? { sectionId: trustCreateSectionId } : {})
        },
        wsUrl
      );
      setTrustAssets((current) => [response.asset, ...current]);
      setTrustTitle("");
      setTrustDescription("");
      setTrustSupportDescription("");
      setTrustMediaKind(primary.kind);
      resetTrustAttachments();
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось создать шаблон."
      );
    } finally {
      setIsSavingTrustAsset(false);
    }
  };

  const deleteTrustAsset = async (assetId: string): Promise<void> => {
    if (!accountId) {
      return;
    }

    try {
      await internalApi.deleteTrustAsset(accountId, assetId, wsUrl);
      setTrustAssets((current) => current.filter((asset) => asset.assetId !== assetId));
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось удалить шаблон."
      );
    }
  };

  // Отправка шаблона: мультифото уходит альбомом (chat.sendAlbum), одиночный
  // контент — обычным sendMessage. contentOverride — отредактированный перед
  // отправкой первый контент (из пред-отправочного редактора).
  // Защита от даблклика по «Отправить» шаблона: второй клик в том же тике
  // отправлял шаблон повторно (два реальных сообщения). Снимается тиком позже.
  // Оптимистичная отметка «использовали»: сервер пишет её в тот же момент, но
  // ждать ответа ради плашки незачем — панель закрывается сразу после отправки.
  const markTrustAssetUsedLocally = (assetId: string): void => {
    if (!selectedChatId) {
      return;
    }
    const now = new Date().toISOString();
    setTrustUsage((current) => {
      const previous = current[assetId];
      return {
        ...current,
        [assetId]: {
          assetId,
          chatId: selectedChatId,
          usedCount: (previous?.usedCount ?? 0) + 1,
          source: previous?.source ?? "auto",
          firstUsedAt: previous?.firstUsedAt ?? now,
          lastUsedAt: now
        }
      };
    });
  };

  // Ручная пометка: нужна, если автоматика не сработала или чат обрабатывали
  // до появления фичи. Снятие — тем же кликом.
  const toggleTrustAssetUsed = async (assetId: string, used: boolean): Promise<void> => {
    if (!accountId || !selectedChatId) {
      return;
    }
    setTrustUsageBusyId(assetId);
    try {
      const { usage } = await internalApi.setTrustAssetUsage(
        { accountId, chatId: selectedChatId, assetId, used },
        wsUrl
      );
      setTrustUsage(Object.fromEntries(usage.map((entry) => [entry.assetId, entry])));
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось изменить пометку шаблона."
      );
    } finally {
      setTrustUsageBusyId(null);
    }
  };

  const trustAssetSendingRef = useRef(false);
  const [trustAssetSending, setTrustAssetSending] = useState(false);
  const sendTrustAsset = (
    asset: InternalTrustAssetRecord,
    contentOverride?: MessageContent
  ): void => {
    if (trustAssetSendingRef.current || !selectedChatId) {
      return;
    }
    trustAssetSendingRef.current = true;
    setTrustAssetSending(true);
    window.setTimeout(() => {
      trustAssetSendingRef.current = false;
      setTrustAssetSending(false);
    }, 700);
    const extra = asset.extraContents ?? [];
    if (extra.length > 0 && asset.groupMedia !== false) {
      sendAlbum(selectedChatId, [contentOverride ?? asset.content, ...extra], asset.assetId);
    } else if (extra.length > 0) {
      // Шаблон с «Группировать: выкл» — раздельные сообщения; каждое несёт
      // trustAssetId, bypass в gateway сверяет контент с любым из шаблонных.
      for (const content of [contentOverride ?? asset.content, ...extra]) {
        sendMessage(selectedChatId, content, undefined, undefined, asset.assetId);
      }
    } else {
      sendMessage(selectedChatId, contentOverride ?? asset.content, undefined, undefined, asset.assetId);
    }
    markTrustAssetUsedLocally(asset.assetId);
    // Панель шаблонов перекрывает ленту чата — закрываем, чтобы отправленное
    // сообщение сразу было видно.
    setActivePanel(null);
    focusChatComposer();
  };

  const openTrustEdit = (asset: InternalTrustAssetRecord): void => {
    const allContents = [asset.content, ...(asset.extraContents ?? [])];
    setTrustEdit({
      asset,
      title: asset.title,
      description: asset.description ?? "",
      supportDescription: asset.supportDescription ?? "",
      textValue:
        asset.content.kind === "text"
          ? asset.content.text
          : ((asset.content as { caption?: string }).caption ?? ""),
      keptContents:
        asset.mediaKind === "link"
          ? []
          : allContents.filter((content) => content.kind !== "text"),
      newAttachments: [],
      groupMedia: asset.groupMedia !== false
    });
    setTrustMenu(null);
  };

  const saveTrustEdit = async (): Promise<void> => {
    if (!trustEdit || !accountId) {
      return;
    }
    const { asset } = trustEdit;
    const title = trustEdit.title.trim();
    if (title.length === 0) {
      setTrustError("Укажите название шаблона.");
      return;
    }

    setIsSavingTrustEdit(true);
    setTrustError(null);
    try {
      let content: MessageContent;
      let extraContents: MessageContent[] = [];
      let mediaKind = asset.mediaKind;

      if (asset.mediaKind === "link") {
        const url = trustEdit.textValue.trim();
        if (!/^https?:\/\//i.test(url)) {
          throw new Error("URL должен начинаться с http:// или https://");
        }
        content = { kind: "text", text: url };
      } else {
        // Медиа: оставленные прежние контенты + новые файлы (загружаем сейчас).
        if (
          trustEdit.newAttachments.some(
            (attachment) =>
              attachment.kind === "voice" &&
              !(attachment.durationSeconds && attachment.durationSeconds > 0)
          )
        ) {
          throw new Error(
            "Не удалось определить длительность аудио — конвертируйте файл в OGG/MP3."
          );
        }
        const uploadedContents: MessageContent[] = [];
        for (const attachment of trustEdit.newAttachments) {
          const uploaded = await mediaApi.uploadFile(wsUrl, attachment.file, attachment.kind);
          uploadedContents.push(buildAttachmentContent(attachment, uploaded, ""));
        }
        const mediaContents = [...trustEdit.keptContents, ...uploadedContents];

        if (asset.mediaKind === "payment") {
          const text = trustEdit.textValue.trim();
          if (text.length < 4) {
            throw new Error("Введите реквизит (номер карты, кошелька, IBAN и т.п.).");
          }
          if (mediaContents.some((entry) => entry.kind !== "photo")) {
            throw new Error("К реквизиту можно прикрепить только фото.");
          }
          const [first, ...rest] = mediaContents;
          // Реквизит живёт в caption первого фото (или в text-контенте, если
          // фото нет) — это же поле сверяет антифрод-фильтр gateway.
          content = first ? applyContentCaption(first, text) : { kind: "text", text };
          extraContents = rest;
        } else {
          if (mediaContents.length === 0) {
            throw new Error("В шаблоне должен остаться хотя бы один файл.");
          }
          if (
            mediaContents.length > 1 &&
            mediaContents.some(
              (entry) => entry.kind !== "photo" && entry.kind !== "video"
            )
          ) {
            throw new Error(
              "Несколько файлов в одном шаблоне — только фото/видео (альбом)."
            );
          }
          const [first, ...rest] = mediaContents;
          content = applyContentCaption(first!, trustEdit.description.trim());
          extraContents = rest;
          mediaKind =
            content.kind === "photo" ||
            content.kind === "video" ||
            content.kind === "voice" ||
            content.kind === "videoNote" ||
            content.kind === "document"
              ? content.kind
              : asset.mediaKind;
        }
      }

      const response = await internalApi.updateTrustAsset(
        {
          accountId,
          assetId: asset.assetId,
          title,
          description: trustEdit.description.trim(),
          supportDescription: trustEdit.supportDescription.trim(),
          mediaKind,
          content,
          extraContents,
          groupMedia: trustEdit.groupMedia
        },
        wsUrl
      );
      setTrustAssets((current) =>
        current.map((entry) =>
          entry.assetId === response.asset.assetId ? response.asset : entry
        )
      );
      trustEdit.newAttachments.forEach((attachment) => revokeAttachmentPreview(attachment));
      setTrustEdit(null);
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось сохранить шаблон."
      );
    } finally {
      setIsSavingTrustEdit(false);
    }
  };

  // Перемещение/копирование выбранных шаблонов. target: null = «Без раздела»,
  // "keep" = оставить раздел оригинала (только для копирования).
  const applyTrustSectionPick = async (target: string | null | "keep"): Promise<void> => {
    if (!accountId || !trustSectionPicker) {
      return;
    }
    const { mode, assetIds } = trustSectionPicker;
    setIsTrustBulkBusy(true);
    setTrustError(null);
    try {
      if (mode === "move") {
        const sectionId = target === "keep" ? null : target;
        await internalApi.moveTrustAssets({ accountId, assetIds, sectionId }, wsUrl);
        const ids = new Set(assetIds);
        setTrustAssets((current) =>
          current.map((entry) => {
            if (!ids.has(entry.assetId)) {
              return entry;
            }
            const { sectionId: _dropped, ...rest } = entry;
            return sectionId ? { ...rest, sectionId } : rest;
          })
        );
      } else {
        const response = await internalApi.copyTrustAssets(
          {
            accountId,
            assetIds,
            ...(target === "keep" ? {} : { targetSectionId: target })
          },
          wsUrl
        );
        setTrustAssets((current) => [...response.assets, ...current]);
      }
      setTrustSectionPicker(null);
      setTrustSelectMode(false);
      setTrustSelectedIds(new Set());
    } catch (cause) {
      setTrustError(
        cause instanceof Error
          ? cause.message
          : mode === "move"
            ? "Не удалось переместить шаблоны."
            : "Не удалось скопировать шаблоны."
      );
    } finally {
      setIsTrustBulkBusy(false);
    }
  };

  const toggleTrustSelected = (assetId: string): void => {
    setTrustSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  };

  const createTrustSection = async (parentId: string | null): Promise<void> => {
    if (!accountId) {
      return;
    }
    const title = trustNewSectionTitle.trim();
    if (title.length === 0) {
      setTrustError(parentId ? "Укажите название подраздела." : "Укажите название раздела.");
      return;
    }
    setIsSavingTrustSection(true);
    setTrustError(null);
    try {
      const response = await internalApi.createTemplateSection(
        { accountId, title, ...(parentId ? { parentId } : {}) },
        wsUrl
      );
      setTrustSections((current) => [...current, response.section]);
      setTrustNewSectionTitle("");
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось создать раздел."
      );
    } finally {
      setIsSavingTrustSection(false);
    }
  };

  const renameTrustSection = async (
    section: InternalTemplateSectionRecord
  ): Promise<void> => {
    const next = window.prompt("Новое название", section.title)?.trim();
    if (!next || next === section.title || !accountId) {
      return;
    }
    setTrustError(null);
    try {
      const response = await internalApi.renameTemplateSection(
        { accountId, sectionId: section.sectionId, title: next },
        wsUrl
      );
      setTrustSections((current) =>
        current.map((entry) =>
          entry.sectionId === response.section.sectionId ? response.section : entry
        )
      );
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось переименовать раздел."
      );
    }
  };

  const deleteTrustSection = async (
    section: InternalTemplateSectionRecord
  ): Promise<void> => {
    const isRoot = section.parentId === null;
    const ok = window.confirm(
      isRoot
        ? `Удалить раздел «${section.title}» вместе с его подразделами? Шаблоны не удалятся — они попадут в «Без раздела».`
        : `Удалить подраздел «${section.title}»? Шаблоны не удалятся — они попадут в «Без раздела».`
    );
    if (!ok || !accountId) {
      return;
    }
    setTrustError(null);
    try {
      await internalApi.deleteTemplateSection(accountId, section.sectionId, wsUrl);
      const removedIds = new Set(
        trustSections
          .filter(
            (entry) =>
              entry.sectionId === section.sectionId ||
              entry.parentId === section.sectionId
          )
          .map((entry) => entry.sectionId)
      );
      setTrustSections((current) =>
        current.filter((entry) => !removedIds.has(entry.sectionId))
      );
      // Локально отвязываем шаблоны от удалённых секций (сервер сделал то же).
      setTrustAssets((current) =>
        current.map((asset) =>
          asset.sectionId && removedIds.has(asset.sectionId)
            ? (({ sectionId: _dropped, ...rest }) => rest)(asset)
            : asset
        )
      );
      if (trustCreateSectionId && removedIds.has(trustCreateSectionId)) {
        setTrustCreateSectionId("");
      }
      setTrustNav(
        isRoot
          ? { mode: "browse", sectionId: null }
          : { mode: "browse", sectionId: section.parentId }
      );
    } catch (cause) {
      setTrustError(
        cause instanceof Error ? cause.message : "Не удалось удалить раздел."
      );
    }
  };

  const startVoiceRecording = async (): Promise<void> => {
    if (!selectedChatId) {
      return;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setComposerError("Запись голоса недоступна в этом браузере.");
      return;
    }

    try {
      setComposerError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      const mimeType = pickVoiceRecordingMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();
      recordingMimeTypeRef.current = recorder.mimeType || mimeType || "audio/ogg";

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setComposerError("Не удалось записать голосовое сообщение.");
        stopRecording();
      };

      recorder.onstop = async () => {
        const chunks = [...mediaChunksRef.current];
        const mime = recordingMimeTypeRef.current || recorder.mimeType || "audio/ogg";
        const recordedSeconds =
          recordingStartedAtRef.current != null
            ? Math.max(1, Math.round((Date.now() - recordingStartedAtRef.current) / 1000))
            : 0;
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
        mediaStreamRef.current = null;
        mediaChunksRef.current = [];
        recordingStartedAtRef.current = null;
        recordingMimeTypeRef.current = "";
        setIsRecordingVoice(false);
        setRecordingElapsedSeconds(0);

        if (chunks.length === 0) {
          return;
        }

        const blob = new Blob(chunks, { type: mime });
        const extension = extensionFromMimeType(mime);
        const file = new File([blob], `voice-note-${Date.now()}${extension}`, { type: mime });
        const previewUrl = URL.createObjectURL(file);
        const metadata = await readAttachmentMetadata(file, "voice", previewUrl);
        // metadata.durationSeconds now comes from the decoded AudioBuffer
        // (exact, rounded up). Wall-clock recordedSeconds is only the last
        // resort if decoding failed entirely — anything > 0 keeps Telegram
        // from clipping the voice note.
        const durationSeconds =
          metadata.durationSeconds && metadata.durationSeconds > 0
            ? metadata.durationSeconds
            : recordedSeconds;
        setPendingAttachments((previous) => {
          previous.forEach((attachment) => revokeAttachmentPreview(attachment));
          return [
            {
              file,
              kind: "voice",
              previewUrl,
              ...metadata,
              ...(durationSeconds > 0 ? { durationSeconds } : {})
            }
          ];
        });
      };

      recorder.start();
      setPendingAttachments((previous) => {
        previous.forEach((attachment) => revokeAttachmentPreview(attachment));
        return [];
      });
      setIsRecordingVoice(true);
      setRecordingElapsedSeconds(0);
    } catch (cause) {
      setComposerError(
        cause instanceof Error ? cause.message : "Не удалось получить доступ к микрофону."
      );
      stopRecording();
    }
  };

  const recordingLabel = `${String(Math.floor(recordingElapsedSeconds / 60)).padStart(2, "0")}:${String(
    recordingElapsedSeconds % 60
  ).padStart(2, "0")}`;

  return (
    <section className="chat-view">
      {copyToast ? (
        <div className="copy-toast copy-toast--chat" role="status">
          <i className="ic ic-check" aria-hidden="true" /> {copyToast}
        </div>
      ) : null}
      <MediaViewer />
      {sendMenu && selectedChatId
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setSendMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setSendMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={sendMenu.x} anchorY={sendMenu.y}>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    setSendMenu(null);
                    setSchedulePickerOpen(true);
                  }}
                >
                  <i className="ic ic-schedule" aria-hidden="true" />
                  Отправить позже
                </button>
                {selectedChat && !selectedChat.isGroupChat && !selectedChat.isSavedMessages ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      setSendMenu(null);
                      scheduleRef.current = { whenOnline: true };
                      composerFormRef.current?.requestSubmit();
                    }}
                    title="Telegram отправит, как только собеседник появится в сети"
                  >
                    <i className="ic ic-clock" aria-hidden="true" />
                    Отправить, когда будет в сети
                  </button>
                ) : null}
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {schedulePickerOpen && selectedChatId ? (
        <SchedulePickerModal
          timeZone={timeZone}
          allowWhenOnline={Boolean(
            selectedChat && !selectedChat.isGroupChat && !selectedChat.isSavedMessages
          )}
          onClose={() => setSchedulePickerOpen(false)}
          onSubmit={(result) => {
            setSchedulePickerOpen(false);
            scheduleRef.current = result;
            composerFormRef.current?.requestSubmit();
          }}
        />
      ) : null}
      {scheduledWindowOpen && selectedChatId ? (
        <ScheduledMessagesModal
          chatId={selectedChatId}
          chatTitle={selectedChat?.title ?? ""}
          onClose={() => setScheduledWindowOpen(false)}
        />
      ) : null}
      {selectedChatId ? (
        <button
          ref={translatorFabRef}
          type="button"
          className={
            translatorOpen
              ? "chat-view__translator-fab chat-view__translator-fab--active"
              : "chat-view__translator-fab"
          }
          style={{
            right: `${translatorFabPos.right}px`,
            top:
              translatorFabPos.top !== null
                ? `${translatorFabPos.top}px`
                : undefined,
            transform: translatorFabPos.top !== null ? "none" : undefined
          }}
          onPointerDown={(event) => {
            if (event.button !== 0) {
              return;
            }
            translatorFabDragRef.current = {
              startX: event.clientX,
              startY: event.clientY,
              startRight: translatorFabPos.right,
              startTop:
                translatorFabPos.top ??
                // rect приводим к визуальным px (в Safari он логический),
                // затем в логические — позиция кнопки задаётся в них.
                (translatorFabRef.current
                  ? visualRect(translatorFabRef.current).top / uiScale
                  : 0),
              moved: false
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const drag = translatorFabDragRef.current;
            if (!drag) {
              return;
            }
            // Курсор даёт визуальные px, позиция кнопки — логические:
            // при UI-scale ≠ 1 кнопка иначе «убегает» от курсора.
            const dx = (event.clientX - drag.startX) / uiScale;
            const dy = (event.clientY - drag.startY) / uiScale;
            if (!drag.moved && Math.hypot(dx, dy) * uiScale < 4) {
              return;
            }
            drag.moved = true;
            // right растёт влево, поэтому вычитаем dx.
            const nextRight = Math.max(
              4,
              Math.min(window.innerWidth / uiScale - 42, drag.startRight - dx)
            );
            const nextTop = Math.max(
              4,
              Math.min(window.innerHeight / uiScale - 42, drag.startTop + dy)
            );
            setTranslatorFabPos({ right: nextRight, top: nextTop });
          }}
          onPointerUp={(event) => {
            const drag = translatorFabDragRef.current;
            translatorFabDragRef.current = null;
            try {
              event.currentTarget.releasePointerCapture(event.pointerId);
            } catch {
              // already released
            }
            if (drag?.moved) {
              try {
                window.localStorage.setItem(
                  "tg-client:translator-fab-pos",
                  JSON.stringify(translatorFabPos)
                );
              } catch {
                // storage full / blocked — ignore
              }
              return;
            }
            setTranslatorOpen((value) => !value);
          }}
          aria-label="Мини-переводчик"
          title="Переводчик (OpenRouter). Перетащи, если перекрывает «прочитано»."
        >
          文A
        </button>
      ) : null}
      {selectedChatId && pinnedMessage ? (
        <div className="chat-pinned-message">
          <button
            type="button"
            className="chat-pinned-message__summary"
            onClick={() => scrollToMessage(pinnedMessage.id)}
          >
            <span className="chat-pinned-message__label">Закреплено</span>
            <span className="chat-pinned-message__text">
              <strong>{messageActorLabel(pinnedMessage)}</strong>{" "}
              {pinnedMessage.text || "Медиа"}
            </span>
          </button>
          <button
            type="button"
            className="chat-pinned-message__close"
            onClick={() => toggleMessagePinned(selectedChatId, pinnedMessage.id, false)}
            aria-label="Открепить"
            title="Открепить"
          >
            <i className="ic ic-close" aria-hidden="true" />
          </button>
        </div>
      ) : null}
      {selectedChatId && selectionMode ? (
        (() => {
          const selectedList = messages.filter((m) => selectedMessageIds.has(m.id));
          const copyableText = formatMessageLines(selectedList, {
            selfName: selfExportName,
            timeZone
          });
          const hasCopyable = copyableText.length > 0;
          const hasOwn = selectedList.some((m) => m.outgoing);
          const frequentTargets = frequentChats.filter(
            (entry) => entry.chatId !== selectedChatId
          );
          const frequentUnavailable = frequentTargets.length === 0;
          // Перевод — только для текстовых сообщений с непустым текстом,
          // которые ещё не переведены (иначе повторный вызов бесполезен и
          // жрёт лимит OpenRouter). Логика зеркалит контекстное меню
          // одиночного сообщения (см. кнопку «Перевести» ниже).
          const translatableIds = selectedList
            .filter(
              (m) =>
                translationSourceText(m.content).length > 0 &&
                !translationsByMessage[translationKey(selectedChatId, m.id)]
            )
            .map((m) => m.id);
          const hasTranslatable = translatableIds.length > 0;
          return (
            <div className="chat-selection-bar" role="toolbar" aria-label="Выбрано сообщений">
              <button
                type="button"
                className="icon-button chat-selection-bar__close"
                onClick={exitSelectionMode}
                aria-label="Отменить выбор"
                title="Отменить (Esc)"
              >
                <i className="ic ic-close" aria-hidden="true" />
              </button>
              <strong className="chat-selection-bar__count">
                Выбрано: {selectedMessageIds.size}
              </strong>
              <div className="chat-selection-bar__actions">
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={!hasCopyable}
                  onClick={() => {
                    void copyTextToClipboard(copyableText);
                    exitSelectionMode();
                  }}
                  title={
                    hasCopyable
                      ? "Скопировать тексты выбранных сообщений (Ctrl+C)"
                      : "У выбранных сообщений нет текста"
                  }
                >
                  <i className="ic ic-copy" aria-hidden="true" /> Копировать
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={!hasTranslatable}
                  onClick={() => {
                    for (const id of translatableIds) {
                      translateMessage(selectedChatId, id);
                    }
                    // Перевод асинхронный (LLM-запрос) — не выходим из
                    // режима, чтобы юзер видел, как переводы подтягиваются
                    // на выбранные сообщения. Хочет выйти — Esc/крестик.
                  }}
                  title={
                    hasTranslatable
                      ? `Перевести ${translatableIds.length} сообщени${
                          translatableIds.length === 1 ? "е" : "й"
                        } (язык — из селектора под композером)`
                      : "Нет подходящих текстовых сообщений для перевода"
                  }
                >
                  <i className="ic ic-translate" aria-hidden="true" /> Перевести
                </button>
                {(
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => {
                      // Пересылка выделенных пачкой: порядок — как в ленте.
                      // С 2026-09-23 доступна всем ролям для любых сообщений.
                      const forwardable = selectedList;
                      if (forwardable.length === 0) return;
                      openForwardComposition(
                        selectedChatId,
                        forwardable.map((m) => m.id)
                      );
                      setForwardPickerOpen(true);
                      exitSelectionMode();
                    }}
                    title="Переслать выбранные сообщения в другой чат"
                  >
                    <i className="ic ic-forward" aria-hidden="true" /> Переслать…
                  </button>
                )}
                {/* «В Частые группы» доступна и саппорту: это единственные
                    адресаты пересылки чужих сообщений, которые пропускает
                    гейтвей (whitelist по серверному списку аккаунта). */}
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={frequentUnavailable}
                  onClick={(event) => {
                    if (frequentUnavailable) return;
                    const rect = visualRect(event.currentTarget);
                    setFrequentMenu({
                      x: rect.left,
                      y: rect.bottom + 4,
                      messageIds: selectedList.map((m) => m.id)
                    });
                  }}
                  title={
                    frequentUnavailable
                      ? frequentChats.length === 0
                        ? "Частые группы не назначены администратором (ПКМ на диалоге → «В Частые группы»)"
                        : "Это и есть частая группа"
                      : "Переслать выбранные в одну из частых групп"
                  }
                >
                  <i className="ic ic-forward" aria-hidden="true" /> В Частые группы
                </button>
                <button
                  type="button"
                  className="button button--ghost chat-selection-bar__danger"
                  disabled={!hasOwn}
                  onClick={() => {
                    const ownIds = selectedList
                      .filter((m) => m.outgoing)
                      .map((m) => m.id);
                    if (ownIds.length === 0) return;
                    setDeleteAlsoForPeer(true);
                    setDeleteDialogMessageIds(ownIds);
                  }}
                  title={
                    hasOwn
                      ? "Удалить выбранные свои сообщения"
                      : "Удалить можно только свои сообщения"
                  }
                >
                  <i className="ic ic-delete" aria-hidden="true" /> Удалить
                </button>
              </div>
            </div>
          );
        })()
      ) : selectedChatId ? (
        <div className="chat-tools">
          <div className="chat-tools__actions">
            <button
              type="button"
              className={searchOpen ? "button button--ghost chat-tools__button chat-tools__button--active" : "button button--ghost chat-tools__button"}
              onClick={() => switchPanel("search")}
            >
              Поиск
            </button>
            <button
              type="button"
              className={tagsOpen ? "button button--ghost chat-tools__button chat-tools__button--active" : "button button--ghost chat-tools__button"}
              onClick={() => switchPanel("tags")}
            >
              Теги
            </button>
            <button
              type="button"
              className={deletedOpen ? "button button--ghost chat-tools__button chat-tools__button--active" : "button button--ghost chat-tools__button"}
              onClick={() => switchPanel("deleted")}
            >
              Удалённые
            </button>
            <button
              type="button"
              className={trustOpen ? "button button--ghost chat-tools__button chat-tools__button--active" : "button button--ghost chat-tools__button"}
              onClick={() => switchPanel("trust")}
            >
              Шаблоны
            </button>
            <button
              type="button"
              className={
                scheduledWindowOpen
                  ? "button button--ghost chat-tools__button chat-tools__button--active"
                  : "button button--ghost chat-tools__button"
              }
              onClick={() => setScheduledWindowOpen(true)}
              title="Отложенные сообщения этого чата"
            >
              Отложенные
              {scheduledMessages.length > 0 ? ` · ${scheduledMessages.length}` : ""}
            </button>
            <button
              type="button"
              className={
                trustOpen && trustFilterKind === "payment"
                  ? "button chat-tools__button chat-tools__button--payment chat-tools__button--payment-on"
                  : "button button--ghost chat-tools__button chat-tools__button--payment"
              }
              onClick={() => {
                setTrustFilterKind("payment");
                // Реквизиты нужны сразу, без хождения по разделам — плоский
                // список по всем разделам с фильтром «Реквизит».
                setTrustNav({ mode: "all" });
                if (activePanel === "search" && selectedChatId) {
                  clearSearch(selectedChatId);
                }
                setActivePanel("trust");
              }}
              title="Открыть утверждённые реквизиты (карты/кошельки/IBAN)"
            >
              <i className="ic ic-card" aria-hidden="true" /> Способы оплаты
            </button>
            <button
              type="button"
              className={
                isChatTranslated
                  ? "button chat-tools__button chat-tools__button--translate chat-tools__button--translate-on"
                  : "button button--ghost chat-tools__button chat-tools__button--translate"
              }
              onClick={() => setChatTranslation(selectedChatId, !isChatTranslated)}
              title={
                isChatTranslated
                  ? `Показать оригинальный текст всех сообщений чата (язык перевода: ${chatTranslationLang.toUpperCase()})`
                  : `Перевести весь чат на «${chatTranslationLang.toUpperCase()}» — все загруженные и новые сообщения сразу, без нажатия на каждое. Язык меняется рядом, в списке «читать».`
              }
            >
              <i className="ic ic-translate" aria-hidden="true" />{" "}
              {isChatTranslated
                ? "Показать оригинал"
                : `Перевести весь чат (${chatTranslationLang.toUpperCase()})`}
            </button>
            {isChatTranslated ? (
              <span
                className="chat-tools__translate-status"
                title={
                  chatTranslationProgress.total === 0
                    ? "В загруженных сообщениях нет текста для перевода"
                    : chatTranslationProgress.complete
                      ? "Все загруженные сообщения переведены"
                      : "Перевод загруженных сообщений в процессе"
                }
              >
                {chatTranslationProgress.total === 0
                  ? "нет текста"
                  : chatTranslationProgress.complete
                    ? `переведено ${chatTranslationProgress.done}/${chatTranslationProgress.total}`
                    : `переводим… ${chatTranslationProgress.done}/${chatTranslationProgress.total}`}
              </span>
            ) : null}
            {/* Языки перевода живут здесь, а не в композере: там они съедали
                ширину у самого поля ввода (жалоба 2026-09-08). «читать» —
                язык, на который переводятся входящие; «писать» — язык
                клиента, на который переводится ваш черновик значком в поле. */}
            <span className="chat-tools__langs">
              <label className="chat-tools__lang">
                читать
                <select
                  value={chatTranslationLang}
                  onChange={(event) => setChatTranslationLanguage(selectedChatId, event.target.value)}
                  aria-label="Язык, на который переводятся входящие"
                  title="Язык, на который переводятся входящие сообщения при включённом «Перевести весь чат»"
                >
                  {translateLanguages.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                  {translateLanguages.every((option) => option.code !== chatTranslationLang) ? (
                    <option value={chatTranslationLang}>{chatTranslationLang.toUpperCase()}</option>
                  ) : null}
                </select>
              </label>
              <label className="chat-tools__lang">
                писать
                <select
                  value={outgoingLang}
                  onChange={(event) => setOutgoingLanguage(selectedChatId, event.target.value)}
                  disabled={isTranslatingOutgoing}
                  aria-label="Язык клиента: на него переводится ваш черновик"
                  title="Язык клиента. На него переводится текст из поля ввода по значку перевода. Выбирается один раз — запоминается для чата и аккаунта."
                >
                  {outgoingLang.length === 0 ? <option value="">— выбрать —</option> : null}
                  {translateLanguages.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                  {outgoingLang.length > 0 &&
                  translateLanguages.every((option) => option.code !== outgoingLang) ? (
                    <option value={outgoingLang}>{outgoingLang.toUpperCase()}</option>
                  ) : null}
                </select>
              </label>
            </span>
            {/* Ручной добор: если часть сообщений так и осталась без перевода
                (провайдер отвечал ошибкой, порвалась сеть), эта кнопка
                сбрасывает счётчик попыток и ставит остаток в очередь заново.
                Раньше единственным способом было выключить и включить перевод
                у чата. */}
            {isChatTranslated &&
            chatTranslationProgress.total > 0 &&
            !chatTranslationProgress.complete ? (
              <button
                type="button"
                className="button button--ghost chat-tools__button"
                onClick={() => retranslateMissing(selectedChatId)}
                title="Перевести те сообщения, которые остались без перевода"
              >
                <i className="ic ic-translate" aria-hidden="true" /> Добрать
              </button>
            ) : null}
            <button
              type="button"
              className="button button--ghost chat-tools__button"
              onClick={() => setExportOpen(true)}
              title="Скопировать или сохранить в .txt весь чат или последние N сообщений"
            >
              <i className="ic ic-download" aria-hidden="true" /> Экспорт
            </button>
            <button
              type="button"
              className="button button--ghost chat-tools__button chat-tools__button--close"
              onClick={() => selectChat(null)}
              aria-label="Закрыть чат"
              title="Закрыть чат (ESC)"
            >
              <i className="ic ic-close" aria-hidden="true" /> Закрыть
            </button>
          </div>
          {selectedChat?.isForum && forumTopics === null ? (
            <div className="chat-tools__topics-empty">Загружаем темы форума…</div>
          ) : null}
          {selectedChat?.isForum && forumTopics && forumTopics.length === 0 ? (
            <div className="chat-tools__topics-empty">
              Темы форума не найдены или у вас нет доступа.
            </div>
          ) : null}
          {selectedChat?.isForum && forumTopics && forumTopics.length > 0 ? (
            <div className="chat-tools__topics" role="tablist" aria-label="Темы форума">
              <button
                type="button"
                role="tab"
                aria-selected={!activeForumTopic}
                className={
                  !activeForumTopic
                    ? "chat-tools__topic chat-tools__topic--active"
                    : "chat-tools__topic"
                }
                onClick={() => setActiveForumTopic(selectedChatId, null)}
              >
                Все темы
              </button>
              {forumTopics.map((topic) => (
                <button
                  key={topic.messageThreadId}
                  type="button"
                  role="tab"
                  aria-selected={activeForumTopic === topic.messageThreadId}
                  className={
                    activeForumTopic === topic.messageThreadId
                      ? "chat-tools__topic chat-tools__topic--active"
                      : "chat-tools__topic"
                  }
                  onClick={() => setActiveForumTopic(selectedChatId, topic.messageThreadId)}
                  title={topic.isClosed ? "Тема закрыта" : undefined}
                >
                  {topic.iconEmoji ? `${topic.iconEmoji} ` : ""}{topic.name}
                  {topic.unreadCount > 0 ? (
                    <span className="chat-tools__topic-unread">{topic.unreadCount}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
          {searchOpen ? (
            <form
              className="chat-search"
              onSubmit={(event) => {
                event.preventDefault();
                searchMessages(selectedChatId, searchQuery, searchScope, 30);
              }}
            >
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchScope === "messages" ? "Найти по тексту" : "Найти документы"}
              />
              <div className="chat-search__scopes">
                <button
                  type="button"
                  className={
                    searchScope === "messages"
                      ? "chat-search__scope chat-search__scope--active"
                      : "chat-search__scope"
                  }
                  onClick={() => setSearchScope("messages")}
                >
                  Сообщения
                </button>
                <button
                  type="button"
                  className={
                    searchScope === "files"
                      ? "chat-search__scope chat-search__scope--active"
                      : "chat-search__scope"
                  }
                  onClick={() => setSearchScope("files")}
                >
                  Файлы
                </button>
              </div>
              <button type="submit" className="button button--primary">
                Найти
              </button>
              <button
                type="button"
                className="chat-search__close"
                onClick={() => {
                  if (selectedChatId) {
                    clearSearch(selectedChatId);
                  }
                  setActivePanel(null);
                }}
                aria-label="Закрыть поиск"
                title="Закрыть поиск"
              >
                <i className="ic ic-close" aria-hidden="true" />
              </button>
            </form>
          ) : null}
          {searchOpen && searchState ? (
            <div className="chat-search-results">
              <div className="chat-search-results__topline">
                <span>
                  {searchState.isLoading
                    ? "Ищем…"
                    : `Найдено: ${searchState.results.length}`}
                </span>
                <span>
                  {searchState.scope === "files" ? "Документы" : "Сообщения"}
                </span>
              </div>
              {searchState.results.length > 0 ? (
                <div className="chat-search-results__list">
                  {searchState.results.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      className="chat-search-results__item"
                      onClick={() => scrollToMessage(result.id)}
                    >
                      <div className="chat-search-results__row">
                        <strong>{result.senderName ?? (result.outgoing ? "Вы" : "Сообщение")}</strong>
                        {result.occurredAt ? (
                          <span
                            className="chat-search-results__time"
                            title={formatFullDateTime(result.occurredAt, timeZone)}
                          >
                            {formatDialogTime(result.occurredAt, timeZone)}
                          </span>
                        ) : null}
                      </div>
                      <span>{result.text}</span>
                    </button>
                  ))}
                </div>
              ) : !searchState.isLoading ? (
                <div className="chat-search-results__empty">Совпадений пока нет.</div>
              ) : null}
            </div>
          ) : null}
          {tagsOpen ? (
            <form
              className="chat-inspector"
              onSubmit={(event) => {
                event.preventDefault();
                void saveInternalTags();
              }}
            >
              <div className="chat-inspector__header">
                <div>
                  <strong>Теги чата</strong>
                  <span>Через запятую. Видны в поиске по списку.</span>
                </div>
                {selectedChat && selectedChat.internalTags.length > 0 ? (
                  <div className="chat-inspector__tags">
                    {selectedChat.internalTags.map((tag) => (
                      <span key={tag} className="chat-inspector__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="chat-inspector__controls">
                <input
                  value={internalTagsInput}
                  onChange={(event) => setInternalTagsInput(event.target.value)}
                  placeholder="Оскорбляет, Пропал, VIP"
                  disabled={isSavingTags}
                />
                <button type="submit" className="button button--primary" disabled={isSavingTags}>
                  {isSavingTags ? "Сохраняем…" : "Сохранить"}
                </button>
              </div>
              {tagsError ? <div className="chat-inspector__error">{tagsError}</div> : null}
            </form>
          ) : null}
          {deletedOpen ? (
            <div className="deleted-store">
              <div className="deleted-store__header">
                <div>
                  <strong>Удалённые сообщения</strong>
                  <span>Сохраняются перед удалением и видны только здесь.</span>
                </div>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => void loadDeletedMessages({ force: true })}
                  disabled={isDeletedLoading}
                >
                  {isDeletedLoading && deletedMessages.length > 0 ? "Обновляем…" : "Обновить"}
                </button>
              </div>
              {deletedError ? <div className="chat-inspector__error">{deletedError}</div> : null}
              {isDeletedLoading && deletedMessages.length === 0 ? (
                <div className="deleted-store__empty">Загружаем…</div>
              ) : deletedMessages.length === 0 ? (
                <div className="deleted-store__empty">Здесь пока пусто.</div>
              ) : (
                <div className="deleted-store__list">
                  {deletedMessages.map((record) => (
                    <div key={record.eventId} className="deleted-store__item">
                      <div className="deleted-store__meta">
                        <strong>
                          {record.payload.senderName ??
                            (record.payload.outgoing ? "Наше сообщение" : "Сообщение")}
                        </strong>
                        <span>
                          Отправлено {formatShortDateTime(record.payload.occurredAt, timeZone)} ·
                          удалено {formatShortDateTime(record.deletedAt, timeZone)}
                        </span>
                      </div>
                      {record.payload.outgoing && record.payload.internalAuthorName ? (
                        <div className="message-bubble__internal-author message-bubble__internal-author--deleted">
                          {record.payload.internalAuthorName}
                        </div>
                      ) : null}
                      {record.payload.replyTo ? (
                        <div className="message-reply">
                          <strong>{record.payload.replyTo.senderName ?? "Ответ"}</strong>
                          <span>{record.payload.replyTo.text}</span>
                        </div>
                      ) : null}
                      <div className="deleted-store__content">
                        {renderMessageContent(record.payload.content, {
                          senderLabel: record.payload.senderName ?? "Удалённое",
                          onOpenMedia: (payload) =>
                            openMediaViewer([
                              {
                                kind: payload.kind,
                                url: payload.url,
                                ...(payload.mimeType ? { mimeType: payload.mimeType } : {}),
                                ...(payload.caption ? { caption: payload.caption } : {}),
                                senderLabel: payload.senderLabel
                              }
                            ])
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Обёртка нужна кнопке «вниз»: она позиционируется абсолютом поверх
          ленты (как в Telegram), а не sticky внутри скролл-контента —
          sticky в flex-колонке с виртуализатором прыгал по высоте. */}
      <div className="chat-body-wrap">
      {selectedChatId && floatingDayLabel ? (
        <div
          className={
            isFloatingDayVisible
              ? "floating-day floating-day--visible"
              : "floating-day"
          }
          aria-hidden="true"
        >
          {floatingDayLabel}
        </div>
      ) : null}
      <div
        ref={bodyRef}
        className={isDragOver ? "chat-view__body chat-view__body--drop" : "chat-view__body"}
        onDragEnter={handleBodyDragEnter}
        onDragLeave={handleBodyDragLeave}
        onDragOver={handleBodyDragOver}
        onDrop={handleBodyDrop}
        onMouseUp={(event) => {
          // Если клик не на интерактивный элемент и нет активного выделения — переводим
          // фокус на поле ввода, чтобы саппорт сразу мог печатать после клика в чат.
          if (window.getSelection()?.toString().length) {
            return;
          }
          const target = event.target as HTMLElement;
          if (target.closest("a, button, input, textarea, select, [role='button'], [contenteditable='true']")) {
            return;
          }
          const composer = composerTextareaRef.current;
          if (composer && !composer.disabled) {
            composer.focus();
          }
        }}
      >
        {messages.length === 0 && !isHistoryLoaded ? (
          <div className="chat-view__skeleton" aria-hidden="true">
            {[68, 45, 72, 32, 58, 40].map((width, index) => (
              <div
                key={index}
                className={
                  index % 2 === 0
                    ? "chat-view__skeleton-bubble"
                    : "chat-view__skeleton-bubble chat-view__skeleton-bubble--outgoing"
                }
                style={{ width: `${width}%` }}
              />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-view__empty">
            {selectedChatId ? (
              <div className="chat-view__empty-card">
                <LottieSticker src="/tg/tgs/UtyanDiscussion.json" size={130} />
                <span>Сообщений пока нет</span>
              </div>
            ) : (
              <span className="chat-view__empty-pill">Выберите чат</span>
            )}
          </div>
        ) : (
          <>
            <div ref={beforeListRef} className="message-list__top">
              {isHistoryLoading ? (
                <div className="message-list__status">Загружаем историю…</div>
              ) : reachedStart ? (
                <div className="message-list__status">Это начало чата</div>
              ) : null}
            </div>
            <div
              className="message-list"
              onMouseDown={handleListMouseDown}
              style={{
                height: `${messageVirtualizer.getTotalSize()}px`,
                position: "relative",
                width: "100%"
              }}
            >
              {messageVirtualizer.getVirtualItems().map((virtualRow) => {
                const message = visibleMessages[virtualRow.index];
                if (!message) {
                  return null;
                }
                const isPinnedMessage = pinnedMessage?.id === message.id;
                // Группировка как в Telegram: подряд идущие сообщения одного
                // отправителя образуют группу; хвост и аватар — у последнего.
                const prevMessage = visibleMessages[virtualRow.index - 1];
                const nextMessage = visibleMessages[virtualRow.index + 1];
                // Разделитель суток, как в оригинальном Telegram: сравниваем
                // календарный день (в поясе саппорта) с предыдущим сообщением.
                // У самого старого сообщения ленты разделитель есть всегда.
                const messageDay = dayKey(message.occurredAt, timeZone);
                const prevDay = prevMessage ? dayKey(prevMessage.occurredAt, timeZone) : null;
                const nextDay = nextMessage ? dayKey(nextMessage.occurredAt, timeZone) : null;
                const showDaySeparator = prevDay !== messageDay;
                const inGroupWithPrev =
                  Boolean(prevMessage) &&
                  prevMessage!.outgoing === message.outgoing &&
                  prevMessage!.senderId === message.senderId &&
                  // разные сутки — группу рвём, иначе хвост/аватар «перепрыгнут»
                  // через разделитель
                  prevDay === messageDay;
                const inGroupWithNext =
                  Boolean(nextMessage) &&
                  nextMessage!.outgoing === message.outgoing &&
                  nextMessage!.senderId === message.senderId &&
                  nextDay === messageDay;
                const isFirstInGroup = !inGroupWithPrev;
                const albumItems = albumLayout.itemsByStartId.get(message.id) ?? null;
                const albumHidden = albumLayout.hiddenIds.has(message.id);
                // Хвост пузыря альбома — по последнему элементу серии: строки
                // продолжения скрыты, и «следующее сообщение» для сетки — это
                // то, что идёт после всего альбома.
                let isLastInGroup = !inGroupWithNext;
                if (albumItems) {
                  const lastIndex = virtualRow.index + albumItems.length - 1;
                  const afterAlbum = visibleMessages[lastIndex + 1];
                  isLastInGroup = !(
                    Boolean(afterAlbum) &&
                    afterAlbum!.outgoing === message.outgoing &&
                    afterAlbum!.senderId === message.senderId &&
                    dayKey(afterAlbum!.occurredAt, timeZone) === messageDay
                  );
                }
                const showUnreadDivider = virtualRow.index === unreadDividerIndex;

                return (
                  <MessageRow
                    key={virtualRow.key}
                    message={message}
                    index={virtualRow.index}
                    translateY={virtualRow.start - messageScrollMargin}
                    measureElement={messageVirtualizer.measureElement}
                    timeZone={timeZone}
                    selectedChatId={selectedChatId}
                    isGroupChat={Boolean(selectedChat?.isGroupChat)}
                    lastReadOutboxMessageId={selectedChat?.lastReadOutboxMessageId}
                    lastReadOutboxAt={selectedChat?.lastReadOutboxAt}
                    isPinnedMessage={isPinnedMessage}
                    showDaySeparator={showDaySeparator}
                    isFirstInGroup={isFirstInGroup}
                    isLastInGroup={isLastInGroup}
                    albumItems={albumItems}
                    albumHidden={albumHidden}
                    showUnreadDivider={showUnreadDivider}
                    isFlash={flashMessageId === message.id}
                    selectionMode={selectionMode}
                    isSelected={selectedMessageIds.has(message.id)}
                    translation={
                      selectedChatId
                        ? translationsByMessage[translationKey(selectedChatId, message.id)]
                        : undefined
                    }
                    reactionPickerOpen={reactionPickerForMessage === message.id}
                    onToggleSelection={toggleMessageSelection}
                    onReply={replyToMessage}
                    onScrollToMessage={scrollToMessageStable}
                    onOpenMedia={openFeedMedia}
                    onTranscribeVoice={transcribeVoice}
                    onClearTranslation={clearTranslationForSelectedChat}
                    onPressInlineButton={pressInlineButton}
                    onToggleReaction={toggleReaction}
                    onOpenReactionPicker={setReactionPickerForMessage}
                    onOpenContextMenu={setMessageContextMenu}
                    onOpenAutoDeleteEditor={() => setAutoDeleteEditorOpen(true)}
                  />
                );
              })}
          </div>
          </>
        )}
      </div>
      {showScrollDown && selectedChatId ? (
        <button
          type="button"
          className="scroll-down"
          onClick={() => {
            programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
            messageVirtualizer.scrollToIndex(visibleMessages.length - 1, {
              align: "end"
            });
          }}
          aria-label="К последним сообщениям"
          title="К последним сообщениям"
        >
          {selectedChat && selectedChat.unreadCount > 0 ? (
            <span className="scroll-down__badge">{selectedChat.unreadCount}</span>
          ) : null}
          <i className="ic ic-arrow-down" aria-hidden="true" />
        </button>
      ) : null}
      </div>

      {(() => {
        if (!messageContextMenu || !selectedChatId) {
          return null;
        }
        const ctxMessage =
          visibleMessages.find((entry) => entry.id === messageContextMenu.messageId) ?? null;
        if (!ctxMessage || ctxMessage.isDeleted) {
          return null;
        }
        const ctxIsPinned = pinnedMessage?.id === ctxMessage.id;
        const closeMenu = (): void => setMessageContextMenu(null);
        return createPortal(
          <div
            className="dialog-context-menu__layer"
            onClick={closeMenu}
            onContextMenu={(event) => {
              event.preventDefault();
              closeMenu();
            }}
            role="presentation"
          >
            <ContextMenuPositioner
              anchorX={messageContextMenu.x}
              anchorY={messageContextMenu.y}
            >
              <div className="dialog-context-menu__reactions">
                {EXTENDED_REACTIONS.map((emoji, index) => (
                  <button
                    key={`${emoji}:${index}`}
                    type="button"
                    className="dialog-context-menu__reaction"
                    onClick={() => {
                      toggleReaction(selectedChatId, ctxMessage.id, emoji);
                      closeMenu();
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div className="dialog-context-menu__divider" />
              {isServerMessageId(ctxMessage.id) ? (
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    cancelEditing();
                    setReplyToMessageId(ctxMessage.id);
                    closeMenu();
                  }}
                >
                  <i className="ic ic-reply" aria-hidden="true" />
                  Ответить
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className="dialog-context-menu__item"
                onClick={() => {
                  // Вход в режим множественного выбора: инициализируем Set
                  // текущим сообщением. Дальше клики по пузырям добавляют/
                  // убирают из выбора.
                  setSelectedMessageIds(new Set([ctxMessage.id]));
                  closeMenu();
                }}
                title="Выбрать несколько сообщений (переслать/копировать/удалить)"
              >
                <i className="ic ic-check" aria-hidden="true" />
                Выбрать
              </button>
              {(
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    // Общий пикер — любое сообщение, любой роли (2026-09-23).
                    openForwardComposition(selectedChatId, ctxMessage.id);
                    setForwardPickerOpen(true);
                    closeMenu();
                  }}
                  title="Переслать в другой чат"
                >
                  <i className="ic ic-forward" aria-hidden="true" />
                  Переслать…
                </button>
              )}
              {/* Единственная пересылка чужих сообщений, доступная саппорту
                  (гейтвей пропускает только адресат из «Частых групп»). */}
              {(() => {
                  const targets = frequentChats.filter((entry) => entry.chatId !== selectedChatId);
                  const unavailable = targets.length === 0;
                  return (
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      disabled={unavailable}
                      onClick={(event) => {
                        if (unavailable) return;
                        const rect = visualRect(event.currentTarget);
                        setFrequentMenu({
                          x: rect.right + 4,
                          y: rect.top,
                          messageIds: [ctxMessage.id]
                        });
                        closeMenu();
                      }}
                      title={
                        unavailable
                          ? frequentChats.length === 0
                            ? "Частые группы не назначены администратором"
                            : "Это и есть частая группа"
                          : "Переслать в одну из частых групп"
                      }
                    >
                      <i className="ic ic-forward" aria-hidden="true" />
                      Переслать в Частые группы…
                    </button>
                  );
              })()}
              <button
                type="button"
                role="menuitem"
                className="dialog-context-menu__item"
                onClick={() => {
                  if (ctxIsPinned) {
                    toggleMessagePinned(selectedChatId, ctxMessage.id, false);
                  } else {
                    // Модалка показывается ВСЕГДА (как в оригинальном ТГ):
                    // в личном чате — с чекбоксом «закрепить и у собеседника»,
                    // в группе — подтверждение «для всех участников».
                    setPinAlsoForPeer(false);
                    setPinDialogMessageId(ctxMessage.id);
                  }
                  closeMenu();
                }}
              >
                <i className={ctxIsPinned ? "ic ic-pin2" : "ic ic-pin"} aria-hidden="true" />
                {ctxIsPinned ? "Открепить" : "Закрепить"}
              </button>
              {(() => {
                // У голосовых/кружков копируем расшифровку с экрана, а не
                // метку «Голосовое сообщение».
                const transcript =
                  (ctxMessage.content.kind === "voice" || ctxMessage.content.kind === "videoNote") &&
                  ctxMessage.content.transcript?.trim()
                    ? ctxMessage.content.transcript.trim()
                    : null;
                const copyable =
                  transcript ??
                  (ctxMessage.content.kind === "voice" || ctxMessage.content.kind === "videoNote"
                    ? ""
                    : ctxMessage.text.trim());
                if (copyable.length === 0) {
                  return null;
                }
                return (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      void copyTextToClipboard(copyable);
                      closeMenu();
                    }}
                  >
                    <i className="ic ic-copy" aria-hidden="true" />
                    {transcript ? "Копировать расшифровку" : "Копировать текст"}
                  </button>
                );
              })()}
              {(() => {
                const imageUrl = copyableImageUrl(ctxMessage.content);
                if (!imageUrl || !canCopyImageToClipboard()) {
                  return null;
                }
                return (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      // Скрин юзера → буфер → Ctrl+V в поле ввода → «стрелки».
                      void copyImageToClipboard(imageUrl).then((ok) =>
                        setCopyToast(ok ? "Фото скопировано" : "Не удалось скопировать фото")
                      );
                      closeMenu();
                    }}
                    title="Скопировать картинку в буфер обмена (потом Ctrl/Cmd+V в поле ввода)"
                  >
                    <i className="ic ic-copy" aria-hidden="true" />
                    Копировать фото
                  </button>
                );
              })()}
              {/* Переводим не только текст: подписи к медиа и расшифровку
                  голосового (после «Распознать») тоже. */}
              {translationSourceText(ctxMessage.content).length > 0 &&
              !translationsByMessage[translationKey(selectedChatId, ctxMessage.id)] ? (
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    translateMessage(selectedChatId, ctxMessage.id);
                    closeMenu();
                  }}
                >
                  <i className="ic ic-translate" aria-hidden="true" />
                  Перевести
                </button>
              ) : null}
              {ctxMessage.outgoing &&
              (ctxMessage.content.kind === "text" ||
                ctxMessage.content.kind === "photo" ||
                ctxMessage.content.kind === "video" ||
                ctxMessage.content.kind === "document") ? (
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    startEditingMessage(ctxMessage.id);
                    closeMenu();
                  }}
                  title={
                    ctxMessage.content.kind === "text"
                      ? "Изменить текст сообщения"
                      : "Изменить подпись или заменить медиа (Ctrl+V / скрепка)"
                  }
                >
                  <i className="ic ic-edit" aria-hidden="true" />
                  {ctxMessage.content.kind === "text" ? "Изменить" : "Редактировать"}
                </button>
              ) : null}
              {ctxMessage.outgoing ? (
                <>
                  <div className="dialog-context-menu__divider" />
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item dialog-context-menu__item--danger"
                    onClick={() => {
                      setDeleteAlsoForPeer(true);
                      setDeleteDialogMessageIds([ctxMessage.id]);
                      closeMenu();
                    }}
                  >
                    <i className="ic ic-delete" aria-hidden="true" />
                    Удалить
                  </button>
                </>
              ) : null}
            </ContextMenuPositioner>
          </div>,
          document.body
        );
      })()}

      {translatorOpen && selectedChatId
        ? createPortal(
            <div
              className="forward-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Мини-переводчик"
            >

              <button
                type="button"
                className="forward-modal__backdrop"
                onClick={() => setTranslatorOpen(false)}
                aria-label="Закрыть переводчик"
              />
              <div className="forward-modal__panel translator-modal">
                <div className="forward-sheet__header">
                  <div>
                    <strong>Переводчик</strong>
                    <p>OpenRouter (тот же ключ, что у автоперевода чата).</p>
                  </div>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setTranslatorOpen(false)}
                    aria-label="Закрыть"
                  >
                    ×
                  </button>
                </div>
                <div className="translator-modal__row">
                  <label className="translator-modal__label">
                    Язык
                    <select
                      value={translatorTargetLang}
                      onChange={(event) => setTranslatorTargetLang(event.target.value)}
                      disabled={isTranslatorBusy}
                    >
                      {translateLanguages.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                      {translateLanguages.every(
                        (option) => option.code !== translatorTargetLang
                      ) ? (
                        <option value={translatorTargetLang}>
                          {translatorTargetLang.toUpperCase()}
                        </option>
                      ) : null}
                    </select>
                  </label>
                </div>
                <textarea
                  className="translator-modal__input"
                  value={translatorInput}
                  onChange={(event) => setTranslatorInput(event.target.value)}
                  placeholder="Введите текст…"
                  rows={5}
                  disabled={isTranslatorBusy}
                />
                <div className="translator-modal__actions">
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => void runTranslator()}
                    disabled={isTranslatorBusy || translatorInput.trim().length === 0}
                  >
                    {isTranslatorBusy ? "Переводим…" : "Перевести"}
                  </button>
                  {translatorResult ? (
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={async () => {
                        const ok = await copyTextToClipboard(translatorResult);
                        if (ok) {
                          setTranslatorCopied(true);
                          window.setTimeout(() => setTranslatorCopied(false), 1800);
                        } else {
                          setTranslatorError("Не удалось скопировать. Выделите текст вручную.");
                        }
                      }}
                    >
                      {translatorCopied ? "Скопировано ✓" : "Скопировать"}
                    </button>
                  ) : null}
                </div>
                {translatorError ? (
                  <div className="translator-modal__error">{translatorError}</div>
                ) : null}
                {translatorResult ? (
                  <div className="translator-modal__result">{translatorResult}</div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}

      {pinDialogMessageId && selectedChatId
        ? createPortal(
            (() => {
              // Личный чат определяем по telegramUserId (он есть только у
              // приватных) с fallback на isGroupChat — надёжнее, чем один флаг.
              const pinChatIsPrivate = Boolean(
                selectedChat &&
                  (selectedChat.telegramUserId !== undefined || !selectedChat.isGroupChat)
              );
              return (
                <div
                  className="forward-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Закрепить сообщение"
                >
                  <button
                    type="button"
                    className="forward-modal__backdrop"
                    onClick={() => setPinDialogMessageId(null)}
                    aria-label="Отмена"
                  />
                  <div className="forward-modal__panel pin-modal">
                    <div className="forward-sheet__header">
                      <div>
                        <strong>Закрепить сообщение</strong>
                        <p>
                          {pinChatIsPrivate
                            ? "По умолчанию закрепляется только у вас."
                            : "Сообщение будет закреплено для всех участников чата."}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => setPinDialogMessageId(null)}
                        aria-label="Закрыть"
                      >
                        ×
                      </button>
                    </div>
                    {pinChatIsPrivate ? (
                      <label className="pin-modal__checkbox">
                        <input
                          type="checkbox"
                          checked={pinAlsoForPeer}
                          onChange={(event) => setPinAlsoForPeer(event.target.checked)}
                        />
                        <span>
                          Также закрепить для {selectedChat?.title ?? "собеседника"}
                        </span>
                      </label>
                    ) : null}
                    <div className="translator-modal__actions">
                      <button
                        type="button"
                        className="button button--primary"
                        onClick={() => {
                          toggleMessagePinned(
                            selectedChatId,
                            pinDialogMessageId,
                            true,
                            // only_for_self валиден только в личных чатах;
                            // в группе всегда закрепляем для всех.
                            pinChatIsPrivate ? !pinAlsoForPeer : false
                          );
                          setPinDialogMessageId(null);
                        }}
                      >
                        Закрепить
                      </button>
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={() => setPinDialogMessageId(null)}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                </div>
              );
            })(),
            document.body
          )
        : null}
      {deleteDialogMessageIds && deleteDialogMessageIds.length > 0 && selectedChatId
        ? createPortal(
            (() => {
              const deleteChatIsPrivate = Boolean(
                selectedChat &&
                  (selectedChat.telegramUserId !== undefined || !selectedChat.isGroupChat)
              );
              const count = deleteDialogMessageIds.length;
              const countLabel =
                count === 1 ? "сообщение" : count < 5 ? "сообщения" : "сообщений";
              const closeDeleteDialog = (): void => setDeleteDialogMessageIds(null);
              return (
                <div
                  className="forward-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Удалить сообщение"
                >
                  <button
                    type="button"
                    className="forward-modal__backdrop"
                    onClick={closeDeleteDialog}
                    aria-label="Отмена"
                  />
                  <div className="forward-modal__panel pin-modal">
                    <div className="forward-sheet__header">
                      <div>
                        <strong>
                          Удалить {count === 1 ? "сообщение" : `${count} ${countLabel}`}?
                        </strong>
                        <p>
                          {deleteChatIsPrivate
                            ? "Без галочки сообщение удалится только у вас."
                            : "Сообщение будет удалено у всех участников чата."}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={closeDeleteDialog}
                        aria-label="Закрыть"
                      >
                        ×
                      </button>
                    </div>
                    {deleteChatIsPrivate ? (
                      <label className="pin-modal__checkbox">
                        <input
                          type="checkbox"
                          checked={deleteAlsoForPeer}
                          onChange={(event) => setDeleteAlsoForPeer(event.target.checked)}
                        />
                        <span>
                          Также удалить для {selectedChat?.title ?? "собеседника"}
                        </span>
                      </label>
                    ) : null}
                    <div className="translator-modal__actions">
                      <button
                        type="button"
                        className="button button--primary"
                        onClick={() => {
                          const revoke = deleteChatIsPrivate ? deleteAlsoForPeer : true;
                          for (const id of deleteDialogMessageIds) {
                            deleteMessage(selectedChatId, id, revoke);
                          }
                          if (deletedOpen) {
                            window.setTimeout(() => {
                              void loadDeletedMessages({ force: true });
                            }, 250);
                          }
                          exitSelectionMode();
                          closeDeleteDialog();
                        }}
                      >
                        Удалить
                      </button>
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={closeDeleteDialog}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                </div>
              );
            })(),
            document.body
          )
        : null}

      {/* Forward-превью теперь живёт в композере (см. .composer__forward
          выше); ПКМ-меню на превью рендерится сюда через портал. */}
      {exportOpen && selectedChatId ? (
        <ExportChatModal
          chatId={selectedChatId}
          chatTitle={selectedChat?.title ?? "Чат"}
          onClose={() => setExportOpen(false)}
        />
      ) : null}
      {composerLinkMenu
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setComposerLinkMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setComposerLinkMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={composerLinkMenu.x} anchorY={composerLinkMenu.y}>
                {findMarkdownLinkAt(draft, composerLinkMenu.start, composerLinkMenu.end) ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        openLinkEditor(composerLinkMenu.start, composerLinkMenu.end);
                        setComposerLinkMenu(null);
                      }}
                    >
                      <i className="ic ic-edit" aria-hidden="true" />
                      Изменить ссылку
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item dialog-context-menu__item--danger"
                      onClick={() => {
                        const link = findMarkdownLinkAt(
                          draft,
                          composerLinkMenu.start,
                          composerLinkMenu.end
                        );
                        if (link) {
                          replaceDraftRange(link.start, link.end, link.label);
                        }
                        setComposerLinkMenu(null);
                      }}
                    >
                      <i className="ic ic-delete" aria-hidden="true" />
                      Удалить ссылку
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      openLinkEditor(composerLinkMenu.start, composerLinkMenu.end);
                      setComposerLinkMenu(null);
                    }}
                    title="Ctrl+K · на Mac ⌘U"
                  >
                    <i className="ic ic-link" aria-hidden="true" />
                    Добавить ссылку
                  </button>
                )}
                {composerLinkMenu.start !== composerLinkMenu.end ? (
                  <>
                    <div className="dialog-context-menu__divider" />
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        void copyTextToClipboard(
                          draft.slice(composerLinkMenu.start, composerLinkMenu.end)
                        );
                        setComposerLinkMenu(null);
                        requestAnimationFrame(() => composerTextareaRef.current?.focus());
                      }}
                    >
                      <i className="ic ic-copy" aria-hidden="true" />
                      Копировать
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        void copyTextToClipboard(
                          draft.slice(composerLinkMenu.start, composerLinkMenu.end)
                        );
                        replaceDraftRange(composerLinkMenu.start, composerLinkMenu.end, "");
                        setComposerLinkMenu(null);
                      }}
                    >
                      <i className="ic ic-delete" aria-hidden="true" />
                      Вырезать
                    </button>
                  </>
                ) : null}
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {linkEditor ? (
        <ComposerLinkModal
          initialLabel={linkEditor.label}
          initialUrl={linkEditor.url}
          isExisting={linkEditor.isExisting}
          onClose={() => {
            setLinkEditor(null);
            requestAnimationFrame(() => composerTextareaRef.current?.focus());
          }}
          onSave={(label, url) => {
            replaceDraftRange(linkEditor.start, linkEditor.end, buildMarkdownLink(label, url));
            setLinkEditor(null);
          }}
          onRemove={() => {
            replaceDraftRange(linkEditor.start, linkEditor.end, linkEditor.label);
            setLinkEditor(null);
          }}
        />
      ) : null}
      {frequentMenu && selectedChatId
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setFrequentMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setFrequentMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={frequentMenu.x} anchorY={frequentMenu.y}>
                <div className="dialog-context-menu__section">Переслать в Частые группы</div>
                {frequentChats
                  .filter((entry) => entry.chatId !== selectedChatId)
                  .map((entry) => (
                    <button
                      key={entry.chatId}
                      type="button"
                      role="menuitem"
                      className="dialog-context-menu__item"
                      onClick={() => {
                        // Как обычное «Переслать…» (TG-флоу): открываем диалог
                        // группы с подготовленной пересылкой — саппорт может
                        // дописать текст, уходит только по «Отправить».
                        // Раньше клик пересылал сразу. Порядок id — порядок
                        // ленты, хронология у получателя сохраняется.
                        openForwardComposition(selectedChatId, frequentMenu.messageIds);
                        if (entry.chatId !== selectedChatId) {
                          selectChat(entry.chatId);
                        }
                        setFrequentMenu(null);
                        exitSelectionMode();
                      }}
                      title={`Переслать в «${frequentChatTitle(entry)}»`}
                    >
                      <i className="ic ic-forward" aria-hidden="true" />
                      {frequentChatTitle(entry)}
                    </button>
                  ))}
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {forwardPreviewMenu && forwardComposition
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setForwardPreviewMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setForwardPreviewMenu(null);
              }}
              role="presentation"
            >
              {/* Тот же позиционер, что и у остальных ПКМ-меню: открывается
                  в точке клика и разворачивается вверх/влево, если у края
                  экрана не хватает места. */}
              <ContextMenuPositioner
                anchorX={forwardPreviewMenu.x}
                anchorY={forwardPreviewMenu.y}
              >
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={forwardComposition.hideSenderName}
                  className="dialog-context-menu__item"
                  onClick={() => {
                    setForwardHideSenderName(!forwardComposition.hideSenderName);
                    setForwardPreviewMenu(null);
                  }}
                >
                  {forwardComposition.hideSenderName ? "✓ " : ""}
                  Скрыть отправителя
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={forwardComposition.hideCaption}
                  className="dialog-context-menu__item"
                  onClick={() => {
                    setForwardHideCaption(!forwardComposition.hideCaption);
                    setForwardPreviewMenu(null);
                  }}
                >
                  {forwardComposition.hideCaption ? "✓ " : ""}
                  Скрыть текст
                </button>
                <div className="dialog-context-menu__divider" />
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item dialog-context-menu__item--danger"
                  onClick={() => {
                    closeForwardComposition();
                    setForwardPreviewMenu(null);
                  }}
                >
                  Отменить пересылку
                </button>
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}

      {/* Строка «📁 В папке: …» отсюда убрана. Она занимала 39px во всю ширину
          колонки между лентой и композером, а подпись короткая и жалась к
          левому краю — остальное читалось как пустая полоса, за которой
          обрезаются сообщения (жалоба саппортов). Папки чата видны на плашках
          в списке диалогов, а полным списком — в панели профиля чата. */}
      <form
        ref={composerFormRef}
        onSubmit={async (event) => {
          event.preventDefault();
          // Guard от двойного submit'а (Enter+клик, dblClick, дребезг):
          // держим true до завершения асинх-веток, затем снимаем.
          if (composerSubmittingRef.current) {
            return;
          }
          composerSubmittingRef.current = true;
          try {
          const trimmedDraft = draft.trim();
          if (!selectedChatId) {
            return;
          }

          setComposerError(null);

          if (isRecordingVoice) {
            setComposerError("Сначала остановите запись голосового сообщения.");
            return;
          }

          if (editingMessageId) {
            const editingMedia = editingTarget !== null && editingTarget.content.kind !== "text";
            const replacement = pendingAttachments[0];
            if (replacement && editingMedia) {
              // Замена медиа: сначала загрузка файла (как при отправке),
              // затем editMessage с новым content; подпись — из поля ввода.
              setIsSubmittingAttachment(true);
              try {
                const uploaded = await mediaApi.uploadFile(
                  wsUrl,
                  replacement.file,
                  replacement.kind
                );
                editMessage(selectedChatId, editingMessageId, trimmedDraft, {
                  content: buildAttachmentContent(replacement, uploaded, trimmedDraft)
                });
              } catch (cause) {
                setComposerError(
                  cause instanceof Error ? cause.message : "Не удалось загрузить файл"
                );
                return;
              } finally {
                setIsSubmittingAttachment(false);
              }
            } else {
              if (trimmedDraft.length === 0 && !editingMedia) {
                return;
              }
              editMessage(selectedChatId, editingMessageId, trimmedDraft, {
                allowEmpty: editingMedia
              });
            }
            // Черновик, отложенный на время правки, возвращается в поле —
            // стирать его правка чужого сообщения не должна.
            cancelEditing();
            return;
          }

          // Расписание приходит из окна «Отправить позже» (scheduleRef) и
          // действует на один сабмит. После планирования открываем окно
          // отложенных — как в оригинальном TG.
          const schedule = scheduleRef.current;
          scheduleRef.current = null;
          const scheduledIso = schedule?.sendAt;
          const whenOnline = schedule?.whenOnline === true;
          const isScheduledSend = Boolean(scheduledIso) || whenOnline;
          const sendOptions = whenOnline ? { whenOnline: true } : {};
          const finishScheduled = (): void => {
            if (isScheduledSend) {
              setScheduledWindowOpen(true);
            }
          };

          // TG-стиль: если активна пересылка — сначала форвард в текущий
          // чат с учётом hide-флагов, потом обычная отправка draft/attachment
          // (если есть). Пустой draft + только форвард — ОК, сабмит должен
          // отработать форвард и выйти.
          if (forwardComposition) {
            for (const sourceMessageId of forwardComposition.sourceMessageIds) {
              forwardMessage(forwardComposition.sourceChatId, sourceMessageId, selectedChatId, {
                hideSenderName: forwardComposition.hideSenderName,
                hideCaption: forwardComposition.hideCaption
              });
            }
            closeForwardComposition();
            if (trimmedDraft.length === 0 && pendingAttachments.length === 0) {
              return;
            }
          }

          if (pendingAttachments.length > 0) {
            setIsSubmittingAttachment(true);
            // «Группировать» (2026-08-06): несколько фото/видео уходят одним
            // альбомом Telegram (подпись — на первом контенте). Отложенная
            // отправка альбомов не поддерживается — при заданном времени
            // остаётся раздельный путь.
            const sendAsAlbum =
              composerGroupMedia &&
              pendingAttachments.length > 1 &&
              pendingAttachments.every(
                (attachment) =>
                  attachment.kind === "photo" || attachment.kind === "video"
              );
            if (sendAsAlbum) {
              // Плейсхолдер первого фото появляется в ленте сразу (до
              // загрузки на сервер) и станет первым сообщением альбома по
              // chat.messageAccepted.
              const first = pendingAttachments[0]!;
              const albumLocalId = isScheduledSend
                ? undefined
                : beginOptimisticSend(
                    selectedChatId,
                    buildLocalPreviewContent(first, trimmedDraft)
                  );
              try {
                const uploads = await uploadAttachmentsParallel(wsUrl, pendingAttachments);
                // Пока файл грузился, вкладку могли переключить на другой
                // аккаунт: chatId прежнего аккаунта в новом — чужой диалог.
                assertSameAccount(accountId);
                const contents: MessageContent[] = pendingAttachments.map((attachment, index) =>
                  buildAttachmentContent(attachment, uploads[index]!, index === 0 ? trimmedDraft : "")
                );
                sendAlbum(selectedChatId, contents, undefined, {
                  ...(albumLocalId ? { localId: albumLocalId } : {}),
                  ...(scheduledIso ? { scheduledAt: scheduledIso } : {}),
                  ...(whenOnline ? { whenOnline: true } : {})
                });
                clearStoredDraft(selectedChatId);
                resetDraftHistory();
                setDraft("");
                setReplyToMessageId(null);
                pendingAttachments.forEach((attachment) =>
                  revokeAttachmentPreviewLater(attachment)
                );
                setPendingAttachments([]);
                finishScheduled();
              } catch (cause) {
                // Ошибка на загрузке: вложения остаются в композере целиком,
                // альбом не отправлен частично — можно повторить «Отправить».
                if (albumLocalId) {
                  cancelOptimisticSend(selectedChatId, albumLocalId);
                }
                setComposerError(
                  cause instanceof Error ? cause.message : "Не удалось загрузить файл"
                );
              } finally {
                setIsSubmittingAttachment(false);
              }
              return;
            }
            // Каждое вложение — отдельное сообщение; подпись и reply уходят
            // с первым. При ошибке на середине уже отправленные выбывают из
            // списка, оставшиеся ждут повторного «Отправить».
            // Кружки не поддерживают подпись — если первым идёт кружок,
            // текст уходит отдельным сообщением после вложений, а не молча
            // теряется.
            // Подписи: текст уходит с первым вложением, у которого подпись
            // поддерживается (кружки — нет); иначе отдельным сообщением.
            const captions = pendingAttachments.map(() => "");
            let captionSent = trimmedDraft.length === 0;
            for (const [index, attachment] of pendingAttachments.entries()) {
              if (!captionSent && attachment.kind !== "videoNote") {
                captions[index] = trimmedDraft;
                captionSent = true;
              }
            }
            // Плейсхолдеры всех вложений появляются в ленте сразу — саппорт
            // видит отправку мгновенно, а загрузка на сервер идёт параллельно
            // (раньше файлы грузились по одному и пузырь появлялся только после).
            const localIds = isScheduledSend
              ? []
              : pendingAttachments.map((attachment, index) =>
                  beginOptimisticSend(
                    selectedChatId,
                    buildLocalPreviewContent(attachment, captions[index] ?? "")
                  )
                );
            // Поле ввода очищаем сразу: черновик уже «в пути».
            clearStoredDraft(selectedChatId);
            resetDraftHistory();
            setDraft("");
            const replyForFirst = replyToMessageId ?? undefined;
            setReplyToMessageId(null);
            const attachmentsToSend = pendingAttachments;
            setPendingAttachments([]);
            try {
              const uploads = await uploadAttachmentsParallel(wsUrl, attachmentsToSend);
              // Пока файл грузился, вкладку могли переключить на другой
              // аккаунт: chatId прежнего аккаунта в новом — чужой диалог.
              assertSameAccount(accountId);
              attachmentsToSend.forEach((attachment, index) => {
                sendMessage(
                  selectedChatId,
                  buildAttachmentContent(attachment, uploads[index]!, captions[index] ?? ""),
                  index === 0 ? replyForFirst : undefined,
                  scheduledIso,
                  undefined,
                  { ...sendOptions, ...(localIds[index] ? { localId: localIds[index]! } : {}) }
                );
              });
              if (!captionSent) {
                sendMessage(selectedChatId, { kind: "text", text: trimmedDraft }, undefined, scheduledIso, undefined, sendOptions);
              }
              attachmentsToSend.forEach((attachment) => revokeAttachmentPreviewLater(attachment));
              finishScheduled();
            } catch (cause) {
              localIds.forEach((localId) => cancelOptimisticSend(selectedChatId, localId));
              // Вложения возвращаем в композер целиком — можно повторить «Отправить».
              setPendingAttachments(attachmentsToSend);
              if (trimmedDraft.length > 0) {
                setDraft(trimmedDraft);
              }
              setComposerError(
                cause instanceof Error ? cause.message : "Не удалось загрузить файл"
              );
            } finally {
              setIsSubmittingAttachment(false);
            }
            return;
          }

          if (trimmedDraft.length === 0) {
            return;
          }

          const parsed = hasMarkdownSyntax(trimmedDraft)
            ? parseMarkdown(trimmedDraft)
            : { text: trimmedDraft, entities: [] };

          // Уходит текст, который мы сами перевели из русского? Запоминаем
          // пару, чтобы под отправленным сообщением показался наш оригинал,
          // а не обратный перевод от LLM.
          if (outgoingTranslated && trimmedDraft === outgoingTranslated.translated.trim()) {
            rememberOutgoingOriginal(selectedChatId, parsed.text, outgoingTranslated.original);
            setOutgoingTranslated(null);
          }

          sendMessage(
            selectedChatId,
            {
              kind: "text",
              text: parsed.text,
              ...(parsed.entities.length > 0 ? { entities: parsed.entities } : {})
            },
            replyToMessageId ?? undefined,
            scheduledIso,
            undefined,
            sendOptions
          );
          clearStoredDraft(selectedChatId);
          resetDraftHistory();
          setDraft("");
          setReplyToMessageId(null);
          finishScheduled();
          } finally {
            // Снимаем guard в microtask: закрытие в том же тике блокирует
            // повторный Enter/click, но следующий render'ы (после setDraft(""))
            // снова разрешают отправку.
            queueMicrotask(() => {
              composerSubmittingRef.current = false;
            });
          }
        }}
        className="composer"
      >
        {(() => {
          // Дропдаун быстрых ответов TG Business: черновик начинается с «/».
          if (!selectedChatId || !draft.startsWith("/") || draft.includes("\n")) {
            return null;
          }
          const filter = draft.slice(1).trim().toLowerCase();
          const matches = quickReplies.filter((shortcut) =>
            shortcut.name.toLowerCase().includes(filter)
          );
          if (matches.length === 0) {
            return quickReplies.length === 0 ? (
              <div className="composer__quick-replies">
                <div className="composer__quick-replies-empty">
                  Быстрые ответы не настроены в Telegram Business этого аккаунта.
                </div>
              </div>
            ) : null;
          }
          return (
            <div className="composer__quick-replies" role="listbox">
              {matches.slice(0, 12).map((shortcut) => (
                <button
                  key={shortcut.shortcutId}
                  type="button"
                  className="composer__quick-reply"
                  onClick={() => {
                    sendQuickReply(selectedChatId, shortcut.shortcutId);
                    focusChatComposer();
                    clearStoredDraft(selectedChatId);
                    resetDraftHistory();
                    setDraft("");
                  }}
                  title={`Отправить быстрый ответ /${shortcut.name}`}
                >
                  <span className="composer__quick-reply-name">/{shortcut.name}</span>
                  <span className="composer__quick-reply-preview">
                    {shortcut.firstMessageText ??
                      `${shortcut.messageCount} сообщ.`}
                  </span>
                  {shortcut.messageCount > 1 ? (
                    <span className="composer__quick-reply-count">
                      ×{shortcut.messageCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          );
        })()}
        {selectedChatId && botReplyKeyboard ? (
          <div className="composer__bot-keyboard">
            {botReplyKeyboard.placeholder ? (
              <div className="composer__bot-keyboard-hint">
                {botReplyKeyboard.placeholder}
              </div>
            ) : null}
            {botReplyKeyboard.rows.map((row, rowIndex) => (
              <div key={rowIndex} className="composer__bot-keyboard-row">
                {row.buttons.map((button, buttonIndex) =>
                  button.kind === "unsupported" ? (
                    // Спецкнопка (запрос телефона/гео/mini-app/опроса):
                    // отправка её лейбла текстом — молча неверное действие,
                    // показываем disabled.
                    <button
                      key={`${rowIndex}:${buttonIndex}`}
                      type="button"
                      className="composer__bot-keyboard-button"
                      disabled
                      title="Кнопка этого типа не поддерживается в CRM"
                    >
                      {button.text}
                    </button>
                  ) : (
                    <button
                      key={`${rowIndex}:${buttonIndex}`}
                      type="button"
                      className="composer__bot-keyboard-button"
                      onClick={() => {
                        // Guard от двойного клика: повторное нажатие в течение
                        // секунды отправило бы второе такое же сообщение.
                        if (botKeyboardSubmittingRef.current) {
                          return;
                        }
                        botKeyboardSubmittingRef.current = true;
                        window.setTimeout(() => {
                          botKeyboardSubmittingRef.current = false;
                        }, 800);
                        sendMessage(selectedChatId, { kind: "text", text: button.text });
                        if (botReplyKeyboard.oneTime) {
                          clearBotReplyKeyboard(selectedChatId);
                        }
                      }}
                      title="Отправить как ответ боту"
                    >
                      {button.text}
                    </button>
                  )
                )}
              </div>
            ))}
            <button
              type="button"
              className="composer__bot-keyboard-close"
              onClick={() => clearBotReplyKeyboard(selectedChatId)}
              title="Скрыть кнопки бота"
              aria-label="Скрыть кнопки бота"
            >
              <i className="ic ic-close" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        {isRecordingVoice ? (
          <div className="composer__recording">
            <div className="composer__recording-copy">
              <strong>Идёт запись голосового</strong>
              <span>{recordingLabel}</span>
            </div>
            <button
              type="button"
              className="button button--primary composer__recording-button"
              onClick={() => stopRecording()}
            >
              Остановить
            </button>
          </div>
        ) : null}
        {replyTarget || editingTarget ? (
          <div className="composer__context">
            {editingTarget && editingTarget.content.kind !== "text" ? (
              (() => {
                const media = editingTarget.content;
                const thumb =
                  ("thumbnailUrl" in media && media.thumbnailUrl) ||
                  (media.kind === "photo" && media.url) ||
                  null;
                const replaced = pendingAttachments.length > 0;
                return (
                  <button
                    type="button"
                    className="composer__context-thumb"
                    onClick={() => void openCurrentMediaForEdit()}
                    disabled={isSubmittingAttachment || media.kind !== "photo"}
                    title={
                      media.kind === "photo"
                        ? "Открыть фото и отредактировать (стрелки) — результат заменит исходное"
                        : "Заменить: вставьте новое из буфера (Ctrl+V) или через скрепку"
                    }
                    aria-label="Текущее медиа сообщения"
                  >
                    {thumb ? (
                      <img src={thumb} alt="" className={replaced ? "is-replaced" : undefined} />
                    ) : (
                      <span className="composer__context-thumb-icon">
                        {media.kind === "video" ? "VIDEO" : "FILE"}
                      </span>
                    )}
                  </button>
                );
              })()
            ) : null}
            <div className="composer__context-copy">
              <strong>
                {editingTarget
                  ? editingTarget.content.kind === "text"
                    ? "Редактирование сообщения"
                    : pendingAttachments.length > 0
                      ? "Замена медиа"
                      : "Редактирование подписи"
                  : "Ответ на сообщение"}
              </strong>
              <span>
                {editingTarget
                  ? editingTarget.content.kind === "text"
                    ? editingTarget.text
                    : pendingAttachments.length > 0
                      ? `Новое: ${pendingAttachments[0]?.file.name ?? ""} · подпись в поле ввода`
                      : "Ctrl+V или скрепка — заменить медиа; текст в поле — подпись"
                  : replyTarget?.text}
              </span>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                stopRecording();
                // Крестик снимает ТОЛЬКО контекст (ответ/правку). Черновик
                // остаётся в поле — стереть его можно лишь руками.
                setReplyToMessageId(null);
                cancelEditing();
              }}
              aria-label="Сбросить контекст"
              disabled={isSubmittingAttachment}
            >
              <i className="ic ic-close" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        {forwardComposition ? (
          <div
            className="composer__forward"
            onContextMenu={(event) => {
              event.preventDefault();
              setForwardPreviewMenu({ x: event.clientX, y: event.clientY });
            }}
          >
            <div className="composer__forward-icon" aria-hidden="true">
              ↪
            </div>
            <div className="composer__forward-copy">
              <strong>
                Переслать из «{forwardSourceChatTitle}»
                {forwardComposition.hideSenderName ? " · без отправителя" : ""}
                {forwardComposition.hideCaption ? " · без текста" : ""}
              </strong>
              <span>
                {forwardComposition.sourceMessageIds.length > 1
                  ? `${forwardComposition.sourceMessageIds.length} сообщени${
                      forwardComposition.sourceMessageIds.length < 5 ? "я" : "й"
                    }: ${forwardSourceMessageText || "медиа"}`
                  : forwardSourceMessageText || "Медиа"}
              </span>
              <em className="composer__forward-hint">
                ПКМ — скрыть отправителя / текст. Send отправит в этот чат (текст
                писать не обязательно).
              </em>
            </div>
            <div className="composer__forward-actions">
              <button
                type="button"
                className="button button--ghost composer__forward-pick"
                onClick={() => setForwardPickerOpen(true)}
                title="Выбрать чат из списка"
              >
                Куда переслать?
              </button>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  closeForwardComposition();
                  setForwardPickerOpen(false);
                }}
                aria-label="Отменить пересылку"
                title="Отменить пересылку"
                disabled={isSubmittingAttachment}
              >
                ×
              </button>
            </div>
          </div>
        ) : null}
        {forwardComposition && forwardPickerOpen ? (
          <ForwardTargetPicker
            sourceChatId={forwardComposition.sourceChatId}
            sourceMessageIds={forwardComposition.sourceMessageIds}
            hideSenderName={forwardComposition.hideSenderName}
            hideCaption={forwardComposition.hideCaption}
            onClose={() => setForwardPickerOpen(false)}
          />
        ) : null}
        {annotatingIndex !== null && pendingAttachments[annotatingIndex]?.kind === "photo"
          ? createPortal(
              <PhotoAnnotator
                file={pendingAttachments[annotatingIndex]!.file}
                onCancel={() => setAnnotatingIndex(null)}
                onApply={(nextFile) => {
                  const target = pendingAttachments[annotatingIndex];
                  if (!target) {
                    setAnnotatingIndex(null);
                    return;
                  }
                  // Старый blob-URL освобождаем ЗДЕСЬ, а не внутри апдейтера
                  // состояния: апдейтер React может вызвать дважды, и превью
                  // отвалилось бы (revoke уже отпущенного URL).
                  revokeAttachmentPreview(target);
                  const nextPreviewUrl = URL.createObjectURL(nextFile);
                  setPendingAttachments((previous) =>
                    previous.map((entry, entryIndex) =>
                      entryIndex === annotatingIndex
                        ? { ...entry, file: nextFile, previewUrl: nextPreviewUrl }
                        : entry
                    )
                  );
                  setAnnotatingIndex(null);
                }}
              />,
              document.body
            )
          : null}
        {pendingAttachments.length > 0 ? (
          <div className="composer__attachments">
            {pendingAttachments.map((attachment, index) => (
              <div className="composer__attachment" key={`${attachment.file.name}:${index}`}>
                {attachment.previewUrl && attachment.kind !== "voice" ? (
                  // Клик по миниатюре — на весь экран, как в оригинальном TG.
                  <button
                    type="button"
                    className="composer__attachment-open"
                    onClick={() =>
                      openMediaViewer([
                        {
                          kind:
                            attachment.kind === "video" || attachment.kind === "videoNote"
                              ? "video"
                              : "photo",
                          url: attachment.previewUrl!,
                          ...(attachment.file.type ? { mimeType: attachment.file.type } : {}),
                          senderLabel: attachment.file.name,
                          local: true
                        }
                      ])
                    }
                    title="Открыть на весь экран"
                    aria-label="Открыть вложение на весь экран"
                  >
                    {attachment.kind === "video" || attachment.kind === "videoNote" ? (
                      <video
                        src={attachment.previewUrl}
                        className={
                          attachment.kind === "videoNote"
                            ? "composer__attachment-preview composer__attachment-preview--circle"
                            : "composer__attachment-preview"
                        }
                        muted
                      />
                    ) : (
                      <img
                        src={attachment.previewUrl}
                        alt={attachment.file.name}
                        className="composer__attachment-preview"
                      />
                    )}
                  </button>
                ) : (
                  <div className="composer__attachment-icon">
                    {attachment.kind === "voice" ? "AUDIO" : "FILE"}
                  </div>
                )}
                <div className="composer__attachment-meta">
                  <strong>{attachment.file.name}</strong>
                  <span>
                    {attachment.kind === "photo"
                      ? "Фотография"
                      : attachment.kind === "video"
                        ? "Видео"
                        : attachment.kind === "videoNote"
                          ? "Кружок"
                          : attachment.kind === "voice"
                            ? "Голосовое"
                            : "Документ"}
                  </span>
                  {attachment.kind === "photo" ? (
                    <button
                      type="button"
                      className="composer__attachment-annotate"
                      onClick={() => setAnnotatingIndex(index)}
                      disabled={isSubmittingAttachment}
                      title="Нарисовать стрелки-указатели на фото"
                    >
                      Стрелки
                    </button>
                  ) : null}
                  {attachment.kind === "video" || attachment.kind === "videoNote" ? (
                    <label
                      className="composer__attachment-circle-toggle"
                      title="Отправить как кружок (круглое видеосообщение). Telegram обрежет видео по кругу; подпись у кружков не поддерживается."
                    >
                      <input
                        type="checkbox"
                        checked={attachment.kind === "videoNote"}
                        onChange={(event) => {
                          if (event.target.checked && attachment.file.size > VIDEO_NOTE_MAX_BYTES) {
                            setComposerError(videoNoteTooBigMessage(attachment.file.size));
                            return;
                          }
                          const nextKind: MediaUploadKind = event.target.checked
                            ? "videoNote"
                            : "video";
                          setComposerError(null);
                          setPendingAttachments((previous) =>
                            previous.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, kind: nextKind } : entry
                            )
                          );
                        }}
                        disabled={isSubmittingAttachment}
                      />
                      <span>Кружок</span>
                    </label>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => {
                    revokeAttachmentPreview(attachment);
                    setPendingAttachments((previous) =>
                      previous.filter((_, itemIndex) => itemIndex !== index)
                    );
                  }}
                  aria-label="Убрать вложение"
                  disabled={isSubmittingAttachment}
                >
                  <i className="ic ic-close" aria-hidden="true" />
                </button>
              </div>
            ))}
            {pendingAttachments.length > 1 &&
            pendingAttachments.every(
              (attachment) =>
                attachment.kind === "photo" || attachment.kind === "video"
            ) ? (
              <label className="composer__group-toggle" title="Отправить фото/видео одним сообщением-альбомом">
                <input
                  type="checkbox"
                  checked={composerGroupMedia}
                  onChange={(event) => setComposerGroupMedia(event.target.checked)}
                  disabled={isSubmittingAttachment}
                />
                <span>Группировать в альбом</span>
              </label>
            ) : null}
          </div>
        ) : null}
        <div className="composer__row">
          {/* Кнопка «🎙» удалена по запросу. Скрепка возвращена по ТЗ
              2026-09-09 («нет возможности грузить видео»): вставка из буфера
              берёт только картинки, а перетаскивание мышью саппортам
              неочевидно. Даёт ровно то же, что и drag-and-drop, — тот же
              attachFiles, те же лимиты, то же логирование медиа не из
              шаблонов для админа. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="composer__file-input"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,.csv,application/*"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.currentTarget.value = "";
              if (files.length > 0) {
                void attachFiles(files);
              }
            }}
          />
          <div className="field">
          <button
            type="button"
            className="cbtn composer__attach-trigger"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Прикрепить файл"
            title="Прикрепить фото, видео или файл"
            disabled={!selectedChatId || isSubmittingAttachment || isRecordingVoice}
          >
            <i className="ic ic-attach" aria-hidden="true" />
          </button>
          {/* Перевод черновика: один значок вместо кнопки с подписью и двух
              селекторов — они переехали в верхнюю панель чата, поле ввода
              из-за них было слишком узким. Повторный клик (пока в поле
              ровно перевод) возвращает русский оригинал, как и Ctrl/Cmd+Z. */}
          <button
            type="button"
            className={
              outgoingTranslated && draft === outgoingTranslated.translated
                ? "cbtn composer__translate-draft composer__translate-draft--on"
                : "cbtn composer__translate-draft"
            }
            onClick={() => {
              if (outgoingTranslated && draft === outgoingTranslated.translated) {
                const history = draftHistoryRef.current;
                history.past.push({ value: draft, start: draft.length, end: draft.length });
                history.future = [];
                history.lastEditAt = 0;
                setDraft(outgoingTranslated.original);
                setOutgoingTranslated(null);
                requestAnimationFrame(() => composerTextareaRef.current?.focus());
                return;
              }
              void translateDraftForSending();
            }}
            disabled={
              !selectedChatId ||
              isTranslatingOutgoing ||
              isRecordingVoice ||
              draft.trim().length === 0
            }
            aria-label="Перевести текст перед отправкой"
            title={
              outgoingTranslated && draft === outgoingTranslated.translated
                ? "Вернуть в поле русский оригинал (или Ctrl+Z)"
                : outgoingLang.length === 0
                  ? "Выберите язык клиента: кнопка языков в шапке чата"
                  : `Перевести написанное на «${outgoingLang.toUpperCase()}» (Ctrl+Shift+Enter). Отправка — обычной кнопкой; вернуть русский — повторный клик или Ctrl+Z.`
            }
          >
            <i className="ic ic-translate" aria-hidden="true" />
          </button>
          <div className="composer-picker-anchor">
            <button
              type="button"
              className="cbtn composer__emoji-trigger"
              onClick={() => setPickerOpen((value) => !value)}
              aria-label="Эмодзи и стикеры"
              title="Эмодзи и стикеры"
              disabled={!selectedChatId || isSubmittingAttachment || isRecordingVoice}
            >
              <i className="ic ic-smile" aria-hidden="true" />
            </button>
            <ComposerPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onEmoji={(value) => {
                // Вставка в текущую позицию каретки textarea. Без selectionStart/End
                // вставляем в конец — поведение, привычное по «type any character».
                const target = composerTextareaRef.current;
                if (!target) {
                  setDraft(draft + value);
                  return;
                }
                const start = target.selectionStart ?? draft.length;
                const end = target.selectionEnd ?? draft.length;
                const next = draft.slice(0, start) + value + draft.slice(end);
                setDraft(next);
                // Возвращаем фокус и ставим каретку сразу после вставленного emoji,
                // чтобы можно было сразу продолжить ввод текста.
                requestAnimationFrame(() => {
                  target.focus();
                  const caret = start + value.length;
                  target.setSelectionRange(caret, caret);
                });
              }}
              onSticker={(sticker) => {
                if (!selectedChatId) {
                  return;
                }
                // Закрываем сразу: иначе при флуде кликов пользователь отправит
                // несколько стикеров подряд. Тапнул — отправилось — закрылось.
                setPickerOpen(false);
                sendMessage(selectedChatId, {
                  kind: "sticker",
                  fileId: sticker.fileId,
                  ...(sticker.emoji ? { emoji: sticker.emoji } : {}),
                  ...(typeof sticker.width === "number" ? { width: sticker.width } : {}),
                  ...(typeof sticker.height === "number" ? { height: sticker.height } : {})
                });
              }}
              stickerSets={stickerSets}
              stickersLoading={stickerSetsLoading}
              requestStickerSets={loadStickerSets}
            />
          </div>
          <textarea
            ref={composerTextareaRef}
            // Маркер для focusChatComposer (lib/composer-focus.ts).
            data-chat-composer=""
            // Как и пузырь сообщения: арабский/иврит набирается справа налево,
            // цифры и латиница внутри фразы остаются на своих местах.
            dir="auto"
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleDraftKeyDown}
            onClick={(event) => rememberDraftSelection(event.currentTarget)}
            onKeyUp={(event) => rememberDraftSelection(event.currentTarget)}
            onContextMenu={(event) => {
              // Своё меню — только когда есть что предложить (выделение или
              // курсор на ссылке); иначе остаётся нативное (вставка и т.п.).
              const el = event.currentTarget;
              const start = el.selectionStart;
              const end = el.selectionEnd;
              if (start === end && !findMarkdownLinkAt(el.value, start, end)) {
                return;
              }
              event.preventDefault();
              setComposerLinkMenu({ x: event.clientX, y: event.clientY, start, end });
            }}
            onPaste={(event) => {
              if (!selectedChatId || isSubmittingAttachment || isRecordingVoice) {
                return;
              }
              // Саппортам paste открыт (решение 2026-08-03): не-шаблонные медиа
              // теперь не блокируются, а логируются для админа. Собираем ВСЕ
              // файлы из буфера — можно вставить несколько картинок разом.
              const items = event.clipboardData?.items;
              if (!items) {
                return;
              }
              const files: File[] = [];
              for (let i = 0; i < items.length; i += 1) {
                const item = items[i];
                if (!item || item.kind !== "file") {
                  continue;
                }
                const file = item.getAsFile();
                // Видео из буфера тоже берём: раньше фильтр пропускал только
                // image/*, и вставленный ролик молча исчезал (ТЗ 2026-09-09).
                if (
                  file &&
                  (file.type.startsWith("image/") || file.type.startsWith("video/"))
                ) {
                  files.push(file);
                }
              }
              if (files.length > 0) {
                event.preventDefault();
                void attachFiles(files);
              }
            }}
            placeholder={
              selectedChatId
                ? editingMessageId
                  ? editingTarget && editingTarget.content.kind !== "text"
                    ? "Подпись к медиа (можно оставить пустой)"
                    : "Измените текст сообщения"
                  : pendingAttachments.length > 0
                    ? "Добавьте подпись"
                    : replyToMessageId
                      ? "Введите ответ"
                      : "Введите сообщение"
                : "Сначала выберите диалог"
            }
            rows={1}
            className="composer__textarea"
            disabled={!selectedChatId || isSubmittingAttachment || isRecordingVoice}
          />
          </div>
          <button
            type="submit"
            disabled={
              !selectedChatId ||
              isSubmittingAttachment ||
              isRecordingVoice ||
              // Пересылка без текста — легальна (как в TG): Send уходит
              // с одной только плашкой forwardComposition.
              (draft.trim().length === 0 &&
                pendingAttachments.length === 0 &&
                !forwardComposition &&
                // Правка медиа: пустая подпись — легальный результат.
                !(editingTarget && editingTarget.content.kind !== "text"))
            }
            className="mic composer__send"
            aria-label={
              isSubmittingAttachment
                ? "Загрузка…"
                : editingMessageId
                  ? "Сохранить"
                  : pendingAttachments.length > 0
                    ? "Отправить файл"
                    : "Отправить"
            }
            title={
              isSubmittingAttachment
                ? "Загрузка…"
                : editingMessageId
                  ? "Сохранить"
                  : pendingAttachments.length > 0
                    ? "Отправить файл · ПКМ — отправить позже"
                    : "Отправить · ПКМ — отправить позже"
            }
            onContextMenu={(event) => {
              // Как в оригинальном TG: ПКМ по «Отправить» → «Отправить позже».
              event.preventDefault();
              if (!selectedChatId || editingMessageId || isSubmittingAttachment || isRecordingVoice) {
                return;
              }
              if (draft.trim().length === 0 && pendingAttachments.length === 0) {
                return;
              }
              setSendMenu({ x: event.clientX, y: event.clientY });
            }}
          >
            {isSubmittingAttachment ? "…" : <i className="ic ic-send" aria-hidden="true" />}
          </button>
        </div>
        {composerError ? <div className="composer__error">{composerError}</div> : null}
        {outgoingTranslationError ? (
          <div className="composer__error">{outgoingTranslationError}</div>
        ) : null}
        {/* Строка сверки под композером: пока в поле лежит перевод, показываем
            обратный перевод — «что прочитает клиент». Место под самим полем,
            а не в его ряду: ширину ввода это не отнимает. */}
        {isTranslatingOutgoing ? (
          <div className="composer__translate-hint">Перевожу и сверяю…</div>
        ) : outgoingTranslated && draft === outgoingTranslated.translated ? (
          <div className="composer__translate-hint">
            <span className="composer__translate-hint-mark">
              {outgoingTranslated.corrected ? "✓ перевод уточнён" : "✓ проверено"}
            </span>
            {outgoingTranslated.back ? (
              <> · клиент прочтёт: «{outgoingTranslated.back}»</>
            ) : (
              <> · обратный перевод не получен</>
            )}
          </div>
        ) : null}
      </form>
      {selectedChatId && trustOpen ? (
        <div className="trust-center trust-center--below-composer">
          <div className="trust-center__header">
            <div>
              <strong>{trustTab === "scripts" ? "Скрипты" : "Шаблоны"}</strong>
              <span>
                {trustTab === "scripts"
                  ? "Ваши личные текстовые заготовки — отправка одним кликом."
                  : trustHasSections
                    ? "Выберите раздел и подраздел — внутри готовые шаблоны для отправки."
                    : "Готовые медиа с подписями. Можно отправлять в текущий чат."}
              </span>
            </div>
            <div className="trust-center__header-actions">
              {trustTab === "assets" ? (
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => void loadTrustAssets()}
                  disabled={isTrustLoading}
                  aria-label="Обновить шаблоны"
                  title="Обновить шаблоны"
                >
                  <i className="ic ic-refresh" aria-hidden="true" />
                </button>
              ) : null}
              {/* Свой крестик: в редизайне ряд кнопок «Шаблоны/Теги/…» над
                  лентой скрыт, и панель, открытую из «Управление шаблонами»,
                  было нечем закрыть (жалоба 2026-09-25). */}
              <button
                type="button"
                className="icon-button trust-center__close"
                onClick={() => setActivePanel(null)}
                aria-label="Закрыть панель шаблонов"
                title="Закрыть (Esc)"
              >
                <i className="ic ic-close" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div
            className="settings-pref__segmented trust-center__tabs"
            role="tablist"
            aria-label="Шаблоны или скрипты"
          >
            <button
              type="button"
              role="tab"
              aria-selected={trustTab === "assets"}
              className={
                trustTab === "assets"
                  ? "settings-pref__segment settings-pref__segment--active"
                  : "settings-pref__segment"
              }
              onClick={() => setTrustTab("assets")}
            >
              Шаблоны
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={trustTab === "scripts"}
              className={
                trustTab === "scripts"
                  ? "settings-pref__segment settings-pref__segment--active"
                  : "settings-pref__segment"
              }
              onClick={() => setTrustTab("scripts")}
            >
              Скрипты
            </button>
          </div>
          {trustTab === "scripts" ? (
            <SupportScriptsPanel
              wsUrl={wsUrl}
              canSend={Boolean(selectedChatId)}
              onSendText={(text) => {
                if (selectedChatId) {
                  sendMessage(selectedChatId, { kind: "text", text });
                  focusChatComposer();
                }
              }}
            />
          ) : (
            <>
          {trustHasSections || isAdminRole
            ? (() => {
                const currentSection =
                  trustNavResolved.mode === "browse" && trustNavResolved.sectionId
                    ? trustSections.find(
                        (section) => section.sectionId === trustNavResolved.sectionId
                      ) ?? null
                    : null;
                const parentOfCurrent = currentSection?.parentId
                  ? trustSections.find(
                      (section) => section.sectionId === currentSection.parentId
                    ) ?? null
                  : null;
                const atRoot =
                  trustNavResolved.mode === "browse" &&
                  trustNavResolved.sectionId === null;
                const childSections =
                  trustNavResolved.mode === "browse"
                    ? trustNavResolved.sectionId === null
                      ? trustRootSections
                      : trustSections.filter(
                          (section) => section.parentId === trustNavResolved.sectionId
                        )
                    : [];
                const breadcrumb =
                  trustNavResolved.mode === "all"
                    ? "Все шаблоны"
                    : trustNavResolved.mode === "unsorted"
                      ? "Без раздела"
                      : currentSection
                        ? parentOfCurrent
                          ? `${parentOfCurrent.title} / ${currentSection.title}`
                          : currentSection.title
                        : null;
                return (
                  <div className="trust-center__sections">
                    {!atRoot ? (
                      <div className="trust-center__nav-row">
                        <button
                          type="button"
                          className="button button--ghost trust-center__back"
                          onClick={() =>
                            setTrustNav(
                              trustNavResolved.mode === "browse" && currentSection
                                ? {
                                    mode: "browse",
                                    sectionId: currentSection.parentId
                                  }
                                : { mode: "browse", sectionId: null }
                            )
                          }
                        >
                          <i className="ic ic-back" aria-hidden="true" /> Назад
                        </button>
                        {breadcrumb ? (
                          <span className="trust-center__breadcrumb">{breadcrumb}</span>
                        ) : null}
                      </div>
                    ) : null}
                    {childSections.map((section) => (
                      <div className="trust-center__section-row" key={section.sectionId}>
                        <button
                          type="button"
                          className="trust-center__section-open"
                          onClick={() =>
                            setTrustNav({ mode: "browse", sectionId: section.sectionId })
                          }
                        >
                          <i
                            className={
                              section.parentId === null
                                ? "ic ic-folder-filled"
                                : "ic ic-folder"
                            }
                            aria-hidden="true"
                          />
                          <span className="trust-center__section-title">
                            {section.title}
                          </span>
                          <span className="trust-center__section-count">
                            {countTrustSectionDeep(section.sectionId)}
                          </span>
                        </button>
                        {isAdminRole ? (
                          <>
                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => void renameTrustSection(section)}
                              aria-label="Переименовать"
                              title="Переименовать"
                            >
                              <i className="ic ic-edit" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="icon-button icon-button--danger"
                              onClick={() => void deleteTrustSection(section)}
                              aria-label={
                                section.parentId === null
                                  ? "Удалить раздел"
                                  : "Удалить подраздел"
                              }
                              title={
                                section.parentId === null
                                  ? "Удалить раздел"
                                  : "Удалить подраздел"
                              }
                            >
                              <i className="ic ic-delete" aria-hidden="true" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    ))}
                    {atRoot && trustHasSections && trustAssets.length > 0 ? (
                      <div className="trust-center__section-row" key="__all__">
                        <button
                          type="button"
                          className="trust-center__section-open"
                          onClick={() => setTrustNav({ mode: "all" })}
                          title="Плоский список всех шаблонов без разделов"
                        >
                          <i className="ic ic-chats-filled" aria-hidden="true" />
                          <span className="trust-center__section-title">Все шаблоны</span>
                          <span className="trust-center__section-count">
                            {trustAssets.length}
                          </span>
                        </button>
                      </div>
                    ) : null}
                    {atRoot && trustHasSections && trustSectionCounts.orphans > 0 ? (
                      <div className="trust-center__section-row" key="__unsorted__">
                        <button
                          type="button"
                          className="trust-center__section-open"
                          onClick={() => setTrustNav({ mode: "unsorted" })}
                        >
                          <i className="ic ic-folder" aria-hidden="true" />
                          <span className="trust-center__section-title">Без раздела</span>
                          <span className="trust-center__section-count">
                            {trustSectionCounts.orphans}
                          </span>
                        </button>
                      </div>
                    ) : null}
                    {isAdminRole &&
                    trustNavResolved.mode === "browse" &&
                    (atRoot || (currentSection && currentSection.parentId === null)) ? (
                      <div className="trust-center__section-create">
                        <input
                          value={trustNewSectionTitle}
                          onChange={(event) =>
                            setTrustNewSectionTitle(event.target.value)
                          }
                          placeholder={
                            atRoot
                              ? "Название нового раздела (Comsa, 1-line, Общее…)"
                              : "Название нового подраздела (Фото, Голосовые…)"
                          }
                          disabled={isSavingTrustSection}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              void createTrustSection(
                                atRoot ? null : currentSection?.sectionId ?? null
                              );
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() =>
                            void createTrustSection(
                              atRoot ? null : currentSection?.sectionId ?? null
                            )
                          }
                          disabled={
                            isSavingTrustSection ||
                            trustNewSectionTitle.trim().length === 0
                          }
                        >
                          <i className="ic ic-plus" aria-hidden="true" />{" "}
                          {atRoot ? "Создать раздел" : "Создать подраздел"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })()
            : null}
          <input
            ref={trustFileInputRef}
            type="file"
            multiple
            className="composer__file-input"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,.csv,application/*"
            onChange={async (event) => {
              const files = Array.from(event.target.files ?? []);
              event.currentTarget.value = "";
              if (files.length === 0) {
                return;
              }

              setTrustError(null);
              const nextAttachments: PendingAttachment[] = [];
              for (const file of files) {
                const autoKind = pickAttachmentKind(file);
                // Выбран тип «Кружок» и файл — видео: сохраняем как
                // видеосообщение, а не обычное видео.
                const kind =
                  trustMediaKind === "videoNote" && autoKind === "video"
                    ? ("videoNote" as MediaUploadKind)
                    : autoKind;
                if (kind === "videoNote" && file.size > VIDEO_NOTE_MAX_BYTES) {
                  setTrustError(videoNoteTooBigMessage(file.size));
                  continue;
                }
                const previewUrl =
                  kind === "photo" ||
                  kind === "video" ||
                  kind === "voice" ||
                  kind === "videoNote"
                    ? URL.createObjectURL(file)
                    : null;
                nextAttachments.push({
                  file,
                  kind,
                  previewUrl,
                  ...(await readAttachmentMetadata(file, kind, previewUrl))
                });
              }
              // Добавляем к уже выбранным (мультифото), но не больше лимита
              // одного альбома Telegram — 10 контентов.
              setTrustAttachments((current) => {
                const merged = [...current, ...nextAttachments];
                if (merged.length > 10) {
                  merged
                    .slice(10)
                    .forEach((attachment) => revokeAttachmentPreview(attachment));
                }
                return merged.slice(0, 10);
              });
              const first = nextAttachments[0];
              if (first && trustMediaKind !== "payment") {
                setTrustMediaKind(first.kind);
              }
              if (first && trustTitle.trim().length === 0) {
                const fileName = first.file.name.replace(/\.[^.]+$/, "").trim();
                if (fileName.length > 0) {
                  setTrustTitle(fileName);
                }
              }
            }}
          />
          {!isSupportRole ? (
            <div className="trust-center__create-toggle">
              <button
                type="button"
                className={
                  trustCreateOpen
                    ? "button button--ghost trust-center__create-toggle-button trust-center__create-toggle-button--active"
                    : "button button--ghost trust-center__create-toggle-button"
                }
                onClick={() =>
                  setTrustCreateOpen((value) => {
                    if (!value) {
                      // Открываем форму из подраздела — шаблон по умолчанию
                      // сохранится именно сюда.
                      setTrustCreateSectionId(
                        trustNavResolved.mode === "browse"
                          ? trustNavResolved.sectionId ?? ""
                          : ""
                      );
                    }
                    return !value;
                  })
                }
                aria-expanded={trustCreateOpen}
              >
                {trustCreateOpen ? (
                  <>
                    <i className="ic ic-close" aria-hidden="true" /> Скрыть форму
                  </>
                ) : (
                  <>
                    <i className="ic ic-plus" aria-hidden="true" /> Создать шаблон
                  </>
                )}
              </button>
            </div>
          ) : null}
          {!isSupportRole && trustCreateOpen ? (
            <div className="trust-center__create">
              <div className="trust-center__create-row">
                <input
                  value={trustTitle}
                  onChange={(event) => setTrustTitle(event.target.value)}
                  placeholder="Название шаблона"
                  disabled={isSavingTrustAsset}
                />
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => trustFileInputRef.current?.click()}
                  disabled={isSavingTrustAsset}
                >
                  Выбрать файл
                </button>
              </div>
              <input
                value={trustDescription}
                onChange={(event) => setTrustDescription(event.target.value)}
                placeholder="Подпись к файлу"
                disabled={isSavingTrustAsset}
              />
              <textarea
                value={trustSupportDescription}
                onChange={(event) => setTrustSupportDescription(event.target.value)}
                placeholder="Внутренняя заметка: что это за файл и когда его отправлять"
                disabled={isSavingTrustAsset}
                rows={3}
              />
              <div className="trust-center__create-row">
                <select
                  value={trustMediaKind}
                  onChange={(event) =>
                    setTrustMediaKind(
                      event.target.value as MediaUploadKind | "link" | "payment"
                    )
                  }
                  disabled={isSavingTrustAsset}
                >
                  <option value="photo">Фото</option>
                  <option value="video">Видео</option>
                  <option value="videoNote">Кружок (видеосообщение)</option>
                  <option value="document">Документы</option>
                  <option value="voice">Голосовые</option>
                  <option value="link">Ссылка</option>
                  <option value="payment">Реквизит (карта/кошелёк/IBAN)</option>
                </select>
                <span className="trust-center__hint">
                  Тип шаблона.
                </span>
              </div>
              {trustHasSections ? (
                <div className="trust-center__create-row">
                  <select
                    value={trustCreateSectionId}
                    onChange={(event) => setTrustCreateSectionId(event.target.value)}
                    disabled={isSavingTrustAsset}
                  >
                    <option value="">Без раздела</option>
                    {trustRootSections.map((root) => {
                      const children = trustSections.filter(
                        (section) => section.parentId === root.sectionId
                      );
                      return (
                        <optgroup label={root.title} key={root.sectionId}>
                          <option value={root.sectionId}>
                            {root.title} — прямо в раздел
                          </option>
                          {children.map((child) => (
                            <option value={child.sectionId} key={child.sectionId}>
                              {child.title}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  <span className="trust-center__hint">
                    Раздел, в котором шаблон увидит саппорт.
                  </span>
                </div>
              ) : null}
              {trustMediaKind === "link" ? (
                <input
                  type="url"
                  value={trustLinkUrl}
                  onChange={(event) => setTrustLinkUrl(event.target.value)}
                  placeholder="https://example.com/payment"
                  disabled={isSavingTrustAsset}
                />
              ) : null}
              {trustMediaKind === "payment" ? (
                <>
                  <textarea
                    value={trustPaymentText}
                    onChange={(event) => setTrustPaymentText(event.target.value)}
                    placeholder="4276 1234 5678 9012  или  TRX-кошелёк  или  IBAN"
                    rows={2}
                    disabled={isSavingTrustAsset}
                  />
                  <div className="trust-center__create-row">
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => trustFileInputRef.current?.click()}
                      disabled={isSavingTrustAsset}
                    >
                      Прикрепить фото
                    </button>
                    <span className="trust-center__hint">
                      Фото (QR/скрин) уйдёт вместе с реквизитом в подписи.
                    </span>
                  </div>
                </>
              ) : null}
              {trustMediaKind !== "link" && trustAttachments.length > 1 ? (
                <label className="trust-center__group-toggle">
                  <input
                    type="checkbox"
                    className="trust-center__select-check"
                    checked={trustGroupMedia}
                    onChange={(event) => setTrustGroupMedia(event.target.checked)}
                    disabled={isSavingTrustAsset}
                  />
                  <span>Группировать в одно сообщение (альбом)</span>
                </label>
              ) : null}
              {trustMediaKind !== "link" && trustAttachments.length > 0 ? (
                <div className="trust-center__selected-list">
                  {trustAttachments.map((attachment, index) => (
                    <div
                      className="trust-center__selected"
                      key={`${attachment.file.name}:${index}`}
                    >
                      <strong>{attachment.file.name}</strong>
                      <span>
                        {attachment.kind === "photo"
                          ? "Фотография"
                          : attachment.kind === "video"
                            ? "Видео"
                            : attachment.kind === "voice"
                              ? "Голосовое"
                              : "Документ"}
                      </span>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => {
                          revokeAttachmentPreview(attachment);
                          setTrustAttachments((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index)
                          );
                        }}
                        aria-label="Убрать файл"
                        disabled={isSavingTrustAsset}
                      >
                        <i className="ic ic-close" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="trust-center__create-actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => void createTrustAsset()}
                  disabled={
                    isSavingTrustAsset ||
                    (trustMediaKind === "link"
                      ? trustLinkUrl.trim().length === 0
                      : trustMediaKind === "payment"
                        ? trustPaymentText.trim().length < 4
                        : trustAttachments.length === 0)
                  }
                >
                  {isSavingTrustAsset ? "Сохраняем…" : "Добавить шаблон"}
                </button>
                <span>Создавать может администратор.</span>
              </div>
            </div>
          ) : null}
          {trustError ? <div className="chat-inspector__error">{trustError}</div> : null}
          {isAdminRole && trustAssetsVisible && filteredTrustAssets.length > 0 ? (
            <div className="trust-center__select-bar">
              {!trustSelectMode ? (
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => {
                    setTrustSelectMode(true);
                    setTrustSelectedIds(new Set());
                  }}
                >
                  Выбрать
                </button>
              ) : (
                <>
                  <span className="trust-center__select-count">
                    Выбрано: {trustSelectedIds.size}
                  </span>
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() =>
                      setTrustSelectedIds(
                        new Set(filteredTrustAssets.map((asset) => asset.assetId))
                      )
                    }
                  >
                    Выбрать все
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={trustSelectedIds.size === 0 || isTrustBulkBusy}
                    onClick={() =>
                      setTrustSectionPicker({
                        mode: "move",
                        assetIds: [...trustSelectedIds]
                      })
                    }
                  >
                    Переместить
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={trustSelectedIds.size === 0 || isTrustBulkBusy}
                    onClick={() =>
                      setTrustSectionPicker({
                        mode: "copy",
                        assetIds: [...trustSelectedIds]
                      })
                    }
                  >
                    Скопировать
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => {
                      setTrustSelectMode(false);
                      setTrustSelectedIds(new Set());
                    }}
                  >
                    Отмена
                  </button>
                </>
              )}
            </div>
          ) : null}
          {trustAssetsVisible ? (
            <div className="trust-center__filters">
              <div className="chat-search__scopes">
                {[
                  ["all", "Все"],
                  ["photo", "Фото"],
                  ["video", "Видео"],
                  ["document", "Документы"],
                  ["voice", "Голосовые"],
                  ["link", "Ссылки"],
                  ["payment", "Способы оплаты"]
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={
                      trustFilterKind === value
                        ? "chat-search__scope chat-search__scope--active"
                        : "chat-search__scope"
                    }
                    onClick={() =>
                      setTrustFilterKind(
                        value as "all" | MediaUploadKind | "link" | "payment"
                      )
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <input
                value={trustFilterQuery}
                onChange={(event) => setTrustFilterQuery(event.target.value)}
                placeholder="Поиск по названию и описаниям"
                disabled={isTrustLoading}
              />
            </div>
          ) : null}
          {isTrustLoading ? (
            <div className="trust-center__empty">Загружаем…</div>
          ) : !trustAssetsVisible ? null : filteredTrustAssets.length === 0 ? (
            <div className="trust-center__empty">
              {trustAssets.length === 0
                ? "Шаблонов пока нет."
                : normalizedTrustQuery.length > 0 || trustFilterKind !== "all"
                  ? "Ничего не найдено."
                  : "В этом подразделе шаблонов пока нет."}
            </div>
          ) : (
            <div className="trust-center__list">
              {filteredTrustAssets.map((asset) => {
                // Feat 5: тумба фото/видео в списке шаблонов. Для только что
                // загруженных ассетов у content нет url/thumbnailUrl (эти поля
                // проставляются лишь когда сообщение приходит с сервера через
                // TDLib update). Fallback — прямой линк на upload-токен через
                // /api/media/file/<fileId>, тот же endpoint что и для реальных
                // медиа-файлов; token декодирует локальный путь в uploads.
                const previewUrl = (() => {
                  if (
                    asset.content.kind !== "photo" &&
                    asset.content.kind !== "video"
                  ) {
                    return undefined;
                  }
                  const direct = asset.content.url ?? asset.content.thumbnailUrl;
                  if (direct && direct.length > 0) return direct;
                  const fileId = asset.content.fileId;
                  if (typeof fileId === "string" && fileId.length > 0) {
                    return `${httpBaseFromWsUrl(wsUrl)}/api/media/file/${encodeURIComponent(fileId)}`;
                  }
                  return undefined;
                })();
                const extraCount = asset.extraContents?.length ?? 0;
                const usedEntry = trustUsage[asset.assetId];
                return (
                  <div
                    key={asset.assetId}
                    className={
                      usedEntry
                        ? "trust-center__item trust-center__item--used"
                        : "trust-center__item"
                    }
                  >
                    {trustSelectMode ? (
                      <input
                        type="checkbox"
                        className="trust-center__select-check"
                        checked={trustSelectedIds.has(asset.assetId)}
                        onChange={() => toggleTrustSelected(asset.assetId)}
                        aria-label={`Выбрать шаблон «${asset.title}»`}
                      />
                    ) : null}
                    {previewUrl ? (
                      <button
                        type="button"
                        className="trust-center__thumb-btn"
                        onClick={() => openPhotoViewer(previewUrl)}
                        title="Открыть в полный размер"
                      >
                        <img
                          src={previewUrl}
                          alt={asset.title}
                          className="trust-center__thumb"
                          loading="lazy"
                          decoding="async"
                        />
                        {extraCount > 0 ? (
                          <span className="trust-center__thumb-count">
                            +{extraCount}
                          </span>
                        ) : null}
                      </button>
                    ) : null}
                    <div className="trust-center__item-copy">
                      {usedEntry ? (
                        <span
                          className="trust-center__used"
                          title={`${
                            usedEntry.source === "manual"
                              ? "Пометка поставлена вручную"
                              : "Отправляли из шаблонов"
                          } · ${formatFullDateTime(usedEntry.lastUsedAt, timeZone)}${
                            usedEntry.actorLogin ? ` · ${usedEntry.actorLogin}` : ""
                          }`}
                        >
                          Уже использовали в этом чате
                          {usedEntry.usedCount > 1 ? ` · ${usedEntry.usedCount}×` : ""}
                        </span>
                      ) : null}
                      <strong>
                        {asset.title}
                        <span className="trust-center__badge">
                          {asset.mediaKind === "photo"
                            ? "Фото"
                            : asset.mediaKind === "video"
                              ? "Видео"
                              : asset.mediaKind === "voice"
                                ? "Голосовое"
                                : asset.mediaKind === "link"
                                  ? "Ссылка"
                                  : asset.mediaKind === "payment"
                                    ? "Реквизит"
                                    : "Документ"}
                        </span>
                      </strong>
                      <span>{asset.description?.trim() || "Без описания"}</span>
                      <span className="trust-center__support">
                        {asset.supportDescription?.trim() ||
                          "Заметка не заполнена."}
                      </span>
                    </div>
                    <div className="trust-center__item-actions">
                      <button
                        type="button"
                        className={
                          usedEntry
                            ? "button button--ghost trust-center__used-toggle trust-center__used-toggle--on"
                            : "button button--ghost trust-center__used-toggle"
                        }
                        onClick={() => void toggleTrustAssetUsed(asset.assetId, !usedEntry)}
                        disabled={!selectedChatId || trustUsageBusyId === asset.assetId}
                        title={
                          usedEntry
                            ? "Снять пометку «уже использовали в этом чате»"
                            : "Пометить как уже использованный в этом чате"
                        }
                      >
                        {usedEntry ? "Снять" : "Пометить"}
                      </button>
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={() => {
                          // Feat 6: открыть редактор перед отправкой — вместо
                          // мгновенного send'а. Для медиа правим caption,
                          // для текста — сам текст.
                          const initialText =
                            asset.content.kind === "text"
                              ? asset.content.text
                              : (asset.content as { caption?: string }).caption ?? "";
                          setTemplateEditor({ asset, text: initialText });
                        }}
                        disabled={!selectedChatId}
                        title="Изменить перед отправкой"
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="button button--primary"
                        onClick={() => sendTrustAsset(asset)}
                        disabled={!selectedChatId || trustAssetSending}
                      >
                        Отправить
                      </button>
                      {isAdminRole ? (
                        <button
                          type="button"
                          className="icon-button trust-center__more"
                          onClick={(event) => {
                            event.stopPropagation();
                            setTrustMenu({
                              assetId: asset.assetId,
                              x: event.clientX,
                              y: event.clientY
                            });
                          }}
                          aria-label="Действия с шаблоном"
                          title="Действия с шаблоном"
                          aria-haspopup="menu"
                        >
                          ⋯
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>
      ) : null}
      {templateEditor
        ? createPortal(
            <div className="template-editor" role="dialog" aria-modal="true">
              <button
                type="button"
                className="template-editor__backdrop"
                aria-label="Закрыть редактор шаблона"
                onClick={() => setTemplateEditor(null)}
              />
              <div className="template-editor__panel">
                <div className="template-editor__header">
                  <strong>Предпросмотр перед отправкой</strong>
                  <span>{templateEditor.asset.title}</span>
                </div>
                {templateEditor.asset.content.kind === "photo" ||
                templateEditor.asset.content.kind === "video" ? (
                  (() => {
                    const previewUrl =
                      templateEditor.asset.content.url ??
                      templateEditor.asset.content.thumbnailUrl ??
                      "";
                    return previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={templateEditor.asset.title}
                        className="template-editor__preview"
                      />
                    ) : null;
                  })()
                ) : null}
                <label className="template-editor__label">
                  <span>
                    {templateEditor.asset.content.kind === "text"
                      ? "Текст"
                      : "Подпись (caption)"}
                  </span>
                  <textarea
                    className="template-editor__textarea"
                    value={templateEditor.text}
                    onChange={(event) =>
                      setTemplateEditor((prev) =>
                        prev ? { ...prev, text: event.target.value } : prev
                      )
                    }
                    rows={4}
                    autoFocus
                  />
                </label>
                <div className="template-editor__actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => setTemplateEditor(null)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={!selectedChatId || trustAssetSending}
                    onClick={() => {
                      if (!selectedChatId) return;
                      const asset = templateEditor.asset;
                      const editedText = templateEditor.text;
                      let content: MessageContent;
                      if (asset.content.kind === "text") {
                        const parsed = hasMarkdownSyntax(editedText)
                          ? parseMarkdown(editedText)
                          : { text: editedText, entities: [] };
                        content = {
                          kind: "text",
                          text: parsed.text,
                          ...(parsed.entities.length > 0
                            ? { entities: parsed.entities }
                            : {})
                        };
                      } else {
                        // Медиа: сохраняем оригинальные поля контента, меняем
                        // только caption. Пустая строка = отправка без
                        // подписи (в TDLib равнозначно удалению caption).
                        const trimmed = editedText.trim();
                        const { caption: _oldCaption, ...rest } = asset.content as
                          MessageContent & { caption?: string };
                        content = trimmed.length > 0
                          ? ({ ...rest, caption: trimmed } as MessageContent)
                          : (rest as MessageContent);
                      }
                      // sendTrustAsset сам закроет плавающую панель шаблонов
                      // и отправит альбомом, если у шаблона несколько медиа.
                      sendTrustAsset(asset, content);
                      setTemplateEditor(null);
                    }}
                  >
                    Отправить
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {trustMenu
        ? (() => {
            const menuAsset = trustAssets.find(
              (entry) => entry.assetId === trustMenu.assetId
            );
            if (!menuAsset) {
              return null;
            }
            const closeTrustMenu = (): void => setTrustMenu(null);
            return createPortal(
              <div
                className="dialog-context-menu__layer"
                onClick={closeTrustMenu}
                onContextMenu={(event) => {
                  event.preventDefault();
                  closeTrustMenu();
                }}
                role="presentation"
              >
                <ContextMenuPositioner anchorX={trustMenu.x} anchorY={trustMenu.y}>
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      setTrustSelectMode(true);
                      setTrustSelectedIds(new Set([menuAsset.assetId]));
                      closeTrustMenu();
                    }}
                  >
                    <i className="ic ic-check" aria-hidden="true" />
                    Выбрать
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      setTrustSectionPicker({
                        mode: "copy",
                        assetIds: [menuAsset.assetId]
                      });
                      closeTrustMenu();
                    }}
                  >
                    <i className="ic ic-copy" aria-hidden="true" />
                    Скопировать
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      setTrustSectionPicker({
                        mode: "move",
                        assetIds: [menuAsset.assetId]
                      });
                      closeTrustMenu();
                    }}
                  >
                    <i className="ic ic-forward" aria-hidden="true" />
                    Переместить
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => openTrustEdit(menuAsset)}
                  >
                    <i className="ic ic-edit" aria-hidden="true" />
                    Редактировать
                  </button>
                  <div className="dialog-context-menu__divider" />
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item dialog-context-menu__item--danger"
                    onClick={() => {
                      closeTrustMenu();
                      if (window.confirm(`Удалить шаблон «${menuAsset.title}»?`)) {
                        void deleteTrustAsset(menuAsset.assetId);
                      }
                    }}
                  >
                    <i className="ic ic-delete" aria-hidden="true" />
                    Удалить
                  </button>
                </ContextMenuPositioner>
              </div>,
              document.body
            );
          })()
        : null}
      {trustSectionPicker
        ? createPortal(
            <div className="template-editor" role="dialog" aria-modal="true">
              <button
                type="button"
                className="template-editor__backdrop"
                aria-label="Закрыть выбор раздела"
                onClick={() => setTrustSectionPicker(null)}
              />
              <div className="template-editor__panel">
                <div className="template-editor__header">
                  <strong>
                    {trustSectionPicker.mode === "move"
                      ? "Переместить в раздел"
                      : "Скопировать в раздел"}
                  </strong>
                  <span>
                    {trustSectionPicker.assetIds.length === 1
                      ? "1 шаблон"
                      : `Шаблонов: ${trustSectionPicker.assetIds.length}`}
                  </span>
                </div>
                <div className="trust-center__picker-list">
                  {trustSectionPicker.mode === "copy" ? (
                    <button
                      type="button"
                      className="trust-center__picker-item"
                      disabled={isTrustBulkBusy}
                      onClick={() => void applyTrustSectionPick("keep")}
                    >
                      В текущий раздел (рядом с оригиналом)
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="trust-center__picker-item"
                    disabled={isTrustBulkBusy}
                    onClick={() => void applyTrustSectionPick(null)}
                  >
                    Без раздела
                  </button>
                  {trustRootSections.map((root) => {
                    const children = trustSections.filter(
                      (section) => section.parentId === root.sectionId
                    );
                    return (
                      <div key={root.sectionId} className="trust-center__picker-group">
                        <button
                          type="button"
                          className="trust-center__picker-item"
                          disabled={isTrustBulkBusy}
                          onClick={() => void applyTrustSectionPick(root.sectionId)}
                        >
                          {root.title}
                        </button>
                        {children.map((child) => (
                          <button
                            key={child.sectionId}
                            type="button"
                            className="trust-center__picker-item trust-center__picker-item--child"
                            disabled={isTrustBulkBusy}
                            onClick={() => void applyTrustSectionPick(child.sectionId)}
                          >
                            {child.title}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <div className="template-editor__actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => setTrustSectionPicker(null)}
                    disabled={isTrustBulkBusy}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {trustEdit
        ? createPortal(
            <div className="template-editor" role="dialog" aria-modal="true">
              <button
                type="button"
                className="template-editor__backdrop"
                aria-label="Закрыть редактирование шаблона"
                onClick={() => {
                  trustEdit.newAttachments.forEach((attachment) =>
                    revokeAttachmentPreview(attachment)
                  );
                  setTrustEdit(null);
                }}
              />
              <div className="template-editor__panel">
                <div className="template-editor__header">
                  <strong>Редактирование шаблона</strong>
                  <span>Изменения сохранятся для всех</span>
                </div>
                <input
                  ref={trustEditFileInputRef}
                  type="file"
                  multiple
                  className="composer__file-input"
                  accept={
                    trustEdit.asset.mediaKind === "payment"
                      ? "image/*"
                      : "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,.csv,application/*"
                  }
                  onChange={async (event) => {
                    const files = Array.from(event.target.files ?? []);
                    event.currentTarget.value = "";
                    if (files.length === 0) {
                      return;
                    }
                    const additions: PendingAttachment[] = [];
                    for (const file of files) {
                      const kind = pickAttachmentKind(file);
                      const previewUrl =
                        kind === "photo" || kind === "video" || kind === "voice"
                          ? URL.createObjectURL(file)
                          : null;
                      additions.push({
                        file,
                        kind,
                        previewUrl,
                        ...(await readAttachmentMetadata(file, kind, previewUrl))
                      });
                    }
                    setTrustEdit((prev) =>
                      prev
                        ? {
                            ...prev,
                            newAttachments: [
                              ...prev.newAttachments,
                              ...additions
                            ].slice(0, 10)
                          }
                        : prev
                    );
                  }}
                />
                <label className="template-editor__label">
                  <span>Название</span>
                  <input
                    value={trustEdit.title}
                    onChange={(event) =>
                      setTrustEdit((prev) =>
                        prev ? { ...prev, title: event.target.value } : prev
                      )
                    }
                    disabled={isSavingTrustEdit}
                  />
                </label>
                {trustEdit.asset.mediaKind === "link" ? (
                  <label className="template-editor__label">
                    <span>URL</span>
                    <input
                      type="url"
                      value={trustEdit.textValue}
                      onChange={(event) =>
                        setTrustEdit((prev) =>
                          prev ? { ...prev, textValue: event.target.value } : prev
                        )
                      }
                      disabled={isSavingTrustEdit}
                    />
                  </label>
                ) : null}
                {trustEdit.asset.mediaKind === "payment" ? (
                  <label className="template-editor__label">
                    <span>Реквизит</span>
                    <textarea
                      className="template-editor__textarea"
                      value={trustEdit.textValue}
                      onChange={(event) =>
                        setTrustEdit((prev) =>
                          prev ? { ...prev, textValue: event.target.value } : prev
                        )
                      }
                      rows={2}
                      disabled={isSavingTrustEdit}
                    />
                  </label>
                ) : null}
                <label className="template-editor__label">
                  <span>
                    {trustEdit.asset.mediaKind === "link" ||
                    trustEdit.asset.mediaKind === "payment"
                      ? "Описание"
                      : "Подпись к файлу (caption)"}
                  </span>
                  <input
                    value={trustEdit.description}
                    onChange={(event) =>
                      setTrustEdit((prev) =>
                        prev ? { ...prev, description: event.target.value } : prev
                      )
                    }
                    disabled={isSavingTrustEdit}
                  />
                </label>
                <label className="template-editor__label">
                  <span>Внутренняя заметка</span>
                  <textarea
                    className="template-editor__textarea"
                    value={trustEdit.supportDescription}
                    onChange={(event) =>
                      setTrustEdit((prev) =>
                        prev
                          ? { ...prev, supportDescription: event.target.value }
                          : prev
                      )
                    }
                    rows={2}
                    disabled={isSavingTrustEdit}
                  />
                </label>
                {trustEdit.asset.mediaKind !== "link" ? (
                  <div className="trust-center__media-list">
                    {trustEdit.keptContents.map((content, index) => (
                      <div
                        className="trust-center__selected"
                        key={`kept:${index}`}
                      >
                        <strong>{trustContentLabel(content)}</strong>
                        <span>текущий файл</span>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() =>
                            setTrustEdit((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    keptContents: prev.keptContents.filter(
                                      (_, itemIndex) => itemIndex !== index
                                    )
                                  }
                                : prev
                            )
                          }
                          aria-label="Убрать файл из шаблона"
                          disabled={isSavingTrustEdit}
                        >
                          <i className="ic ic-close" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                    {trustEdit.newAttachments.map((attachment, index) => (
                      <div
                        className="trust-center__selected"
                        key={`new:${attachment.file.name}:${index}`}
                      >
                        <strong>{attachment.file.name}</strong>
                        <span>новый файл</span>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => {
                            revokeAttachmentPreview(attachment);
                            setTrustEdit((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    newAttachments: prev.newAttachments.filter(
                                      (_, itemIndex) => itemIndex !== index
                                    )
                                  }
                                : prev
                            );
                          }}
                          aria-label="Убрать новый файл"
                          disabled={isSavingTrustEdit}
                        >
                          <i className="ic ic-close" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => trustEditFileInputRef.current?.click()}
                      disabled={isSavingTrustEdit}
                    >
                      {trustEdit.asset.mediaKind === "payment"
                        ? "Добавить фото"
                        : "Добавить файлы"}
                    </button>
                    {trustEdit.keptContents.length + trustEdit.newAttachments.length > 1 ? (
                      <label className="trust-center__group-toggle">
                        <input
                          type="checkbox"
                          className="trust-center__select-check"
                          checked={trustEdit.groupMedia}
                          onChange={(event) =>
                            setTrustEdit((prev) =>
                              prev ? { ...prev, groupMedia: event.target.checked } : prev
                            )
                          }
                          disabled={isSavingTrustEdit}
                        />
                        <span>Группировать в одно сообщение (альбом)</span>
                      </label>
                    ) : null}
                  </div>
                ) : null}
                <div className="template-editor__actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => {
                      trustEdit.newAttachments.forEach((attachment) =>
                        revokeAttachmentPreview(attachment)
                      );
                      setTrustEdit(null);
                    }}
                    disabled={isSavingTrustEdit}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => void saveTrustEdit()}
                    disabled={isSavingTrustEdit || trustEdit.title.trim().length === 0}
                  >
                    {isSavingTrustEdit ? "Сохраняем…" : "Сохранить"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {autoDeleteEditorOpen && selectedChatId
        ? createPortal(
            <div
              className="forward-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Автоудаление сообщений"
            >
              <button
                type="button"
                className="forward-modal__backdrop"
                onClick={() => setAutoDeleteEditorOpen(false)}
                aria-label="Закрыть"
              />
              <div className="forward-modal__panel auto-delete-modal">
                <div className="auto-delete-modal__header">
                  <strong>Автоудаление сообщений</strong>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setAutoDeleteEditorOpen(false)}
                    aria-label="Закрыть"
                  >
                    <i className="ic ic-close" aria-hidden="true" />
                  </button>
                </div>
                <p className="auto-delete-modal__hint">
                  Отключить автоудаление сообщений в этом чате.
                </p>
                <div className="auto-delete-modal__actions">
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => {
                      setChatAutoDeleteTime(selectedChatId, 0);
                      setAutoDeleteEditorOpen(false);
                    }}
                  >
                    Никогда
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={() => setAutoDeleteEditorOpen(false)}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
});
ChatView.displayName = "ChatView";
